import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import { devLogSafe, devLog, devError } from './utils/secureLogger';
import { useAdminPermissions, canAccessSectionSync, ADMIN_SECTIONS, clearPermissionsCache } from './utils/adminPermissions';
import {
  ResponsiveTable,
  MobileRecruiterCard,
  MobileStudentCard,
  MobileUniversityMissingStudentCard,
  MobilePendingStudentCard,
  MobilePartnerActivityCard,
  MobileNav,
  MobileSidebar,
  ResponsiveContainer,
  useDeviceDetect
} from './components/ResponsiveComponents';
import MobileDebugger from './components/MobileDebugger';
import AdminBoardLiaisonManagement from './AdminBoardLiaisonManagement';
import { nacmeLogo } from './assets/images';

const BACKEND_URL = API_URL;

// Component for collapsible requirement dropdowns
const RequirementDropdown = ({ label, items, recruiterId, fieldName, expandedItems, setExpandedItems }) => {
  const dropdownKey = `${recruiterId}-${fieldName}`;
  const isExpanded = expandedItems.has(dropdownKey);

  const toggleDropdown = (e) => {
    e.stopPropagation(); // Prevent triggering the recruiter expansion
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(dropdownKey)) {
      newExpanded.delete(dropdownKey);
    } else {
      newExpanded.add(dropdownKey);
    }
    setExpandedItems(newExpanded);
  };

  if (!items || items.length === 0) {
    return (
      <div>
        <p className="font-medium text-gray-700 dark:text-gray-300">{label}:</p>
        <p className="text-gray-600 dark:text-gray-400">Not specified</p>
      </div>
    );
  }

  const displayText = items.length === 1 ? items[0] : `${items.length} items`;
  const shouldShowDropdown = items.length > 1;

  return (
    <div>
      <p className="font-medium text-gray-700 dark:text-gray-300">{label}:</p>
      {shouldShowDropdown ? (
        <div>
          <button
            onClick={toggleDropdown}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            <span>{displayText}</span>
            <svg
              className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isExpanded && (
            <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-600 rounded border text-xs">
              <ul className="list-disc list-inside space-y-1">
                {items.map((item, idx) => (
                  <li key={idx} className="text-gray-700 dark:text-gray-300">{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-600 dark:text-gray-400">{displayText}</p>
      )}
    </div>
  );
};

// Component for collapsible internship description dropdown
const InternshipDropdown = ({ description, recruiterId, expandedDescriptions, setExpandedDescriptions }) => {
  const isExpanded = expandedDescriptions.has(recruiterId);

  const toggleDropdown = (e) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedDescriptions);
    if (newExpanded.has(recruiterId)) {
      newExpanded.delete(recruiterId);
    } else {
      newExpanded.add(recruiterId);
    }
    setExpandedDescriptions(newExpanded);
  };

  if (!description || !description.trim()) {
    return (
      <div>
        <p className="font-medium text-blue-600 dark:text-blue-400">Internship Description:</p>
        <p className="text-gray-600 dark:text-gray-400">Not provided</p>
      </div>
    );
  }

  // Format the description with proper spacing between paragraphs
  const formatDescription = (desc) => {
    return desc.split('\n').filter(line => line.trim()).map((paragraph, index) => (
      <p key={index} className="mb-2 last:mb-0">{paragraph.trim()}</p>
    ));
  };

  return (
    <div>
      <button
        onClick={toggleDropdown}
        className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors font-medium"
      >
        <span>Internship Description</span>
        <svg
          className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isExpanded && (
        <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-600 rounded border text-sm text-gray-700 dark:text-gray-300">
          {formatDescription(description)}
        </div>
      )}
    </div>
  );
};

// Helper function to parse admin name from verified_by field
const getAdminName = (verifiedBy) => {
  if (!verifiedBy) return 'Unknown';

  // Format: "Admin Name (Admin - CSV Upload)" or "Admin Name (email@example.com)"
  const match = verifiedBy.match(/^(.+?)\s*\(/);
  if (match && match[1]) {
    return match[1].trim();
  }

  return verifiedBy;
};

// Helper function to format admin upload batch info
const formatBatchInfo = (adminUploadNotes) => {
  if (!adminUploadNotes) return '';

  // Extract batch ID from format: "Imported via admin CSV upload - Batch: admin_123_1705334400"
  const match = adminUploadNotes.match(/Batch:\s*(.+)/);
  if (match && match[1]) {
    return match[1].trim();
  }

  return adminUploadNotes;
};

const AdminApproveRecruiters = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Initialize activeTab from URL parameter if present
  const initialTab = searchParams.get('tab') || 'recruiters';
  const [allRecruiters, setAllRecruiters] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending, approved, rejected
  const [activeTab, setActiveTab] = useState(initialTab); // recruiters, tokens, partner-tokens, partner-accounts
  
  // Update activeTab when URL parameters change
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Token management state
  const [updateTokens, setUpdateTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [tokenFilter, setTokenFilter] = useState('active'); // active, all, expired, used, no_token
  const [generateTokenForm, setGenerateTokenForm] = useState({
    applicantIds: '',
    expiresInHours: 720
  });
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [selectedNoTokenStudents, setSelectedNoTokenStudents] = useState(new Set());
  const [selectedTokens, setSelectedTokens] = useState(new Set()); // For bulk token operations
  const [studentGroupFilter, setStudentGroupFilter] = useState('');
  const [groupCounts, setGroupCounts] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first
  const [applicationStatusFilter, setApplicationStatusFilter] = useState('all'); // 'all', 'applied', 'not_applied'
  const [appStatusCounts, setAppStatusCounts] = useState(null);
  const [showBulkTokenModal, setShowBulkTokenModal] = useState(false);
  const [bulkTokenExpiry, setBulkTokenExpiry] = useState(720);
  const [bulkTokenGenerating, setBulkTokenGenerating] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [matchingOrganizations, setMatchingOrganizations] = useState({}); // Store matching orgs by applicant_id
  const [fetchingMatchingOrgs, setFetchingMatchingOrgs] = useState(false);
  const [expandedMatchingOrgs, setExpandedMatchingOrgs] = useState(new Set()); // Track which students have expanded org sections

  // Cycle management state
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [availableCycles, setAvailableCycles] = useState([]);
  const [currentCycleYear, setCurrentCycleYear] = useState(null);
  const [cyclesLoading, setCyclesLoading] = useState(false);

  const [applicantSearch, setApplicantSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedApplicants, setSelectedApplicants] = useState(new Set());
  const [universitySearch, setUniversitySearch] = useState('');
  const [searchType, setSearchType] = useState('general'); // 'general' or 'university'
  const [expandedRecruiters, setExpandedRecruiters] = useState(new Set());
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [expandedItems, setExpandedItems] = useState(new Set());

  // University Partner Token Management state
  const [universityPartners, setUniversityPartners] = useState([]);
  const [verificationTokens, setVerificationTokens] = useState([]);

  // Admin permissions for section hiding
  const { permissions, loading: permissionsLoading } = useAdminPermissions();
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  // Partner Account Management state
  const [activationTokens, setActivationTokens] = useState({});
  const [generatingActivation, setGeneratingActivation] = useState(new Set());
  const [selectedPartners, setSelectedPartners] = useState(new Set());
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
  const [eligibleStudents, setEligibleStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [tokenExpiryHours, setTokenExpiryHours] = useState(720); // 30 days default
  const [generatingToken, setGeneratingToken] = useState(false);
  const [fetchingStudents, setFetchingStudents] = useState(false);
  const [revokingTokens, setRevokingTokens] = useState(new Set());

  // Verification Access Control state
  const [verificationPublicAccess, setVerificationPublicAccess] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  // Form field requirement settings
  const [raceRequired, setRaceRequired] = useState(true);
  const [ethnicityRequired, setEthnicityRequired] = useState(true);
  
  // Hold feature setting
  const [holdFeatureEnabled, setHoldFeatureEnabled] = useState(true);
  
  // Reject students state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // Student approval/rejection response modal state
  const [showStudentResponseModal, setShowStudentResponseModal] = useState(false);
  const [studentResponseData, setStudentResponseData] = useState(null);
  
  // Activation token modal state
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activationTokenData, setActivationTokenData] = useState(null);
  
  // Settings toggle modal state (for verification, race, ethnicity)
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsModalData, setSettingsModalData] = useState(null);
  
  // Partner activation modal state
  const [showPartnerActivationModal, setShowPartnerActivationModal] = useState(false);
  const [partnerActivationData, setPartnerActivationData] = useState(null);
  
  // Partner approval modal state
  const [showPartnerApprovalModal, setShowPartnerApprovalModal] = useState(false);
  const [partnerApprovalData, setPartnerApprovalData] = useState(null);
  
  // Bulk operation modal state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkModalData, setBulkModalData] = useState(null);
  

  // Internship Completion state
  const [internshipCompletionData, setInternshipCompletionData] = useState(null);
  const [internshipCompletionLoading, setInternshipCompletionLoading] = useState(false);
  const [internshipSearchQuery, setInternshipSearchQuery] = useState('');
  const [internshipCompletionCollapsed, setInternshipCompletionCollapsed] = useState(false);
  const [updatingCompletion, setUpdatingCompletion] = useState(new Set());

  // Approve New Students state
  const [pendingStudents, setPendingStudents] = useState([]);
  const [pendingStudentsLoading, setPendingStudentsLoading] = useState(false);
  const [selectedPendingStudents, setSelectedPendingStudents] = useState(new Set());
  const [processingStudents, setProcessingStudents] = useState(false);

  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [expandedUniversities, setExpandedUniversities] = useState(new Set());

  // Partner Activity state
  const [partnerActivityData, setPartnerActivityData] = useState(null);
  const [partnerActivityLoading, setPartnerActivityLoading] = useState(false);

  // Student Activity state
  const [studentActivityData, setStudentActivityData] = useState(null);
  const [studentActivityLoading, setStudentActivityLoading] = useState(false);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [studentActivityCollapsed, setStudentActivityCollapsed] = useState(false);
  const [sectionNavCollapsed, setSectionNavCollapsed] = useState(false);

  // Students University Missing state
  const [studentsUniversityMissingData, setStudentsUniversityMissingData] = useState(null);
  const [studentsUniversityMissingLoading, setStudentsUniversityMissingLoading] = useState(false);
  const [universityMissingSearchQuery, setUniversityMissingSearchQuery] = useState('');
  const [studentsUniversityMissingCollapsed, setStudentsUniversityMissingCollapsed] = useState(false); // Expanded by default

  // Unassigned Students state
  const [unassignedStudentsData, setUnassignedStudentsData] = useState(null);
  const [unassignedStudentsLoading, setUnassignedStudentsLoading] = useState(false);
  const [unassignedSearchQuery, setUnassignedSearchQuery] = useState('');
  const [unassignedStudentsCollapsed, setUnassignedStudentsCollapsed] = useState(false); // Expanded by default

  // Student Login Activity state
  const [studentLoginActivityData, setStudentLoginActivityData] = useState(null);
  const [studentLoginActivityLoading, setStudentLoginActivityLoading] = useState(false);
  const [studentLoginActivityCollapsed, setStudentLoginActivityCollapsed] = useState(false);

  // Recruiter Activity state
  const [recruiterActivityData, setRecruiterActivityData] = useState(null);
  const [recruiterActivityLoading, setRecruiterActivityLoading] = useState(false);
  const [recruiterActivityExporting, setRecruiterActivityExporting] = useState(false);
  const [expandedRecruitersActivity, setExpandedRecruitersActivity] = useState(new Set());
  const [selectedRecruitersForPrint, setSelectedRecruitersForPrint] = useState(new Set()); // Empty set means "All Recruiters"
  const [showPrintOptions, setShowPrintOptions] = useState(false); // Collapsible print selection

  // Geocoding state
  const [geocodingStatus, setGeocodingStatus] = useState(null);
  const [geocodingInProgress, setGeocodingInProgress] = useState(false);
  const [geocodingProgress, setGeocodingProgress] = useState({ processed: 0, total: 0, successful: 0, failed: 0 });
  const [geocodingCollapsed, setGeocodingCollapsed] = useState(false);
  const [geocodingExportLoading, setGeocodingExportLoading] = useState(false);

  // Mobile Sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Token Management Dropdown state
  const [tokenDropdownOpen, setTokenDropdownOpen] = useState(false);
  const tokenDropdownRef = React.useRef(null);
  
  // Students Dropdown state
  const [studentsDropdownOpen, setStudentsDropdownOpen] = useState(false);
  const studentsDropdownRef = React.useRef(null);
  
  // University Partner Dropdown state
  const [partnerDropdownOpen, setPartnerDropdownOpen] = useState(false);
  const partnerDropdownRef = React.useRef(null);
  
  // Company Dropdown state
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const companyDropdownRef = React.useRef(null);
  
  // Device detection for responsive design
  const { isMobile, isTablet, screenSize } = useDeviceDetect();

  // Recruiter candidate export state
  const [exportingCandidates, setExportingCandidates] = useState(new Set());

  // User Activity Dropdown state
  const [userActivityDropdownOpen, setUserActivityDropdownOpen] = useState(false);
  const userActivityDropdownRef = React.useRef(null);

  // Organization Matches Dropdown state
  const [organizationMatchesDropdownOpen, setOrganizationMatchesDropdownOpen] = useState(false);
  const organizationMatchesDropdownRef = React.useRef(null);
  
  // Alumni Dropdown state
  const [alumniDropdownOpen, setAlumniDropdownOpen] = useState(false);
  const alumniDropdownRef = React.useRef(null);

  // Reports Dropdown state
  const [reportsDropdownOpen, setReportsDropdownOpen] = useState(false);
  const reportsDropdownRef = React.useRef(null);

  // Pending tasks alert state (research, outreach, brokerage combined)
  const [pendingTasksSummary, setPendingTasksSummary] = useState(null);
  const [showPendingTasksModal, setShowPendingTasksModal] = useState(false);

  // Alumni Dash Admin state
  const [showAlumniAdminModal, setShowAlumniAdminModal] = useState(false);
  const [apifyToken, setApifyToken] = useState('');
  const [isApifyTokenConfigured, setIsApifyTokenConfigured] = useState(false);
  const [isLoadingApifyToken, setIsLoadingApifyToken] = useState(false);
  const [isSavingApifyToken, setIsSavingApifyToken] = useState(false);
  const [apifyTokenMessage, setApifyTokenMessage] = useState('');

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tokenDropdownRef.current && !tokenDropdownRef.current.contains(event.target)) {
        setTokenDropdownOpen(false);
      }
      if (studentsDropdownRef.current && !studentsDropdownRef.current.contains(event.target)) {
        setStudentsDropdownOpen(false);
      }
      if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(event.target)) {
        setPartnerDropdownOpen(false);
      }
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setCompanyDropdownOpen(false);
      }
      if (userActivityDropdownRef.current && !userActivityDropdownRef.current.contains(event.target)) {
        setUserActivityDropdownOpen(false);
      }
      if (organizationMatchesDropdownRef.current && !organizationMatchesDropdownRef.current.contains(event.target)) {
        setOrganizationMatchesDropdownOpen(false);
      }
      if (alumniDropdownRef.current && !alumniDropdownRef.current.contains(event.target)) {
        setAlumniDropdownOpen(false);
      }
      if (reportsDropdownRef.current && !reportsDropdownRef.current.contains(event.target)) {
        setReportsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch available cycle periods
  useEffect(() => {
    const fetchCycles = async () => {
      const token = getValidToken();
      if (!token) return;

      setCyclesLoading(true);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/cycle-periods`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.data) {
          const cycles = response.data;
          setAvailableCycles(cycles);

          // Find and set current cycle
          const current = cycles.find(c => c.is_current);
          if (current) {
            setCurrentCycleYear(current.cycle_year);
            setSelectedCycle(current.cycle_year); // Default to current
          }
        }
      } catch (error) {
        console.error('Error fetching cycles:', error);
      } finally {
        setCyclesLoading(false);
      }
    };

    fetchCycles();
  }, []);

  // Check for pending tasks on login (research, outreach, brokerage — once per session)
  useEffect(() => {
    const checkPendingTasks = async () => {
      if (sessionStorage.getItem('pending_tasks_checked')) return;

      const token = getValidToken();
      if (!token) return;

      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/my-pending-tasks-summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = response.data;
        const hasAny = (data.research?.total > 0) || (data.outreach?.total > 0) || (data.brokerage?.total > 0) || (data.content_moderation?.total > 0) || (data.leadership_review_queue?.total > 0) || (data.grant_reviews?.total > 0);
        if (hasAny) {
          setPendingTasksSummary(data);
          setShowPendingTasksModal(true);
        }
        sessionStorage.setItem('pending_tasks_checked', 'true');
      } catch (err) {
        console.warn('[PendingTasks] Error:', err);
      }
    };

    checkPendingTasks();
  }, []);

  // Function to handle tab switching with URL update
  const switchToTab = (tabName) => {
    setActiveTab(tabName);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabName);
    navigate(`/admin?${newSearchParams.toString()}`, { replace: true });
  };

  // Fetch dashboard data
  const fetchDashboard = async () => {
    setDashboardLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/verification-dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError({ type: 'error', message: err.response?.data?.detail || 'Failed to load dashboard' });
    } finally {
      setDashboardLoading(false);
    }
  };

  // Fetch partner activity data
  const fetchPartnerActivity = async () => {
    setPartnerActivityLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/partner-activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      devLogSafe('Partner activity response', response.data);
      devLogSafe('Partner activity array', response.data.partner_activity);
      setPartnerActivityData(response.data.partner_activity);
    } catch (error) {
      console.error('Error fetching partner activity:', error);
      setError('Failed to load partner activity data');
    } finally {
      setPartnerActivityLoading(false);
    }
  };

  const exportStudentActivityToCSV = () => {
    if (!studentActivityData || studentActivityData.length === 0) {
      alert('No data to export');
      return;
    }

    // Filter students based on current search
    const filteredStudents = studentActivityData.filter(student => {
      if (!studentSearchQuery) return true;
      const searchLower = studentSearchQuery.toLowerCase();
      return (
        student.student_name.toLowerCase().includes(searchLower) ||
        (student.applicant_id && student.applicant_id.toString().includes(searchLower))
      );
    });

    // Define CSV headers
    const headers = [
      'Student ID',
      'Student Name',
      'Status',
      'Last Application',
      'Date Verified',
      'Eligibility',
      'Email',
      'Preferred Companies',
      'Offers',
      'Offers Accepted',
      'Hired By',
      'Total Applications',
      'Current Cycle',
      'Applications This Cycle'
    ];

    // Create CSV rows
    const rows = filteredStudents.map(student => [
      student.applicant_id || '',
      student.student_name || '',
      student.verification_status === 'verified' ? 'Verified' : 'Pending',
      student.last_application_formatted || '',
      student.verified_at || '',
      student.eligibility !== null ? (student.eligibility === true ? 'Eligible' : 'Not Eligible') : '',
      student.university_email || '',
      student.preferred_companies || '',
      student.total_offers || 0,
      student.offers_accepted || 0,
      student.hired_by_companies || '',
      student.total_applications || 0,
      student.current_cycle_year || '',
      student.applications_this_cycle || 0
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const filename = `student_activity_${new Date().toISOString().split('T')[0]}.csv`;

    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();

    // Clean up
    URL.revokeObjectURL(link.href);
  };

  const fetchStudentActivity = async () => {
    setStudentActivityLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/student-activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      devLogSafe('Student activity response', response.data);
      devLogSafe('Student activity array', response.data.student_activity);
      setStudentActivityData(response.data.student_activity);
    } catch (error) {
      console.error('Error fetching student activity:', error);
      setError('Failed to load student activity data');
    } finally {
      setStudentActivityLoading(false);
    }
  };

  // Fetch geocoding status
  const fetchGeocodingStatus = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      const response = await axios.get(`${BACKEND_URL}/api/admin/geocode-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeocodingStatus(response.data);
    } catch (error) {
      console.error('Error fetching geocoding status:', error);
    }
  };

  // Run geocoding batch and continue until complete
  const runGeocodingBatch = async () => {
    const token = getValidToken();
    if (!token) {
      setError('Your session has expired. Please log in again.');
      return;
    }

    setGeocodingInProgress(true);
    let totalProcessed = 0;
    let totalSuccessful = 0;
    let totalFailed = 0;
    let isComplete = false;

    try {
      // Get initial status
      const statusResponse = await axios.get(`${BACKEND_URL}/api/admin/geocode-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const initialPending = statusResponse.data.pending_with_valid_address || 0;
      setGeocodingProgress({ processed: 0, total: initialPending, successful: 0, failed: 0 });

      // Process batches until complete
      while (!isComplete) {
        const response = await axios.post(
          `${BACKEND_URL}/api/admin/geocode-students?batch_size=50`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const { results, remaining, is_complete } = response.data;
        totalProcessed += results.processed;
        totalSuccessful += results.successful;
        totalFailed += results.failed;
        isComplete = is_complete || remaining <= 0;

        setGeocodingProgress({
          processed: totalProcessed,
          total: initialPending,
          successful: totalSuccessful,
          failed: totalFailed
        });

        // Small delay between batches to avoid overwhelming the server
        if (!isComplete) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Refresh status after completion
      await fetchGeocodingStatus();
    } catch (error) {
      console.error('Error during geocoding:', error);
      setError('Geocoding process encountered an error. Please try again.');
    } finally {
      setGeocodingInProgress(false);
    }
  };

  // Export failed geocoding students to CSV
  const exportFailedGeocodingToCSV = async () => {
    const token = getValidToken();
    if (!token) {
      setError('Your session has expired. Please log in again.');
      return;
    }

    setGeocodingExportLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/geocode-failed`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { failed_students } = response.data;

      if (!failed_students || failed_students.length === 0) {
        alert('No failed geocoding students to export');
        return;
      }

      // Define CSV headers
      const headers = [
        'Student ID',
        'First Name',
        'Last Name',
        'Email',
        'Street',
        'City',
        'State',
        'ZIP',
        'University'
      ];

      // Convert to CSV rows
      const csvRows = failed_students.map(student => [
        student.id || '',
        student.first_name || '',
        student.last_name || '',
        student.email || '',
        (student.street || '').replace(/"/g, '""'),
        student.city || '',
        student.state || '',
        student.zip || '',
        (student.current_university || '').replace(/"/g, '""')
      ]);

      // Build CSV content
      const csvContent = [
        headers.join(','),
        ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `failed_geocoding_students_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting failed geocoding students:', error);
      setError('Failed to export geocoding data. Please try again.');
    } finally {
      setGeocodingExportLoading(false);
    }
  };

  // Fetch students with missing university status data
  const fetchStudentsUniversityMissing = async () => {
    setStudentsUniversityMissingLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/students-university-missing`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      devLogSafe('Students university missing response', response.data);
      devLogSafe('Students university missing array', response.data.students_university_missing);
      setStudentsUniversityMissingData(response.data.students_university_missing);
    } catch (error) {
      console.error('Error fetching students with missing university status:', error);
      setError('Failed to load students with missing university status data');
    } finally {
      setStudentsUniversityMissingLoading(false);
    }
  };

  // Export students with missing university status to CSV
  const exportStudentsUniversityMissingToCSV = () => {
    if (!studentsUniversityMissingData || studentsUniversityMissingData.length === 0) {
      alert('No data to export');
      return;
    }

    // Filter students based on current search
    const filteredStudents = studentsUniversityMissingData.filter(student => {
      if (!universityMissingSearchQuery) return true;
      const searchLower = universityMissingSearchQuery.toLowerCase();
      return (
        student.student_name.toLowerCase().includes(searchLower) ||
        (student.applicant_id && student.applicant_id.toString().includes(searchLower))
      );
    });

    // Define CSV headers
    const headers = [
      'Student ID',
      'Student Name',
      'Scholarship',
      'Status',
      'Last Application',
      'Date Verified',
      'Eligibility',
      'Email',
      'Preferred Companies',
      'Total Applications',
      'Current Cycle',
      'Applications This Cycle'
    ];

    // Create CSV rows
    const rows = filteredStudents.map(student => [
      student.applicant_id || '',
      student.student_name || '',
      student.scholarship_verified || '',
      student.verification_status || 'pending',
      student.last_application_formatted || '',
      student.verified_at_formatted || '',
      student.eligibility || '',
      student.university_email || '',
      student.preferred_companies || '',
      student.total_applications || 0,
      student.current_cycle_year || '',
      student.applications_this_cycle || 0
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => {
        // Escape quotes and wrap in quotes if necessary
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
      .join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const filename = `students_university_missing_${new Date().toISOString().split('T')[0]}.csv`;

    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();

    // Clean up
    URL.revokeObjectURL(link.href);
  };

  // Fetch unassigned students data
  const fetchUnassignedStudents = async () => {
    setUnassignedStudentsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/unassigned-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      devLogSafe('Unassigned students response', response.data);
      devLogSafe('Unassigned students array', response.data.unassigned_students);
      setUnassignedStudentsData(response.data.unassigned_students);
    } catch (error) {
      console.error('Error fetching unassigned students:', error);
      setError('Failed to load unassigned students data');
    } finally {
      setUnassignedStudentsLoading(false);
    }
  };

  // Export unassigned students to CSV
  const exportUnassignedStudentsToCSV = () => {
    if (!unassignedStudentsData || unassignedStudentsData.length === 0) {
      alert('No data to export');
      return;
    }

    // Filter students based on current search
    const filteredStudents = unassignedStudentsData.filter(student => {
      if (!unassignedSearchQuery) return true;
      const searchLower = unassignedSearchQuery.toLowerCase();
      return (
        student.student_name.toLowerCase().includes(searchLower) ||
        (student.applicant_id && student.applicant_id.toString().includes(searchLower))
      );
    });

    // Define CSV headers
    const headers = [
      'Student ID',
      'Student Name',
      'Status',
      'Last Application',
      'Date Verified',
      'Eligibility',
      'Email',
      'Preferred Companies',
      'Total Applications',
      'Current Cycle',
      'Applications This Cycle'
    ];

    // Create CSV rows
    const rows = filteredStudents.map(student => [
      student.applicant_id || '',
      student.student_name || '',
      student.verification_status || 'pending',
      student.last_application_formatted || '',
      student.verified_at_formatted || '',
      student.eligibility || '',
      student.university_email || '',
      student.preferred_companies || '',
      student.total_applications || 0,
      student.current_cycle_year || '',
      student.applications_this_cycle || 0
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => {
        // Escape quotes and wrap in quotes if necessary
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
      .join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const filename = `unassigned_students_${new Date().toISOString().split('T')[0]}.csv`;

    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();

    // Clean up
    URL.revokeObjectURL(link.href);
  };

  // Fetch student login activity data
  const fetchStudentLoginActivity = async () => {
    setStudentLoginActivityLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/student-login-activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      devLogSafe('Student login activity response', response.data);
      devLogSafe('Student login activity array', response.data.student_login_activity);
      setStudentLoginActivityData(response.data.student_login_activity);
    } catch (error) {
      console.error('Error fetching student login activity:', error);
      setError('Failed to load student login activity data');
    } finally {
      setStudentLoginActivityLoading(false);
    }
  };

  // Fetch recruiter activity data
  const fetchRecruiterActivity = async () => {
    setRecruiterActivityLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/recruiter-activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      devLogSafe('Recruiter activity response', response.data);
      setRecruiterActivityData(response.data.recruiter_activities);
    } catch (error) {
      console.error('Error fetching recruiter activity:', error);
      setError('Failed to load recruiter activity data');
    } finally {
      setRecruiterActivityLoading(false);
    }
  };

  // Print handler for recruiter activity
  const handlePrintRecruiterActivity = () => {
    window.print();
  };

  // Export CSV handler for recruiter activity
  const handleExportRecruiterActivityCSV = async () => {
    setRecruiterActivityExporting(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/recruiter-activity/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recruiter_activity_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting recruiter activity:', error);
      setError('Failed to export recruiter activity data');
    } finally {
      setRecruiterActivityExporting(false);
    }
  };

  const toggleUniversity = (universityName) => {
    const newExpanded = new Set(expandedUniversities);
    if (newExpanded.has(universityName)) {
      newExpanded.delete(universityName);
    } else {
      newExpanded.add(universityName);
    }
    setExpandedUniversities(newExpanded);
  };

  const exportUniversityCSV = (university) => {
    // Create CSV content
    let csvContent = "Full Name,Email,Student Applied,Application Date,Verification Status,Eligibility Status\n";

    // Add student data
    university.students.forEach(student => {
      const verificationStatus = student.verification_status || 'Unverified';
      const eligibilityStatus = (student.eligibility_status === true || student.eligibility_status === 'true') ? 'Eligible' : 'Not Eligible';
      const applicationDate = student.application_date || '';
      csvContent += `"${student.name}","${student.email}","${student.student_applied}","${applicationDate}","${verificationStatus}","${eligibilityStatus}"\n`;
    });

    // Add empty lines
    csvContent += "\n\n";

    // Add partner information
    csvContent += "UNIVERSITY PARTNERS\n";
    csvContent += "Name,Email,Level\n";
    university.partners.forEach(partner => {
      csvContent += `"${partner.name}","${partner.email}","${partner.level || 'N/A'}"\n`;
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${university.university}_verification_data.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportAllUniversitiesCSV = () => {
    if (!dashboardData || !dashboardData.universities) return;

    // Create CSV content with university column
    let csvContent = "University,Full Name,Email,Student Applied,Application Date,Verification Status,Eligibility Status\n";

    // Add all student data from all universities
    dashboardData.universities.forEach(university => {
      university.students.forEach(student => {
        const verificationStatus = student.verification_status || 'Unverified';
        const eligibilityStatus = (student.eligibility_status === true || student.eligibility_status === 'true') ? 'Eligible' : 'Not Eligible';
        const applicationDate = student.application_date || '';
        csvContent += `"${university.university}","${student.name}","${student.email}","${student.student_applied}","${applicationDate}","${verificationStatus}","${eligibilityStatus}"\n`;
      });
    });

    // Add empty lines
    csvContent += "\n\n";

    // Add all partner information grouped by university
    csvContent += "UNIVERSITY PARTNERS BY INSTITUTION\n";
    csvContent += "University,Partner Name,Email,Level\n";
    dashboardData.universities.forEach(university => {
      university.partners.forEach(partner => {
        csvContent += `"${university.university}","${partner.name}","${partner.email}","${partner.level || 'N/A'}"\n`;
      });
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `all_universities_verification_data_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchAllRecruiters();
  }, []);

  // Re-fetch data when returning to the page or when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible again, refresh data
        fetchAllRecruiters();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Re-fetch data when navigating back to the recruiters tab specifically
  useEffect(() => {
    if (activeTab === 'recruiters') {
      fetchAllRecruiters();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'tokens') {
      fetchUpdateTokens();
    } else if (activeTab === 'partner-tokens') {
      fetchUniversityPartners();
      fetchVerificationTokens();
    } else if (activeTab === 'partner-accounts') {
      fetchUniversityPartners();
      fetchVerificationAccessSetting();
      fetchFormFieldSettings();
      fetchHoldFeatureSetting();
    } else if (activeTab === 'approve-students') {
      fetchPendingStudents();
    } else if (activeTab === 'student-activity') {
      fetchStudentActivity();
      fetchInternshipCompletions();
      fetchStudentsUniversityMissing();
      fetchUnassignedStudents();
      fetchStudentLoginActivity();
      fetchGeocodingStatus();
    } else if (activeTab === 'recruiter-activity') {
      fetchRecruiterActivity();
    } else if (activeTab === 'partner-activity') {
      fetchPartnerActivity();
    } else if (activeTab === 'dashboard') {
      fetchDashboard();
    }
  }, [activeTab, ...(activeTab === 'tokens' || activeTab === 'partner-tokens' ? [tokenFilter, studentGroupFilter, applicationStatusFilter, sortOrder, selectedCycle] : [])]);

  // Clear selected students when token filter changes
  useEffect(() => {
    setSelectedNoTokenStudents(new Set());
  }, [tokenFilter]);

  // Fetch matching organizations when updateTokens changes and we're on the tokens tab
  useEffect(() => {
    if (activeTab === 'tokens' && updateTokens.length > 0) {
      const applicantIds = updateTokens.map(token => token.applicant_id).filter(id => id);
      fetchMatchingOrganizations(applicantIds);
    }
  }, [updateTokens, activeTab]);

  const fetchAllRecruiters = async () => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setLoading(false);
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/all-recruiters`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setAllRecruiters(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching recruiters:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(err.response?.data?.detail || 'Failed to fetch recruiters. Please ensure you are logged in as an admin.');
      }
      setLoading(false);
    }
  };

  const fetchUpdateTokens = async () => {
    setTokensLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      // Build query parameters
      const activeOnly = tokenFilter === 'active';
      let queryParams = `active_only=${activeOnly}&limit=10000`;

      // Add cycle_year parameter (defaults to current cycle on backend if not provided)
      if (selectedCycle) {
        queryParams += `&cycle_year=${selectedCycle}`;
      }

      // Add filter_type parameter for "no_token" filter
      if (tokenFilter === 'no_token') {
        queryParams += '&filter_type=no_token';

        // Add student group filter if specified
        if (studentGroupFilter) {
          queryParams += `&student_group=${studentGroupFilter}`;
        }

        // Add application status filter
        if (applicationStatusFilter && applicationStatusFilter !== 'all') {
          queryParams += `&application_status=${applicationStatusFilter}`;
        }

        // Add sort order
        queryParams += `&sort_order=${sortOrder}`;
      }

      const response = await axios.get(`${BACKEND_URL}/api/list-update-tokens?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let tokens = response.data.tokens || [];

      // Store group counts if available
      if (response.data.group_counts) {
        setGroupCounts(response.data.group_counts);
      }

      // Store application status counts if available
      if (response.data.app_status_counts) {
        setAppStatusCounts(response.data.app_status_counts);
      }

      // Apply additional filtering
      if (tokenFilter === 'expired') {
        tokens = tokens.filter(t => t.is_expired && !t.used);
      } else if (tokenFilter === 'used') {
        tokens = tokens.filter(t => t.used);
      }

      setUpdateTokens(tokens);
    } catch (err) {
      console.error('Error fetching tokens:', err);
      setError({ type: 'error', message: err.response?.data?.detail || 'Failed to fetch update tokens' });
    } finally {
      setTokensLoading(false);
    }
  };

  // Fetch matching organizations for students
  const fetchMatchingOrganizations = async (applicantIds) => {
    if (!applicantIds || applicantIds.length === 0) {
      return {};
    }

    setFetchingMatchingOrgs(true);
    try {
      const token = getValidToken();
      if (!token) {
        return {};
      }

      const response = await axios.get(`${BACKEND_URL}/api/student-matches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Build a map of applicant_id -> matching_organizations
      const matchingOrgsMap = {};
      if (response.data && response.data.students) {
        response.data.students.forEach(student => {
          const studentId = student.student_info.id;
          if (applicantIds.includes(studentId)) {
            matchingOrgsMap[studentId] = student.matching_organizations || [];
          }
        });
      }

      // For students not in the response, set empty array
      applicantIds.forEach(id => {
        if (!matchingOrgsMap[id]) {
          matchingOrgsMap[id] = [];
        }
      });

      setMatchingOrganizations(matchingOrgsMap);
      return matchingOrgsMap;

    } catch (err) {
      console.error('Error fetching matching organizations:', err);
      return {};
    } finally {
      setFetchingMatchingOrgs(false);
    }
  };

  // Helper functions for bulk token generation
  const getTotalMatchingCount = () => {
    if (!appStatusCounts) return 0;

    if (applicationStatusFilter === 'all') {
      return appStatusCounts.all || 0;
    } else if (applicationStatusFilter === 'applied') {
      return appStatusCounts.applied || 0;
    } else if (applicationStatusFilter === 'not_applied') {
      return appStatusCounts.not_applied || 0;
    }
    return 0;
  };

  const getGroupFilterLabel = () => {
    if (!studentGroupFilter) return 'All Groups';
    const labels = {
      'self_registered': 'Self-Registered',
      'admin_upload': 'Admin Upload',
      'partner_upload_approved': 'Partner Upload',
      'partner_verified': 'Partner Verified',
      'unverified': 'Unverified'
    };
    return labels[studentGroupFilter] || 'All Groups';
  };

  const getAppStatusLabel = () => {
    const labels = {
      'all': 'All',
      'applied': 'Applied',
      'not_applied': 'Not Applied'
    };
    return labels[applicationStatusFilter] || 'All';
  };

  // Filter students based on application status and search term
  const filteredUpdateTokens = React.useMemo(() => {
    let filtered = updateTokens;

    // Apply application status filter (only for tokens with IDs, not no_token view)
    if (tokenFilter !== 'no_token' && applicationStatusFilter !== 'all') {
      filtered = filtered.filter(token => {
        if (applicationStatusFilter === 'not_applied') {
          return !token.has_applied_this_cycle;
        } else if (applicationStatusFilter === 'applied') {
          return token.has_applied_this_cycle;
        }
        return true;
      });
    }

    // Apply search filter if search term exists
    if (!studentSearchTerm.trim()) {
      return filtered;
    }

    const searchLower = studentSearchTerm.toLowerCase().trim();

    return filtered.filter(token => {
      // Search by ID
      if (token.applicant_id && token.applicant_id.toString().includes(searchLower)) {
        return true;
      }

      // Search by name (for "No Token Issued" tokens)
      if (token.applicant_name && token.applicant_name.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search by personal email (for "No Token Issued" tokens)
      if (token.personal_email && token.personal_email.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search by university email (for "No Token Issued" tokens)
      if (token.university_email_verified && token.university_email_verified.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search by applicant email (for Active/All/Expired/Used tokens)
      if (token.applicant_email && token.applicant_email.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Search by token string (for Active/All/Expired/Used tokens)
      if (token.token && token.token.toLowerCase().includes(searchLower)) {
        return true;
      }

      return false;
    });
  }, [updateTokens, studentSearchTerm, tokenFilter, applicationStatusFilter]);

  // CSV Export function for selected students
  const exportSelectedStudentsToCSV = async () => {
    if (selectedNoTokenStudents.size === 0) {
      alert('Please select students to export.');
      return;
    }

    try {
      const token = getValidToken();
      if (!token) {
        alert('Your session has expired. Please log in again.');
        return;
      }

      // Fetch fresh data for selected students including their tokens
      const selectedIds = Array.from(selectedNoTokenStudents);
      const studentDataPromises = selectedIds.map(async (applicantId) => {
        try {
          // Fetch token data for this student
          const tokenResponse = await axios.get(
            `${BACKEND_URL}/api/list-update-tokens?applicant_id=${applicantId}&active_only=false&limit=1&cycle_year=${selectedCycle || currentCycleYear}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );

          const tokens = tokenResponse.data.tokens || [];
          const latestToken = tokens.length > 0 ? tokens[0] : null;

          // Get student data from current list or create placeholder
          let studentData = updateTokens.find(t => t.applicant_id === applicantId);

          // If not in current view, fetch from all tokens
          if (!studentData && latestToken) {
            studentData = latestToken;
          }

          // If still no data, fetch student record directly
          if (!studentData) {
            const studentResponse = await axios.get(
              `${BACKEND_URL}/api/list-update-tokens?filter_type=no_token&limit=10000&cycle_year=${selectedCycle || currentCycleYear}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            studentData = studentResponse.data.tokens.find(t => t.applicant_id === applicantId);
          }

          if (!studentData) {
            return null;
          }

          // Use latest token if available
          if (latestToken) {
            studentData = { ...studentData, ...latestToken };
          }

          return studentData;
        } catch (err) {
          console.error(`Error fetching data for student ${applicantId}:`, err);
          return null;
        }
      });

      const selectedTokens = (await Promise.all(studentDataPromises)).filter(t => t !== null);

      if (selectedTokens.length === 0) {
        alert('No students found to export.');
        return;
      }

      // Build CSV header
      const headers = [
        'Student Name',
        'Email',
        'GPA',
        'Major',
        'Class Year',
        'Matching Organizations',
        'Applied via Magic Link',
        'Update Token',
        'Token Hours Remaining'
      ];

      // Build CSV rows
      const rows = selectedTokens.map(studentToken => {
        const applicantId = studentToken.applicant_id;
        const matchingOrgs = matchingOrganizations[applicantId] || [];
        const matchingOrgsList = matchingOrgs.map(org => org.organization).join('; ');

        // Calculate hours remaining for token (if exists)
        let hoursRemaining = 'No Token';
        if (studentToken.token && studentToken.expires_at && !studentToken.used && !studentToken.is_expired) {
          const now = new Date();
          const expiresAt = new Date(studentToken.expires_at);
          const diffMs = expiresAt - now;
          const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
          hoursRemaining = diffHours > 0 ? diffHours.toString() : 'Expired';
        } else if (studentToken.token && studentToken.is_expired) {
          hoursRemaining = 'Expired';
        } else if (studentToken.token && studentToken.used) {
          hoursRemaining = 'Used';
        }

        // Determine if student applied via magic link
        const appliedViaMagicLink = (studentToken.used_magic_link && studentToken.application_status === 'applied') ? 'Yes' : 'No';

        return [
          studentToken.applicant_name || 'N/A',
          studentToken.university_email_verified || studentToken.personal_email || 'N/A',
          studentToken.gpa_verified || studentToken.current_gpa || 'N/A',
          studentToken.major_verified || studentToken.major || 'N/A',
          studentToken.current_academic_class_verified || studentToken.current_academic_class || 'N/A',
          matchingOrgsList || 'None',
          appliedViaMagicLink,
          studentToken.token || 'Not Issued',
          hoursRemaining
        ];
      });

      // Convert to CSV string
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escape cells containing commas or quotes
          const cellStr = cell.toString();
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `student_tokens_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setError({ type: 'success', message: `Successfully exported ${selectedTokens.length} student(s) to CSV` });
      setTimeout(() => setError(null), 3000);

    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError({ type: 'error', message: 'Failed to export CSV. Please try again.' });
    }
  };

  // Bulk token generation handler
  const handleBulkTokenGeneration = async () => {
    setBulkTokenGenerating(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/generate-bulk-update-tokens`,
        {
          student_group: studentGroupFilter || null,
          application_status: applicationStatusFilter !== 'all' ? applicationStatusFilter : null,
          expires_in_hours: bulkTokenExpiry,
          cycle_year: selectedCycle || currentCycleYear
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setShowBulkTokenModal(false);
      setError({ type: 'success', message: response.data.message });

      // Refresh the token list
      fetchUpdateTokens();

    } catch (err) {
      console.error('Error generating bulk tokens:', err);
      setError({ type: 'error', message: err.response?.data?.detail || 'Failed to generate bulk tokens' });
    } finally {
      setBulkTokenGenerating(false);
    }
  };

  // Alumni Dash Admin functions
  const fetchApifyToken = async () => {
    setIsLoadingApifyToken(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/settings/apify_token`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setApifyToken(response.data.setting_value || '');
      setIsApifyTokenConfigured(response.data.setting_value && response.data.setting_value.trim().length > 0);
    } catch (err) {
      console.error('Error fetching Apify token:', err);
      setApifyTokenMessage('Failed to load Apify token');
    } finally {
      setIsLoadingApifyToken(false);
    }
  };

  const saveApifyToken = async () => {
    setIsSavingApifyToken(true);
    setApifyTokenMessage('');
    
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      await axios.put(`${BACKEND_URL}/api/admin/settings/apify_token`, {
        setting_value: apifyToken
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setApifyTokenMessage('Apify token saved successfully!');
      setTimeout(() => setApifyTokenMessage(''), 3000);
      
      // Refresh the token to update the status display
      await fetchApifyToken();
    } catch (err) {
      console.error('Error saving Apify token:', err);
      setApifyTokenMessage('Failed to save Apify token');
    } finally {
      setIsSavingApifyToken(false);
    }
  };

  const searchApplicants = async (query, searchByUniversity = false) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError({ type: 'error', message: 'Your session has expired. Please log in again.' });
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const limit = searchByUniversity ? 100 : 20; // Get more results for university searches
      const response = await axios.get(`${BACKEND_URL}/api/applicants/search?q=${encodeURIComponent(query)}&limit=${limit}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let results = response.data.applicants || [];

      // If searching by university, filter to ensure university matches
      // Prefer verified university field if available
      if (searchByUniversity) {
        results = results.filter(applicant => {
          const university = applicant.current_university_verified || applicant.current_university;
          return university && university.toLowerCase().includes(query.toLowerCase());
        });
      }

      setSearchResults(results);
    } catch (err) {
      console.error('Error searching applicants:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to search applicants';
      setError({ type: 'error', message: `Search error: ${errorMessage}` });
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchUniversityPartners = async () => {
    setPartnersLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/university-partners`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setUniversityPartners(response.data || []);
    } catch (err) {
      console.error('Error fetching university partners:', err);
      setError({ type: 'error', message: err.response?.data?.detail || 'Failed to fetch university partners' });
    } finally {
      setPartnersLoading(false);
    }
  };

  const fetchVerificationTokens = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      const response = await axios.get(`${BACKEND_URL}/api/admin/verification-tokens`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setVerificationTokens(response.data || []);
    } catch (err) {
      console.error('Error fetching verification tokens:', err);
      setError({ type: 'error', message: err.response?.data?.detail || 'Failed to fetch verification tokens' });
    }
  };

  const fetchEligibleStudents = async (partnerId) => {
    if (fetchingStudents) return; // Prevent multiple simultaneous requests

    setFetchingStudents(true);
    try {
      const token = getValidToken();
      if (!token) return;

      const response = await axios.get(`${BACKEND_URL}/api/admin/university-partners/eligible-students/${partnerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setEligibleStudents(response.data.eligible_students || []);
    } catch (err) {
      console.error('Error fetching eligible students:', err);
      setError({ type: 'error', message: err.response?.data?.detail || 'Failed to fetch eligible students' });
    } finally {
      setFetchingStudents(false);
    }
  };

  const generateVerificationToken = async () => {
    if (!selectedPartner || selectedStudents.size === 0) {
      setError({ type: 'error', message: 'Please select a partner and at least one student' });
      return;
    }

    setGeneratingToken(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/verification-tokens/generate`,
        {
          university_partner_id: selectedPartner,
          student_ids: Array.from(selectedStudents),
          expires_in_hours: tokenExpiryHours
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setError({
        type: 'success',
        message: `Verification token generated successfully! Token: ${response.data.token}`
      });

      // Reset form
      setSelectedPartner(null);
      setSelectedStudents(new Set());
      setEligibleStudents([]);

      // Refresh tokens list
      fetchVerificationTokens();

    } catch (err) {
      console.error('Error generating verification token:', err);
      setError({ type: 'error', message: err.response?.data?.detail || 'Failed to generate verification token' });
    } finally {
      setGeneratingToken(false);
    }
  };

  const fetchVerificationAccessSetting = async () => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings/partner_verification_public_access`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const settingValue = response.data.setting_value === 'true';
      setVerificationPublicAccess(settingValue);

    } catch (err) {
      console.error('Error fetching verification access setting:', err);
      // If error, keep default value of true
    }
  };

  const toggleVerificationPublicAccess = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const newValue = !verificationPublicAccess;
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/settings/partner_verification_public_access`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setVerificationPublicAccess(newValue);
      
      // Show modal instead of error message
      setSettingsModalData({
        success: true,
        setting: 'verification',
        enabled: newValue,
        message: `Verification form access has been ${newValue ? 'enabled' : 'disabled'} successfully.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error updating verification access setting:', err);
      // Show error in modal
      setSettingsModalData({
        success: false,
        setting: 'verification',
        message: err.response?.data?.detail || 'Failed to update verification access setting'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchHoldFeatureSetting = async () => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings/hold_feature_enabled`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const settingValue = response.data.setting_value === 'true';
      setHoldFeatureEnabled(settingValue);
    } catch (err) {
      console.error('Error fetching hold feature setting:', err);
      // If error, keep default value of false
    }
  };

  const toggleHoldFeature = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !holdFeatureEnabled;
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/settings/hold_feature_enabled`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setHoldFeatureEnabled(newValue);
      
      // Show modal instead of error message
      setSettingsModalData({
        success: true,
        setting: 'hold',
        enabled: newValue,
        message: `Hold feature has been ${newValue ? 'enabled' : 'disabled'} successfully.`
      });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error updating hold feature setting:', err);
      // Show error in modal
      setSettingsModalData({
        success: false,
        setting: 'hold',
        message: err.response?.data?.detail || 'Failed to update hold feature setting'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  const approvePartner = async (partnerId, isApproved) => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/university-partners/approve`,
        {
          partner_id: parseInt(partnerId),
          is_approved: isApproved
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Show modal instead of error message
      setPartnerApprovalData({
        success: true,
        partnerId: partnerId,
        approved: isApproved,
        message: response.data.message || `Partner has been ${isApproved ? 'approved' : 'rejected'} successfully.`
      });
      setShowPartnerApprovalModal(true);

      // Refresh partners list
      fetchUniversityPartners();

    } catch (err) {
      console.error('Error updating partner approval:', err);
      console.error('Full error response:', err.response);
      console.error('Error data:', err.response?.data);

      let errorMessage = 'Failed to update partner approval status';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail[0]?.msg || errorMessage;
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
      }

      // Show error in modal
      setPartnerApprovalData({
        success: false,
        message: errorMessage
      });
      setShowPartnerApprovalModal(true);
    }
  };

  // Fetch internship completion data
  const fetchInternshipCompletions = async () => {
    setInternshipCompletionLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/internship-completions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      devLogSafe('Internship completion response', response.data);
      devLogSafe('Internship completion array', response.data.internship_completions);
      setInternshipCompletionData(response.data.internship_completions);
    } catch (error) {
      console.error('Error fetching internship completions:', error);
      setError('Failed to load internship completion data');
    } finally {
      setInternshipCompletionLoading(false);
    }
  };

  // Update completion status
  const updateCompletionStatus = async (studentId, interactionId, completionStatus, completionDate, notes) => {
    const updateKey = `${studentId}-${interactionId}`;
    setUpdatingCompletion(prev => new Set(prev).add(updateKey));
    
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const requestData = {
        student_id: studentId,
        interaction_id: interactionId,
        completion_status: completionStatus,
        completion_date: completionDate,
        notes: notes || ''
      };

      const response = await axios.put(`${BACKEND_URL}/api/admin/update-completion-status`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Refresh the data
        await fetchInternshipCompletions();
      }
    } catch (error) {
      console.error('Error updating completion status:', error);
      setError('Failed to update completion status');
    } finally {
      setUpdatingCompletion(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
    }
  };

  const exportInternshipCompletionsToCSV = () => {
    if (!internshipCompletionData || internshipCompletionData.length === 0) {
      alert('No internship completion data to export');
      return;
    }

    // Filter completions based on search query (same logic as display)
    const filteredCompletions = internshipCompletionData.filter(completion => {
      if (!internshipSearchQuery) return true;
      const searchLower = internshipSearchQuery.toLowerCase();
      return (
        completion.student_name.toLowerCase().includes(searchLower) ||
        completion.company.toLowerCase().includes(searchLower) ||
        (completion.applicant_id && completion.applicant_id.toString().includes(searchLower))
      );
    });

    if (filteredCompletions.length === 0) {
      alert('No matching completion data to export');
      return;
    }

    const headers = [
      'Student ID',
      'Student Name', 
      'Company',
      'CSIP Completion Status',
      'Completion Date',
      'Status',
      'Last Application',
      'Date Verified',
      'Eligibility',
      'Email',
      'Notes'
    ];

    const csvData = filteredCompletions.map(completion => [
      completion.applicant_id || '-',
      completion.student_name,
      completion.company,
      completion.completion_status,
      completion.completion_date_formatted || '-',
      completion.verification_status === 'verified' ? 'Verified' : 'Pending',
      completion.last_application_formatted || '-',
      completion.verified_at ? new Date(completion.verified_at).toLocaleDateString() : '-',
      completion.eligibility !== null ? (completion.eligibility === true ? 'Eligible' : 'Not Eligible') : '-',
      completion.university_email,
      completion.notes || '-'
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `internship-completions-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch pending students with extensive debugging
  const fetchPendingStudents = async () => {
    console.log('🔄 Starting fetchPendingStudents...');
    setPendingStudentsLoading(true);
    
    try {
      const token = getValidToken();
      if (!token) {
        console.error('❌ No valid token found');
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      console.log('✅ Token validated');

      console.log('🌐 Making API call to /api/admin/pending-students');
      const response = await axios.get(`${BACKEND_URL}/api/admin/pending-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('📦 Raw API Response:', response);
      console.log('📊 Response Data:', response.data);
      console.log('🔢 Response Status:', response.status);

      // Validate response structure
      if (!response.data) {
        console.error('❌ No data in response');
        setError('Invalid response from server');
        return;
      }

      if (!response.data.batches) {
        console.error('❌ No batches in response data');
        console.log('📋 Available keys:', Object.keys(response.data));
        setError('Invalid response structure - no batches found');
        return;
      }

      console.log('📚 Batches found:', response.data.batches.length);
      console.log('🔢 Total pending:', response.data.total_pending);

      // Flatten batched data carefully
      const allStudents = [];
      let processedBatches = 0;
      let processedStudents = 0;

      response.data.batches.forEach((batch, batchIndex) => {
        console.log(`📦 Processing batch ${batchIndex + 1}:`, batch.batch_id);
        console.log(`🏫 University: ${batch.university}`);
        console.log(`👥 Students in batch: ${batch.students ? batch.students.length : 0}`);

        if (batch.students && Array.isArray(batch.students)) {
          batch.students.forEach((student, studentIndex) => {
            console.log(`👤 Student ${studentIndex + 1}:`, {
              id: student.id,
              name: `${student.first_name} ${student.last_name}`,
              email: student.university_email,
              status: student.status
            });
            
            // Add batch info to student for display
            const enrichedStudent = {
              ...student,
              batch_id: batch.batch_id,
              university: batch.university,
              uploaded_by: batch.uploaded_by,
              uploaded_at: batch.uploaded_at
            };
            
            allStudents.push(enrichedStudent);
            processedStudents++;
          });
          processedBatches++;
        } else {
          console.warn(`⚠️ Batch ${batchIndex + 1} has no students array:`, batch);
        }
      });

      console.log(`✅ Processing complete:`);
      console.log(`  📦 Batches processed: ${processedBatches}/${response.data.batches.length}`);
      console.log(`  👥 Students processed: ${processedStudents}`);
      console.log(`  📋 Final student array length: ${allStudents.length}`);

      console.log('🎯 Setting pendingStudents state...');
      setPendingStudents(allStudents);
      
      console.log('✅ fetchPendingStudents completed successfully');

    } catch (error) {
      console.error('❌ Error in fetchPendingStudents:', error);
      console.error('📝 Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      let errorMessage = 'Failed to load pending students';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      console.log('🏁 Setting loading to false');
      setPendingStudentsLoading(false);
    }
  };

  // Function to handle selection of pending students
  const toggleStudentSelection = (studentId) => {
    const newSelection = new Set(selectedPendingStudents);
    if (newSelection.has(studentId)) {
      newSelection.delete(studentId);
    } else {
      newSelection.add(studentId);
    }
    setSelectedPendingStudents(newSelection);
    console.log('Updated selection:', Array.from(newSelection));
  };

  // Function to scroll to specific section in Student Activity page
  const scrollToSection = (sectionId) => {
    const element = document.querySelector(`[data-card="${sectionId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Function to select/deselect all pending students
  const toggleSelectAllStudents = () => {
    if (selectedPendingStudents.size === pendingStudents.length) {
      // All selected, deselect all
      setSelectedPendingStudents(new Set());
      console.log('Deselected all students');
    } else {
      // Not all selected, select all
      const allIds = new Set(pendingStudents.map(student => student.id));
      setSelectedPendingStudents(allIds);
      console.log('Selected all students:', Array.from(allIds));
    }
  };

  // Function to approve selected students
  const approveSelectedStudents = async () => {
    if (selectedPendingStudents.size === 0) {
      console.log('❌ No students selected for approval');
      setStudentResponseData({
        type: 'error',
        action: 'approval',
        title: 'No Students Selected',
        message: 'Please select at least one student to approve.',
        details: []
      });
      setShowStudentResponseModal(true);
      return;
    }

    const selectedIds = Array.from(selectedPendingStudents);
    console.log('🚀 Starting approval process for students:', selectedIds);
    
    setProcessingStudents(true);
    
    try {
      const token = getValidToken();
      if (!token) {
        console.log('❌ No valid token found');
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      console.log('📡 Sending approve request to:', `${BACKEND_URL}/api/admin/approve-students`);
      console.log('📦 Request payload:', selectedIds);

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/approve-students`,
        selectedIds,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('✅ Approve response received:', response.data);
      console.log('🔍 Results details:', response.data.results);
      
      // Log each result in detail
      if (response.data.results && Array.isArray(response.data.results)) {
        response.data.results.forEach((result, index) => {
          console.log(`📋 Result ${index}:`, JSON.stringify(result, null, 2));
        });
      }
      
      // Handle the batched response structure
      const approvedCount = response.data.summary?.approved || 0;
      const failedCount = response.data.summary?.failed || 0;
      
      console.log(`📊 Summary: ${approvedCount} approved, ${failedCount} failed`);
      
      // Extract error details from results array
      let errorDetails = '';
      if (response.data.results && Array.isArray(response.data.results)) {
        const failedResults = response.data.results.filter(result => result.success === false);
        if (failedResults.length > 0) {
          errorDetails = failedResults.map(result => {
            let errorMsg = result.error || result.message || 'Unknown error';
            // Extract the main error message from PostgreSQL errors
            if (errorMsg.includes('NotNullViolation')) {
              const match = errorMsg.match(/null value in column "([^"]+)"/);
              if (match) {
                errorMsg = `Missing required field: ${match[1]}`;
              }
            }
            return `Student ${result.student_id}: ${errorMsg}`;
          }).join('; ');
          console.log('💥 Failed results:', failedResults);
        }
      }
      
      if (approvedCount > 0) {
        // Show success modal
        setStudentResponseData({
          type: 'success',
          action: 'approval',
          title: 'Students Approved Successfully',
          message: `Successfully approved ${approvedCount} student${approvedCount !== 1 ? 's' : ''}${failedCount > 0 ? ` (${failedCount} failed)` : ''}.`,
          details: failedCount > 0 ? [errorDetails] : []
        });
        setShowStudentResponseModal(true);
        
        // Clear selection
        setSelectedPendingStudents(new Set());
        console.log('🧹 Cleared selection after successful approval');
        
        // Refresh the list to show updated status
        await fetchPendingStudents();
      } else {
        // All failed - show detailed error
        const errorMessage = errorDetails || response.data.summary?.message || 'Unknown error';
        console.log('❌ All approvals failed:', errorMessage);
        setStudentResponseData({
          type: 'error',
          action: 'approval',
          title: 'Student Approval Failed',
          message: 'Failed to approve any students.',
          details: [errorMessage]
        });
        setShowStudentResponseModal(true);
      }

    } catch (err) {
      console.error('❌ Error approving students:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Failed to approve students';
      console.log('📝 Error details:', errorMessage);
      setStudentResponseData({
        type: 'error',
        action: 'approval',
        title: 'Approval Error',
        message: 'An unexpected error occurred while approving students.',
        details: [errorMessage]
      });
      setShowStudentResponseModal(true);
    } finally {
      console.log('🏁 Setting processing to false');
      setProcessingStudents(false);
    }
  };

  // Function to reject selected students
  const rejectSelectedStudents = async () => {
    if (selectedPendingStudents.size === 0) {
      console.log('❌ No students selected for rejection');
      setStudentResponseData({
        type: 'error',
        action: 'rejection',
        title: 'No Students Selected',
        message: 'Please select at least one student to reject.',
        details: []
      });
      setShowStudentResponseModal(true);
      return;
    }

    if (!rejectReason.trim()) {
      console.log('❌ No rejection reason provided');
      setStudentResponseData({
        type: 'error',
        action: 'rejection',
        title: 'Rejection Reason Required',
        message: 'Please provide a reason for rejection.',
        details: []
      });
      setShowStudentResponseModal(true);
      return;
    }

    const selectedIds = Array.from(selectedPendingStudents);
    console.log('🚀 Starting rejection process for students:', selectedIds);
    console.log('📝 Rejection reason:', rejectReason);
    
    setProcessingStudents(true);
    
    try {
      const token = getValidToken();
      if (!token) {
        console.log('❌ No valid token found');
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      console.log('📡 Sending reject request to:', `${BACKEND_URL}/api/admin/reject-students`);
      
      const requestData = {
        student_ids: selectedIds,
        reason: rejectReason.trim()
      };
      console.log('📦 Request payload:', requestData);

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/reject-students`,
        requestData,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('✅ Reject response received:', response.data);
      
      // Handle the batched response structure
      const rejectedCount = response.data.summary?.rejected || 0;
      const failedCount = response.data.summary?.failed || 0;
      
      console.log(`📊 Summary: ${rejectedCount} rejected, ${failedCount} failed`);
      
      if (rejectedCount > 0) {
        // Show success modal
        setStudentResponseData({
          type: 'success',
          action: 'rejection',
          title: 'Students Rejected Successfully',
          message: `Successfully rejected ${rejectedCount} student${rejectedCount !== 1 ? 's' : ''}${failedCount > 0 ? ` (${failedCount} failed)` : ''}.`,
          details: failedCount > 0 ? [`${failedCount} students failed to be rejected`] : []
        });
        setShowStudentResponseModal(true);
        
        // Clear selection and close modal
        setSelectedPendingStudents(new Set());
        setShowRejectModal(false);
        setRejectReason('');
        console.log('🧹 Cleared selection and closed modal after successful rejection');
        
        // Refresh the list to show updated status
        await fetchPendingStudents();
      } else {
        // Extract error details from results if available
        let errorDetails = '';
        if (response.data.results && Array.isArray(response.data.results)) {
          const failedResults = response.data.results.filter(result => result.success === false);
          if (failedResults.length > 0) {
            errorDetails = failedResults.map(result => 
              `Student ${result.student_id}: ${result.error || result.message || 'Unknown error'}`
            ).join('; ');
          }
        }
        
        const errorMessage = errorDetails || response.data.summary?.message || 'Unknown error';
        console.log('❌ All rejections failed:', errorMessage);
        setStudentResponseData({
          type: 'error',
          action: 'rejection',
          title: 'Student Rejection Failed',
          message: 'Failed to reject any students.',
          details: [errorMessage]
        });
        setShowStudentResponseModal(true);
      }

    } catch (err) {
      console.error('❌ Error rejecting students:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Failed to reject students';
      console.log('📝 Error details:', errorMessage);
      setStudentResponseData({
        type: 'error',
        action: 'rejection',
        title: 'Rejection Error',
        message: 'An unexpected error occurred while rejecting students.',
        details: [errorMessage]
      });
      setShowStudentResponseModal(true);
    } finally {
      console.log('🏁 Setting processing to false');
      setProcessingStudents(false);
    }
  };

  // Functions to fetch and toggle form field requirements
  const fetchFormFieldSettings = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      // Fetch race requirement setting
      const raceResponse = await axios.get(
        `${BACKEND_URL}/api/admin/settings/race_field_required`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setRaceRequired(raceResponse.data.setting_value === 'true');

      // Fetch ethnicity requirement setting
      const ethnicityResponse = await axios.get(
        `${BACKEND_URL}/api/admin/settings/ethnicity_field_required`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setEthnicityRequired(ethnicityResponse.data.setting_value === 'true');

    } catch (err) {
      console.error('Error fetching form field settings:', err);
      // Keep default values on error
    }
  };

  const toggleRaceRequired = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const newValue = !raceRequired;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/race_field_required`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean',
          description: 'Controls whether race field is required in student application form'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setRaceRequired(newValue);
      
      // Show modal instead of error message
      setSettingsModalData({
        success: true,
        setting: 'race',
        enabled: newValue,
        message: `Race field is now ${newValue ? 'required' : 'optional'} in the student application form.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error updating race field setting:', err);
      // Show error in modal
      setSettingsModalData({
        success: false,
        setting: 'race',
        message: err.response?.data?.detail || 'Failed to update race field setting'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleEthnicityRequired = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const newValue = !ethnicityRequired;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/ethnicity_field_required`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean',
          description: 'Controls whether ethnicity field is required in student application form'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setEthnicityRequired(newValue);
      
      // Show modal instead of error message
      setSettingsModalData({
        success: true,
        setting: 'ethnicity',
        enabled: newValue,
        message: `Ethnicity field is now ${newValue ? 'required' : 'optional'} in the student application form.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error updating ethnicity field setting:', err);
      // Show error in modal
      setSettingsModalData({
        success: false,
        setting: 'ethnicity',
        message: err.response?.data?.detail || 'Failed to update ethnicity field setting'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  const revokeVerificationToken = async (tokenId) => {
    setRevokingTokens(prev => new Set(prev).add(tokenId));
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      await axios.post(
        `${BACKEND_URL}/api/admin/verification-tokens/revoke/${tokenId}`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setError({
        type: 'success',
        message: 'Verification token revoked successfully'
      });

      // Refresh tokens list
      fetchVerificationTokens();

    } catch (err) {
      console.error('Error revoking verification token:', err);
      setError({ type: 'error', message: err.response?.data?.detail || 'Failed to revoke verification token' });
    } finally {
      setRevokingTokens(prev => {
        const newSet = new Set(prev);
        newSet.delete(tokenId);
        return newSet;
      });
    }
  };

  // Partner Account Management Functions
  const generateActivationToken = async (partnerId) => {
    setGeneratingActivation(prev => new Set(prev).add(partnerId));
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/university-partners/generate-activation-token`,
        { partner_id: parseInt(partnerId) },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Store the activation token for display
      console.log('Activation token response:', response.data); // Debug log
      console.log('Partner ID:', partnerId, 'Type:', typeof partnerId);

      setActivationTokens(prev => {
        // Use the original partnerId parameter to ensure consistency with display logic
        const newTokens = {
          ...prev,
          [partnerId]: response.data
        };
        console.log('Updated activation tokens state:', newTokens);
        console.log('Using partner ID key:', partnerId, 'Type:', typeof partnerId);
        console.log('Keys in activationTokens:', Object.keys(newTokens));
        return newTokens;
      });

      // Show modal instead of error message
      setActivationTokenData({
        partnerId: partnerId,
        url: response.data.activation_url,
        token: response.data.token
      });
      setShowActivationModal(true);

    } catch (err) {
      console.error('Error generating activation token:', err);
      console.error('Full error response:', err.response);
      console.error('Error data:', err.response?.data);
      setError({ type: 'error', message: err.response?.data?.detail || 'Failed to generate activation token' });
    } finally {
      setGeneratingActivation(prev => {
        const newSet = new Set(prev);
        newSet.delete(partnerId);
        return newSet;
      });
    }
  };

  const togglePartnerStatus = async (partnerId, currentStatus) => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      await axios.post(
        `${BACKEND_URL}/api/admin/university-partners/toggle-status`,
        parseInt(partnerId),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Show modal instead of error message
      const newStatus = !currentStatus;
      setPartnerActivationData({
        success: true,
        partnerId: partnerId,
        activated: newStatus,
        message: `Partner has been ${newStatus ? 'activated' : 'deactivated'} successfully.`
      });
      setShowPartnerActivationModal(true);

      // Refresh partners list
      fetchUniversityPartners();

    } catch (err) {
      console.error('Error toggling partner status:', err);
      console.error('Full error response:', err.response);
      console.error('Error data:', err.response?.data);

      let errorMessage = 'Failed to update partner status';
      if (err.response?.data?.detail) {
        // If detail is an array of validation errors, get the first message
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail[0]?.msg || errorMessage;
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
      }

      // Show error in modal
      setPartnerActivationData({
        success: false,
        message: errorMessage
      });
      setShowPartnerActivationModal(true);
    }
  };

  // Bulk operation functions
  const togglePartnerSelection = (partnerId) => {
    const newSelected = new Set(selectedPartners);
    if (newSelected.has(partnerId)) {
      newSelected.delete(partnerId);
    } else {
      newSelected.add(partnerId);
    }
    setSelectedPartners(newSelected);
  };

  const selectAllPartners = () => {
    setSelectedPartners(new Set(universityPartners.map(p => p.id)));
  };

  const deselectAllPartners = () => {
    setSelectedPartners(new Set());
  };

  const bulkGenerateActivationTokens = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (selectedPartners.size === 0) {
      setBulkModalData({
        type: 'error',
        title: 'No Partners Selected',
        message: 'Please select at least one partner to generate tokens for.',
        details: []
      });
      setShowBulkModal(true);
      return;
    }

    setBulkOperationInProgress(true);
    let successCount = 0;
    let errorCount = 0;
    const errors = [];
    const successfulTokens = {};

    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      for (const partnerId of selectedPartners) {
        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/admin/university-partners/generate-activation-token`,
            { partner_id: parseInt(partnerId) },
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          
          // Store token data for individual card display
          successfulTokens[partnerId] = response.data;
          successCount++;
        } catch (err) {
          errorCount++;
          const partner = universityPartners.find(p => p.id === partnerId);
          errors.push(`${partner?.full_name || partnerId}: ${err.message}`);
        }
      }

      // Update activation tokens state for individual card display
      setActivationTokens(prev => ({
        ...prev,
        ...successfulTokens
      }));

      // Show bulk modal with results
      setBulkModalData({
        type: successCount > 0 ? 'success' : 'error',
        title: 'Bulk Token Generation Complete',
        message: `Generated ${successCount} activation tokens successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}.`,
        details: errors
      });
      setShowBulkModal(true);

      setSelectedPartners(new Set());
    } catch (err) {
      setBulkModalData({
        type: 'error',
        title: 'Bulk Operation Failed',
        message: 'An unexpected error occurred during bulk token generation.',
        details: [err.message]
      });
      setShowBulkModal(true);
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const bulkRevokeActivationTokens = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (selectedPartners.size === 0) {
      setBulkModalData({
        type: 'error',
        title: 'No Partners Selected',
        message: 'Please select at least one partner to revoke tokens for.',
        details: []
      });
      setShowBulkModal(true);
      return;
    }

    // Show confirmation modal first
    if (!window.confirm(`Are you sure you want to revoke activation tokens for ${selectedPartners.size} selected partners? This action cannot be undone.`)) {
      return;
    }

    setBulkOperationInProgress(true);
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      for (const partnerId of selectedPartners) {
        try {
          await axios.post(
            `${BACKEND_URL}/api/admin/revoke-activation-token`,
            { token_id: parseInt(partnerId) },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          // Remove token from state since it's revoked
          setActivationTokens(prev => {
            const newTokens = { ...prev };
            delete newTokens[partnerId];
            return newTokens;
          });
          
          successCount++;
        } catch (err) {
          errorCount++;
          const partner = universityPartners.find(p => p.id === partnerId);
          errors.push(`${partner?.full_name || partnerId}: ${err.message}`);
        }
      }

      setBulkModalData({
        type: successCount > 0 ? 'success' : 'error',
        title: 'Bulk Token Revocation Complete',
        message: `Revoked ${successCount} activation tokens successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}.`,
        details: errors
      });
      setShowBulkModal(true);

      setSelectedPartners(new Set());
    } catch (err) {
      setBulkModalData({
        type: 'error',
        title: 'Bulk Operation Failed',
        message: 'An unexpected error occurred during bulk token revocation.',
        details: [err.message]
      });
      setShowBulkModal(true);
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const bulkTogglePartnerStatus = async (e, activate) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (selectedPartners.size === 0) {
      setBulkModalData({
        type: 'error',
        title: 'No Partners Selected',
        message: 'Please select at least one partner to change status for.',
        details: []
      });
      setShowBulkModal(true);
      return;
    }

    const action = activate ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} ${selectedPartners.size} selected partners?`)) {
      return;
    }

    setBulkOperationInProgress(true);
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      for (const partnerId of selectedPartners) {
        try {
          await axios.post(
            `${BACKEND_URL}/api/admin/university-partners/set-status`,
            {
              partner_id: parseInt(partnerId),
              is_active: activate
            },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          successCount++;
        } catch (err) {
          errorCount++;
          const partner = universityPartners.find(p => p.id === partnerId);
          errors.push(`${partner?.full_name || partnerId}: ${err.message}`);
        }
      }

      setBulkModalData({
        type: successCount > 0 ? 'success' : 'error',
        title: `Bulk ${activate ? 'Activation' : 'Deactivation'} Complete`,
        message: `${activate ? 'Activated' : 'Deactivated'} ${successCount} partners successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}.`,
        details: errors
      });
      setShowBulkModal(true);

      setSelectedPartners(new Set());
      fetchUniversityPartners(); // Refresh the list
    } catch (err) {
      setBulkModalData({
        type: 'error',
        title: 'Bulk Operation Failed',
        message: `An unexpected error occurred during bulk ${action}.`,
        details: [err.message]
      });
      setShowBulkModal(true);
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const updateFormWithSelectedIds = (selectedIds) => {
    const idsArray = Array.from(selectedIds);
    setGenerateTokenForm({
      ...generateTokenForm,
      applicantIds: idsArray.join(', ')
    });
  };

  const generateUpdateTokens = async () => {
    if (!generateTokenForm.applicantIds.trim()) {
      setError({ type: 'error', message: 'Please enter at least one applicant ID' });
      return;
    }

    try {
      const token = getValidToken();
      const applicantIds = generateTokenForm.applicantIds
        .split(',')
        .map(id => parseInt(id.trim()))
        .filter(id => !isNaN(id));

      if (applicantIds.length === 0) {
        setError({ type: 'error', message: 'Please enter valid applicant IDs (numbers only)' });
        return;
      }

      const requestBody = {
        applicant_ids: applicantIds,
        expires_in_hours: generateTokenForm.expiresInHours
      };

      const response = await axios.post(`${BACKEND_URL}/api/generate-update-tokens`, requestBody, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const tokenCount = response.data.success;
      const tokenText = tokenCount === 1 ? 'token' : 'tokens';
      setError({ type: 'success', message: `Successfully generated ${tokenCount} update ${tokenText}` });
      setGenerateTokenForm({ applicantIds: '', expiresInHours: 720 });
      setShowGenerateForm(false);
      fetchUpdateTokens(); // Refresh the tokens list
      
      // Auto-dismiss success message after 3 seconds
      setTimeout(() => {
        setError(null);
      }, 3000);
    } catch (err) {
      console.error('Error generating tokens:', err);
      let errorMessage = 'Failed to generate update tokens';

      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Handle validation errors
          errorMessage = err.response.data.detail.map(e => e.msg || e.toString()).join(', ');
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else {
          // Handle object errors (validation/rate limiting)
          errorMessage = 'Request failed due to validation errors. Please check your input and try again.';
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError({ type: 'error', message: errorMessage });
    }
  };

  const revokeTokens = async (tokenIds) => {
    try {
      const token = getValidToken();
      await axios.post(`${BACKEND_URL}/api/revoke-tokens`, tokenIds, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setError({ type: 'success', message: `Successfully revoked ${tokenIds.length} token(s)` });
      fetchUpdateTokens(); // Refresh the tokens list
    } catch (err) {
      console.error('Error revoking tokens:', err);
      setError({ type: 'error', message: err.response?.data?.detail || 'Failed to revoke tokens' });
    }
  };

  const extendToken = async (tokenId, days) => {
    try {
      const token = getValidToken();
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/extend-token/${tokenId}?days=${days}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setError({
        type: 'success',
        message: `Extended token by ${days} days for ${response.data.applicant_name}`
      });
      fetchUpdateTokens(); // Refresh the tokens list

      // Auto-dismiss success message after 3 seconds
      setTimeout(() => setError(null), 3000);
    } catch (err) {
      console.error('Error extending token:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to extend token';
      setError({ type: 'error', message: errorMessage });

      // Auto-dismiss error message after 5 seconds
      setTimeout(() => setError(null), 5000);
    }
  };

  const generateSingleToken = async (applicantId, applicantName) => {
    try {
      const token = getValidToken();
      const requestBody = {
        applicant_ids: [applicantId],
        expires_in_hours: 720 // Default 30 days
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/generate-update-tokens`,
        requestBody,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setError({
        type: 'success',
        message: `Generated new token for ${applicantName}`
      });
      fetchUpdateTokens(); // Refresh the tokens list

      // Auto-dismiss success message after 3 seconds
      setTimeout(() => setError(null), 3000);
    } catch (err) {
      console.error('Error generating token:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to generate token';
      setError({ type: 'error', message: errorMessage });

      // Auto-dismiss error message after 5 seconds
      setTimeout(() => setError(null), 5000);
    }
  };

  // Bulk token operations
  const toggleTokenSelection = (tokenId) => {
    const newSelection = new Set(selectedTokens);
    if (newSelection.has(tokenId)) {
      newSelection.delete(tokenId);
    } else {
      newSelection.add(tokenId);
    }
    setSelectedTokens(newSelection);
  };

  const toggleSelectAllTokens = () => {
    // Get currently visible tokens (respecting filters)
    const visibleSelectableTokens = filteredUpdateTokens
      .filter(token => token.id && !token.has_applied_this_cycle)
      .map(token => token.id);

    if (selectedTokens.size === visibleSelectableTokens.length && visibleSelectableTokens.length > 0) {
      // Deselect all
      setSelectedTokens(new Set());
    } else {
      // Select all visible selectable tokens
      setSelectedTokens(new Set(visibleSelectableTokens));
    }
  };

  const bulkExtendTokens = async (days) => {
    if (selectedTokens.size === 0) {
      setError({ type: 'error', message: 'No tokens selected' });
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      const token = getValidToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/extend-tokens-bulk?days=${days}`,
        Array.from(selectedTokens),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const { success_count, skipped_count, failed_count } = response.data;
      let message = `Extended ${success_count} token(s) by ${days} days`;
      if (skipped_count > 0) message += `, skipped ${skipped_count} (already applied)`;
      if (failed_count > 0) message += `, failed ${failed_count}`;

      setError({ type: 'success', message });
      setSelectedTokens(new Set()); // Clear selection
      fetchUpdateTokens(); // Refresh the tokens list

      // Auto-dismiss success message after 5 seconds
      setTimeout(() => setError(null), 5000);
    } catch (err) {
      console.error('Error bulk extending tokens:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to extend tokens';
      setError({ type: 'error', message: errorMessage });

      // Auto-dismiss error message after 5 seconds
      setTimeout(() => setError(null), 5000);
    }
  };

  const exportTokensToCSV = () => {
    // Get tokens to export (either selected or all filtered)
    const tokensToExport = selectedTokens.size > 0
      ? filteredUpdateTokens.filter(t => selectedTokens.has(t.id))
      : filteredUpdateTokens.filter(t => t.id); // Only export tokens with IDs

    if (tokensToExport.length === 0) {
      setError({ type: 'error', message: 'No tokens to export' });
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Create CSV headers
    const headers = [
      'Student ID',
      'Student Name',
      'Student Email',
      'Token',
      'Update Link',
      'Created Date',
      'Expiration Date',
      'Status',
      'Application Status',
      'University',
      'GPA',
      'Major',
      'Class Year'
    ];

    // Create CSV rows
    const rows = tokensToExport.map(token => [
      token.applicant_id,
      token.applicant_name,
      token.university_email_verified || token.personal_email || 'N/A',
      token.token || '',
      token.update_link || '',
      token.created_at ? new Date(token.created_at).toLocaleDateString() : '',
      token.expires_at ? new Date(token.expires_at).toLocaleDateString() : '',
      token.used ? 'Used' : token.is_expired ? 'Expired' : 'Active',
      token.has_applied_this_cycle ? 'Applied' : 'Not Applied',
      token.university || '',
      token.gpa_verified || token.current_gpa || '',
      token.major_verified || token.major || '',
      token.current_academic_class_verified || token.current_academic_class || ''
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape commas and quotes in cell content
        const cellStr = String(cell || '');
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `tokens_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setError({
      type: 'success',
      message: `Exported ${tokensToExport.length} token(s) to CSV`
    });
    setTimeout(() => setError(null), 3000);
  };

  const copyToClipboard = (text, showMessage = true) => {
    navigator.clipboard.writeText(text).then(() => {
      if (showMessage) {
        setError({ type: 'success', message: 'Copied to clipboard!' });
        setTimeout(() => setError(null), 2000);
      }
    }).catch(() => {
      if (showMessage) {
        setError({ type: 'error', message: 'Failed to copy to clipboard' });
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const handleStatusUpdate = async (recruiterId, newStatus) => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/update-recruiter-status/${recruiterId}`,
        newStatus,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Show success message briefly
      setError(null);
      const successMessage = `Recruiter status updated to ${newStatus}`;
      setError({ type: 'success', message: successMessage });
      setTimeout(() => setError(null), 3000);

      // Refresh the list
      fetchAllRecruiters();
    } catch (err) {
      console.error('Error updating recruiter status:', err);
      setError(err.response?.data?.detail || 'Failed to update recruiter status');
    }
  };

  // Export candidates for a recruiter (favorited or matching)
  const handleExportCandidates = async (recruiterId, recruiterName, exportType) => {
    const exportKey = `${recruiterId}-${exportType}`;

    try {
      setExportingCandidates(prev => new Set(prev).add(exportKey));

      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/recruiter/${recruiterId}/export-candidates`,
        {
          params: { export_type: exportType },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const { csv_data, total_candidates, cycle_year } = response.data;

      if (total_candidates === 0) {
        setError({
          type: 'info',
          message: `No ${exportType === 'favorited' ? 'favorited applicants' : 'matching applicants'} found for ${recruiterName}`
        });
        setTimeout(() => setError(null), 3000);
        return;
      }

      // Convert to CSV string
      const csvContent = csv_data.map(row =>
        row.map(cell => {
          const cellString = String(cell || '');
          if (cellString.includes(',') || cellString.includes('"') || cellString.includes('\n')) {
            return `"${cellString.replace(/"/g, '""')}"`;
          }
          return cellString;
        }).join(',')
      ).join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      // Generate filename
      const today = new Date().toISOString().split('T')[0];
      const sanitizedName = recruiterName.replace(/[^a-zA-Z0-9]/g, '_');
      const exportLabel = exportType === 'favorited' ? 'favorited_applicants' : 'matching_applicants';
      link.setAttribute('download', `${sanitizedName}_${exportLabel}_${cycle_year}_${today}.csv`);

      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setError({ type: 'success', message: `Exported ${total_candidates} ${exportType === 'favorited' ? 'favorited' : 'matching'} applicants for ${recruiterName}` });
      setTimeout(() => setError(null), 3000);

    } catch (err) {
      console.error('Error exporting candidates:', err);
      setError(err.response?.data?.detail || `Failed to export ${exportType} candidates`);
    } finally {
      setExportingCandidates(prev => {
        const next = new Set(prev);
        next.delete(exportKey);
        return next;
      });
    }
  };

  const handleSelectAll = () => {
    const allIds = new Set(searchResults.map(a => a.id));
    setSelectedApplicants(allIds);
    updateFormWithSelectedIds(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedApplicants(new Set());
    setGenerateTokenForm({ ...generateTokenForm, applicantIds: '' });
  };

  const toggleApplicantSelection = (applicantId) => {
    const newSelected = new Set(selectedApplicants);
    if (newSelected.has(applicantId)) {
      newSelected.delete(applicantId);
    } else {
      newSelected.add(applicantId);
    }
    setSelectedApplicants(newSelected);
    updateFormWithSelectedIds(newSelected);
  };

  const toggleRecruiter = (recruiterId) => {
    const newExpanded = new Set(expandedRecruiters);
    if (newExpanded.has(recruiterId)) {
      newExpanded.delete(recruiterId);
    } else {
      newExpanded.add(recruiterId);
    }
    setExpandedRecruiters(newExpanded);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Remove the early return for errors - we'll display them inline instead

  const filteredRecruiters = allRecruiters.filter(recruiter => {
    if (statusFilter === 'all') return true;
    const status = recruiter.status || (recruiter.approved ? 'approved' : 'pending');
    return status === statusFilter;
  }).sort((a, b) => {
    // Sort alphabetically by organization name
    const orgA = (a.organization || '').toLowerCase();
    const orgB = (b.organization || '').toLowerCase();
    return orgA.localeCompare(orgB);
  });

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 ${isMobile ? 'pt-14' : ''}`}>
      {/* Mobile Sidebar */}
      <MobileSidebar
        activeTab={activeTab}
        onTabChange={(tabId) => {
          if (tabId === 'home') {
            navigate('/admin');
          } else if (tabId === 'students') {
            setActiveTab('student-activity');
          } else if (tabId === 'recruiters') {
            setActiveTab('recruiters');
          } else if (tabId === 'profile') {
            // Logout functionality
            const token = getValidToken();
            if (token) {
              fetch(`${BACKEND_URL}/logout`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }).finally(() => {
                localStorage.removeItem('authToken');
                clearPermissionsCache();
                sessionStorage.removeItem('pending_tasks_checked');
                navigate('/admin');
              });
            } else {
              navigate('/admin');
            }
          } else {
            setActiveTab(tabId);
          }
        }}
        navigate={navigate}
        isOpen={isMobileSidebarOpen}
        onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />
      {/* Notification Modal */}
      {error && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={() => setError(null)}
            ></div>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className={`px-4 pt-5 pb-4 sm:p-6 sm:pb-4 ${
                error.type === 'success' ? 'bg-green-50 dark:bg-green-900' : 'bg-red-50 dark:bg-red-900'
              }`}>
                <div className="sm:flex sm:items-start">
                  <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
                    error.type === 'success' ? 'bg-green-100' : 'bg-red-100'
                  } sm:mx-0 sm:h-10 sm:w-10`}>
                    {error.type === 'success' ? (
                      <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    )}
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className={`text-lg leading-6 font-medium ${
                      error.type === 'success' ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'
                    }`} id="modal-title">
                      {error.type === 'success' ? 'Success' : 'Error'}
                    </h3>
                    <div className="mt-2">
                      <p className={`text-sm ${
                        error.type === 'success' ? 'text-green-700 dark:text-green-200' : 'text-red-700 dark:text-red-200'
                      }`}>
                        {error.message}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                    error.type === 'success' 
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Admin Dashboard
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                Manage recruiters and student update tokens
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-12 w-auto"
              />
              <button
                onClick={() => {
                  // Clear token from localStorage
                  localStorage.removeItem('token');
                  localStorage.removeItem('authToken');
                  // Clear any cached permissions
                  clearPermissionsCache();
                  // Redirect to landing page
                  navigate('/');
                  // Show confirmation
                  setTimeout(() => {
                    alert('Successfully logged out');
                  }, 100);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 hidden md:block">
          <nav className="flex space-x-8">
            {canAccessSectionSync(ADMIN_SECTIONS.RECRUITER_MANAGEMENT, permissions) && (
              <button
                onClick={() => switchToTab('recruiters')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'recruiters'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Recruiter Account Management ({allRecruiters.length})
              </button>
            )}
            {/* Company Dropdown */}
            {canAccessSectionSync(ADMIN_SECTIONS.COMPANY_MANAGEMENT, permissions) && (
              <div
                ref={companyDropdownRef}
                className="relative"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCompanyDropdownOpen(!companyDropdownOpen);
                  }}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 ${
                    companyDropdownOpen
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Company Account Management
                  <svg
                    className={`w-4 h-4 transform transition-transform ${companyDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {companyDropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                  >
                    <button
                      onClick={() => {
                        navigate('/admin/company-management');
                        setCompanyDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="font-medium">Company Account Management</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Manage company accounts and details</div>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/companies');
                        setCompanyDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="font-medium">All Companies</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">View all companies in the system</div>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/admin/workday-configuration');
                        setCompanyDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="font-medium">Workday Configuration</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Configure Workday integrations</div>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/admin/company-stats');
                        setCompanyDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="font-medium">Company Dashboard Stats</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">View matching statistics by company</div>
                    </button>
                    <button
                      onClick={() => {
                        setActiveTab('board-liaisons');
                        setCompanyDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded-b-md"
                    >
                      <div className="font-medium">Board Liaisons</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Manage company board liaisons</div>
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* University Partner Dropdown */}
            {canAccessSectionSync(ADMIN_SECTIONS.PARTNER_MANAGEMENT, permissions) && (
              <div
                ref={partnerDropdownRef}
                className="relative"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPartnerDropdownOpen(!partnerDropdownOpen);
                  }}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 ${
                    activeTab === 'partner-accounts' || activeTab === 'dashboard' || partnerDropdownOpen
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  University Partner Account Management
                  <svg
                    className={`w-4 h-4 transform transition-transform ${partnerDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {partnerDropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                  >
                    <button
                      onClick={() => {
                        switchToTab('partner-accounts');
                        if (!universityPartners.length) {
                          fetchUniversityPartners();
                        }
                        setPartnerDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="font-medium">Partner Accounts</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Manage university partner accounts</div>
                    </button>
                    <button
                      onClick={() => {
                        switchToTab('dashboard');
                        fetchDashboard();
                        setPartnerDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded-b-md"
                    >
                      <div className="font-medium">Verification Dashboard</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">View verification status and activity</div>
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* Students Dropdown */}
            {canAccessSectionSync(ADMIN_SECTIONS.STUDENT_MANAGEMENT, permissions) && (
              <div
                ref={studentsDropdownRef}
                className="relative"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setStudentsDropdownOpen(!studentsDropdownOpen);
                  }}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 ${
                    activeTab === 'approve-students' || studentsDropdownOpen
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Students
                  <svg
                    className={`w-3 h-3 transform transition-transform ${studentsDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {studentsDropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                  >
                    <button
                      onClick={() => {
                        navigate('/admin/student-matches');
                        setStudentsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="font-medium">Student Matches (Verified)</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Using verified data only</div>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/admin/student-matches-rdl');
                        setStudentsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors border-l-2 border-blue-500"
                    >
                      <div className="font-medium flex items-center gap-2">
                        Student Matches (RDL)
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100 rounded-full">NEW</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">What recruiters actually see</div>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/admin/non-matching-students');
                        setStudentsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="font-medium">Non-matching Students</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Students without verified matches</div>
                    </button>
                    <button
                      onClick={() => {
                        switchToTab('approve-students');
                        setStudentsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="font-medium">Approve New Students</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Review and approve pending students</div>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/admin/duplicate-students');
                        setStudentsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded-b-md"
                    >
                      <div className="font-medium">Merge Duplicate Students</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Find and merge duplicate student records</div>
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* Token Management Dropdown */}
            {canAccessSectionSync(ADMIN_SECTIONS.TOKEN_MANAGEMENT, permissions) && (
              <div
                ref={tokenDropdownRef}
                className="relative"
              >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setTokenDropdownOpen(!tokenDropdownOpen);
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 ${
                  activeTab === 'tokens' || activeTab === 'partner-tokens' || tokenDropdownOpen
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Token Management
                <svg
                  className={`w-4 h-4 transform transition-transform ${tokenDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {tokenDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                >
                  {canAccessSectionSync(ADMIN_SECTIONS.RECRUITER_TOKENS, permissions) && (
                    <button
                      onClick={() => {
                        navigate('/admin/recruiter-tokens');
                        setTokenDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700"
                    >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <div className="font-medium">Recruiter Tokens</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Manage recruiter access tokens</div>
                      </div>
                    </div>
                    </button>
                  )}
                  {canAccessSectionSync(ADMIN_SECTIONS.STUDENT_TOKENS, permissions) && (
                    <button
                      onClick={() => {
                        switchToTab('tokens');
                        setTokenDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700"
                    >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <div>
                        <div className="font-medium">Student Tokens</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Manage student update tokens</div>
                      </div>
                    </div>
                    </button>
                  )}
                  {canAccessSectionSync(ADMIN_SECTIONS.PARTNER_TOKENS, permissions) && (
                    <button
                      onClick={() => {
                        switchToTab('partner-tokens');
                        setTokenDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300"
                    >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <div>
                        <div className="font-medium">University Tokens</div>
                        <div className="text-xs text-red-600 font-semibold">Assign students to be verified via Partner Verification Form</div>
                      </div>
                    </div>
                    </button>
                  )}
                </div>
              )}
              </div>
            )}
            {/* User Activity Dropdown */}
            {canAccessSectionSync(ADMIN_SECTIONS.USER_ACTIVITY, permissions) && (
              <div
                ref={userActivityDropdownRef}
                className="relative"
              >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setUserActivityDropdownOpen(!userActivityDropdownOpen);
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 ${
                  activeTab === 'partner-activity' || activeTab === 'student-activity' || activeTab === 'recruiter-activity' || userActivityDropdownOpen
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                User Activity
                <svg
                  className={`w-4 h-4 transform transition-transform ${userActivityDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {userActivityDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                >
                  {canAccessSectionSync(ADMIN_SECTIONS.PARTNER_ACTIVITY, permissions) && (
                    <button
                      onClick={() => {
                        switchToTab('partner-activity');
                        fetchPartnerActivity();
                        setUserActivityDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700"
                    >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <div>
                        <div className="font-medium">Partner Activity</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">University partner login tracking</div>
                      </div>
                    </div>
                    </button>
                  )}
                  {canAccessSectionSync(ADMIN_SECTIONS.STUDENT_ACTIVITY, permissions) && (
                    <button
                      onClick={() => {
                        switchToTab('student-activity');
                        fetchStudentActivity();
                        fetchInternshipCompletions();
                        fetchStudentsUniversityMissing();
                        fetchUnassignedStudents();
                        fetchStudentLoginActivity();
                        setUserActivityDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700"
                    >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <div>
                        <div className="font-medium">Student Activity</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Applications and login tracking</div>
                      </div>
                    </div>
                    </button>
                  )}
                  {canAccessSectionSync(ADMIN_SECTIONS.RECRUITER_ACTIVITY, permissions) && (
                    <button
                      onClick={() => {
                        switchToTab('recruiter-activity');
                        fetchRecruiterActivity();
                        setUserActivityDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300"
                    >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <div className="font-medium">Recruiter Activity</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Interaction history and metrics</div>
                      </div>
                    </div>
                    </button>
                  )}
                </div>
              )}
              </div>
            )}
            {/* Reports Dropdown */}
            {canAccessSectionSync(ADMIN_SECTIONS.USER_ACTIVITY, permissions) && (
              <div
                ref={reportsDropdownRef}
                className="relative"
              >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setReportsDropdownOpen(!reportsDropdownOpen);
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 ${
                  reportsDropdownOpen
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Reports
                <svg
                  className={`w-4 h-4 transform transition-transform ${reportsDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Reports Dropdown Menu */}
              {reportsDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                >
                  {canAccessSectionSync(ADMIN_SECTIONS.STUDENT_ACTIVITY, permissions) && (
                    <button
                      onClick={() => {
                        navigate('/admin/student-survey-report');
                        setReportsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700"
                    >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <div className="font-medium">Student Survey Status Report</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Application status survey data by cycle</div>
                      </div>
                    </div>
                    </button>
                  )}
                  {canAccessSectionSync(ADMIN_SECTIONS.RECRUITER_ACTIVITY, permissions) && (
                    <button
                      onClick={() => {
                        navigate('/admin/recruiter-stats-report');
                        setReportsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700"
                    >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <div>
                        <div className="font-medium">Recruiter Status Report</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Recruiter activity statistics by cycle</div>
                      </div>
                    </div>
                    </button>
                  )}
                  {canAccessSectionSync(ADMIN_SECTIONS.RECRUITER_ACTIVITY, permissions) && (
                    <button
                      onClick={() => {
                        navigate('/admin/recruiter-pipeline');
                        setReportsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700"
                    >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <div>
                        <div className="font-medium">Internship Pipeline Report</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Students in interview/offer pipeline by company</div>
                      </div>
                    </div>
                    </button>
                  )}
                  {canAccessSectionSync(ADMIN_SECTIONS.STUDENT_ACTIVITY, permissions) && (
                    <button
                      onClick={() => {
                        navigate('/admin/university-partner-report');
                        setReportsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300"
                    >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <div>
                        <div className="font-medium">University Partner Report</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Student data by university for each cycle</div>
                      </div>
                    </div>
                    </button>
                  )}
                </div>
              )}
              </div>
            )}
            {canAccessSectionSync(ADMIN_SECTIONS.PARTNER_MANAGEMENT, permissions) && (
              <div className="relative" ref={organizationMatchesDropdownRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOrganizationMatchesDropdownOpen(!organizationMatchesDropdownOpen);
                  }}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 ${
                    organizationMatchesDropdownOpen
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Organization Matches
                  <svg
                    className={`w-4 h-4 transform transition-transform ${organizationMatchesDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {organizationMatchesDropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                  >
                    <button
                      onClick={() => {
                        navigate('/admin/organization-matches');
                        setOrganizationMatchesDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-blue-500">🏢</span>
                        <div>
                          <div className="font-medium">Company-Student Matches</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">View all organization matches</div>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/admin/multi-company-students');
                        setOrganizationMatchesDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-purple-500">👥</span>
                        <div>
                          <div className="font-medium">Multi-Company Students</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Students with multiple matches</div>
                        </div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Alumni & Development Dropdown */}
            {canAccessSectionSync(ADMIN_SECTIONS.ALUMNI_MANAGEMENT, permissions) && (
              <div
                ref={alumniDropdownRef}
                className="relative"
              >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setAlumniDropdownOpen(!alumniDropdownOpen);
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1 ${
                  alumniDropdownOpen
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Alumni & Development
                <svg
                  className={`w-4 h-4 transform transition-transform ${alumniDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {alumniDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                >
                  <button
                    onClick={() => {
                      window.open('/alumni/job-board', '_blank');
                      setAlumniDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-green-500">💼</span>
                      <div>
                        <div className="font-medium">Alumni Job Board</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">View alumni job postings</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/admin/resume-reviews');
                      setAlumniDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-blue-500">📄</span>
                      <div>
                        <div className="font-medium">Resume Reviews</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Manage AI resume review feature</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/admin/network-analytics');
                      setAlumniDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-purple-500">📊</span>
                      <div>
                        <div className="font-medium">Network Analytics</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">View alumni networking insights</div>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      navigate('/admin/content-moderation');
                      setAlumniDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-red-500">🛡️</span>
                      <div>
                        <div className="font-medium">Content Moderation</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Review reported content and take actions</div>
                      </div>
                    </div>
                  </button>

                  {canAccessSectionSync(ADMIN_SECTIONS.CONTENT_CURATION, permissions) && (
                    <>
                      <button
                        onClick={() => {
                          navigate('/admin/curation-topics');
                          setAlumniDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-purple-500">📋</span>
                          <div>
                            <div className="font-medium">Curation Topics</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Manage news and video topics</div>
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => {
                          navigate('/admin/pending-posts');
                          setAlumniDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-orange-500">⏳</span>
                          <div>
                            <div className="font-medium">Pending Posts</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Review and approve curator content</div>
                          </div>
                        </div>
                      </button>

                      {canAccessSectionSync(ADMIN_SECTIONS.CURATOR_MANAGEMENT, permissions) && (
                        <button
                          onClick={() => {
                            navigate('/admin/curator-management');
                            setAlumniDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-green-500">👥</span>
                            <div>
                              <div className="font-medium">Curator Management</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Grant curator access to alumni users</div>
                            </div>
                          </div>
                        </button>
                      )}
                    </>
                  )}

                  {canAccessSectionSync(ADMIN_SECTIONS.ALUMNI_MANAGEMENT, permissions) && (
                    <>
                      <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>

                      {canAccessSectionSync(ADMIN_SECTIONS.EXECUTIVE_INTELLIGENCE, permissions) && (
                      <button
                        onClick={() => {
                          navigate('/admin/executive-intelligence');
                          setAlumniDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center gap-3">
                          <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          <div>
                            <div className="font-medium text-indigo-600 dark:text-indigo-400">Executive Intelligence</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Alumni career tracking &amp; leadership scoring</div>
                          </div>
                        </div>
                      </button>
                      )}

                      {canAccessSectionSync(ADMIN_SECTIONS.SWEE, permissions) && (
                      <button
                        onClick={() => {
                          navigate('/admin/swee');
                          setAlumniDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center gap-3">
                          <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                          <div>
                            <div className="font-medium text-emerald-600 dark:text-emerald-400">SWEE</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Workforce evolution &amp; earnings call analysis</div>
                          </div>
                        </div>
                      </button>
                      )}

                      {canAccessSectionSync(ADMIN_SECTIONS.MEMBERSHIP_MANAGER, permissions) && (
                      <button
                        onClick={() => {
                          navigate('/admin/membership-manager');
                          setAlumniDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center gap-3">
                          <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          <div>
                            <div className="font-medium text-amber-600 dark:text-amber-400">Membership Manager</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Upgrade and manage alumni tiers</div>
                          </div>
                        </div>
                      </button>
                      )}

                      <button
                        onClick={() => {
                          navigate('/admin/alumni-research-assignments');
                          setAlumniDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center gap-3">
                          <svg className="w-5 h-5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          <div>
                            <div className="font-medium text-teal-600 dark:text-teal-400">Research & Outreach Assignments</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">My alumni LinkedIn research tasks</div>
                          </div>
                        </div>
                      </button>

                      {canAccessSectionSync(ADMIN_SECTIONS.CRM, permissions) && (
                      <button
                        onClick={() => {
                          navigate('/admin/crm');
                          setAlumniDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center gap-3">
                          <svg className="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <div>
                            <div className="font-medium text-violet-600 dark:text-violet-400">CRM</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Alumni contact management, gifts & outreach</div>
                          </div>
                        </div>
                      </button>
                      )}

                      {canAccessSectionSync(ADMIN_SECTIONS.GRANT_MANAGEMENT, permissions) && (
                      <button
                        onClick={() => {
                          navigate('/admin/grants');
                          setAlumniDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center gap-3">
                          <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <div className="font-medium text-amber-600 dark:text-amber-400">Grant Management</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Track grant opportunities & pipeline</div>
                          </div>
                        </div>
                      </button>
                      )}

                      {permissions?.is_super_admin && (
                        <button
                          onClick={() => {
                            navigate('/admin/outreach-manager');
                            setAlumniDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                        >
                          <div className="flex items-center gap-3">
                            <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <div>
                              <div className="font-medium text-purple-600 dark:text-purple-400">Outreach Manager</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Create & manage outreach assignments across all staff</div>
                            </div>
                          </div>
                        </button>
                      )}
                    </>
                  )}

                  {canAccessSectionSync(ADMIN_SECTIONS.ALUMNI_DASH_ADMIN, permissions) && (
                  <button
                    onClick={() => {
                      setShowAlumniAdminModal(true);
                      setAlumniDropdownOpen(false);
                      fetchApifyToken();
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 rounded-md"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-blue-500">⚙️</span>
                      <div>
                        <div className="font-medium">Alumni Dash Admin</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Configure LinkedIn import settings</div>
                      </div>
                    </div>
                  </button>
                  )}
                </div>
              )}
            </div>
            )}

            {canAccessSectionSync(ADMIN_SECTIONS.PLATFORM_SETTINGS, permissions) && (
              <button
                onClick={() => navigate('/admin/platform-settings')}
                className="py-2 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 font-medium text-sm"
              >
                Platform Settings
              </button>
            )}
            {canAccessSectionSync(ADMIN_SECTIONS.SYSTEM_SETTINGS, permissions) && (
              <button
                onClick={() => navigate('/admin/user-management')}
                className="py-2 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 font-medium text-sm"
              >
                Admin Users
              </button>
            )}
          </nav>
        </div>

        {/* Error/Success Messages */}
        {error && typeof error === 'object' && error.message && (
          <div className={`mb-6 p-4 rounded-md ${
            error.type === 'success'
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}>
            {error.message}
          </div>
        )}

        {/* Recruiters Tab */}
        {activeTab === 'recruiters' && (
          <>
            {/* Filter Buttons */}
            <div className="mb-6 flex flex-wrap gap-2 justify-center sm:justify-start">
              {['all', 'pending', 'approved', 'rejected'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setStatusFilter(filter)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === filter
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)} (
                  {filter === 'all'
                    ? allRecruiters.length
                    : allRecruiters.filter(r => (r.status || (r.approved ? 'approved' : 'pending')) === filter).length
                  })
                </button>
              ))}
              
              {/* Navigation Link to Recruiter Tokens */}
              {canAccessSectionSync(ADMIN_SECTIONS.RECRUITER_TOKENS, permissions) && (
                <button
                  onClick={() => navigate('/admin/recruiter-tokens')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800 dark:hover:bg-green-900/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-3.586l4.293-4.293A6 6 0 0115 7z" />
                  </svg>
                  Recruiter Tokens
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
              
              {/* MapView Application Link */}
              {canAccessSectionSync(ADMIN_SECTIONS.MAP_VIEW_ACCESS, permissions) && (
                <button
                  onClick={() => navigate('/app')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800 dark:hover:bg-purple-900/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  MapView (2024-2025)
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
              
              {/* Blog Admin Link */}
              {canAccessSectionSync(ADMIN_SECTIONS.BLOG_MANAGEMENT, permissions) && (
                <button
                  onClick={() => navigate('/blog-admin')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800 dark:hover:bg-orange-900/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Blog Admin
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {/* Job Analytics Link */}
              {canAccessSectionSync(ADMIN_SECTIONS.JOB_ANALYTICS_ACCESS, permissions) && (
                <button
                  onClick={() => navigate('/admin/job-analytics')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800 dark:hover:bg-purple-900/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Job Analytics
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {/* Business Development Link */}
              {canAccessSectionSync(ADMIN_SECTIONS.BUSINESS_DEVELOPMENT, permissions) && (
                <button
                  onClick={() => navigate('/admin/business-development')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800 dark:hover:bg-emerald-900/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6.5M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m-8 0H6a2 2 0 00-2 2v6.5" />
                  </svg>
                  Business Development
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {/* Feature Toggle Link */}
              {canAccessSectionSync(ADMIN_SECTIONS.FEATURE_CONTROL_ACCESS, permissions) && (
                <button
                  onClick={() => navigate('/admin/feature-toggles')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-400 dark:border-indigo-800 dark:hover:bg-indigo-900/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Feature Control
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {/* Student Records Link */}
              {canAccessSectionSync(ADMIN_SECTIONS.STUDENT_RECORDS, permissions) && (
                <button
                  onClick={() => navigate('/admin/student-records')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100 dark:bg-cyan-900/20 dark:text-cyan-400 dark:border-cyan-800 dark:hover:bg-cyan-900/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Student Records
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {/* Merge Duplicate Students Link */}
              {canAccessSectionSync(ADMIN_SECTIONS.STUDENT_RECORDS, permissions) && (
                <button
                  onClick={() => navigate('/admin/duplicate-students')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800 dark:hover:bg-yellow-900/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Merge Duplicate Students
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {/* Platform Settings Link */}
              {canAccessSectionSync(ADMIN_SECTIONS.PLATFORM_SETTINGS, permissions) && (
                <button
                  onClick={() => navigate('/admin/platform-settings')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800 dark:hover:bg-orange-900/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  Platform Settings
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Recruiters List */}
            {filteredRecruiters.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <p className="text-gray-600 dark:text-gray-300">
                  No recruiters found for the selected filter.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredRecruiters.map((recruiter) => {
                  const status = recruiter.status || (recruiter.approved ? 'approved' : 'pending');
                  const isExpanded = expandedRecruiters.has(recruiter.id);

                  return (
                    <div key={recruiter.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                      {/* Recruiter Header */}
                      <div
                        className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => toggleRecruiter(recruiter.id)}
                      >
                        <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                          <div className="flex-1 w-full">
                            <div className="flex flex-wrap items-center gap-3 mb-3">
                              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {recruiter.organization}
                              </h2>
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                status === 'approved'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : status === 'rejected'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              }`}>
                                {status.toUpperCase()}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                              <div>
                                <p><strong>Recruiter:</strong> {recruiter.name}</p>
                                <p><strong>Email:</strong> {recruiter.email}</p>
                                <p><strong>Phone:</strong> {recruiter.phone || 'Not provided'}</p>

                                <div className="mt-3">
                                  <InternshipDropdown
                                    description={recruiter.internship_description}
                                    recruiterId={recruiter.id}
                                    expandedDescriptions={expandedDescriptions}
                                    setExpandedDescriptions={setExpandedDescriptions}
                                  />
                                </div>
                              </div>
                              <div>
                                <p><strong>Company Locations:</strong> {
                                  (() => {
                                    if (!recruiter.company_locations) return 'Not specified';

                                    let company_locations = {};
                                    let locations_display = [];

                                    // Parse company locations with error handling (same logic as backend)
                                    try {
                                      company_locations = JSON.parse(recruiter.company_locations);
                                    } catch (e) {
                                      if (recruiter.company_locations) {
                                        company_locations = {"Location": [recruiter.company_locations]};
                                      } else {
                                        company_locations = {};
                                      }
                                    }

                                    // Handle the case where we wrapped invalid JSON in {"Location": [string]}
                                    if (Object.keys(company_locations).length === 1 && "Location" in company_locations) {
                                      const raw_location = company_locations["Location"][0];

                                      // Try to parse and fix common JSON issues
                                      try {
                                        const parsed_locations = JSON.parse(raw_location);
                                        if (typeof parsed_locations === 'object' && parsed_locations !== null) {
                                          // Successfully parsed the embedded JSON
                                          for (const [state, cities] of Object.entries(parsed_locations)) {
                                            if (Array.isArray(cities)) {
                                              const cities_str = cities.join(", ");
                                              locations_display.push(`${state}: ${cities_str}`);
                                            } else {
                                              locations_display.push(`${state}: ${cities}`);
                                            }
                                          }
                                        } else {
                                          // Not a dict, just use the raw string
                                          locations_display.push(raw_location);
                                        }
                                      } catch (e2) {
                                        // Try to extract state/city information from malformed JSON
                                        if (raw_location.startsWith('{') && raw_location.endsWith('}')) {
                                          // Looks like JSON but is malformed, try to extract readable info
                                          // Remove outer braces and quotes
                                          const content = raw_location.slice(1, -1).replace(/"/g, '');

                                          // Split by commas and try to identify state:city patterns
                                          const parts = content.split(',');
                                          let current_state = null;

                                          for (let i = 0; i < parts.length; i++) {
                                            const part = parts[i].trim();
                                            if (part.includes(':')) {
                                              // This looks like "State:[cities]" or "State:city"
                                              const [state_part, city_part] = part.split(':', 2);
                                              current_state = state_part.trim();
                                              let city_part_clean = city_part.trim();

                                              // Handle city lists like "[city1,city2]"
                                              if (city_part_clean.startsWith('[')) {
                                                // The city list might span multiple parts if there are commas
                                                const city_list_parts = [city_part_clean];

                                                // If this part doesn't end with ], look for the closing bracket
                                                if (!city_part_clean.endsWith(']')) {
                                                  let j = i + 1;
                                                  while (j < parts.length) {
                                                    const next_part = parts[j].trim();
                                                    city_list_parts.push(next_part);
                                                    if (next_part.endsWith(']')) {
                                                      i = j; // Skip the parts we've consumed
                                                      break;
                                                    }
                                                    j++;
                                                  }
                                                }

                                                // Join all the city list parts and parse
                                                const full_city_list = city_list_parts.join(',');
                                                if (full_city_list.startsWith('[') && full_city_list.endsWith(']')) {
                                                  const cities_str = full_city_list.slice(1, -1); // Remove brackets
                                                  const cities = cities_str.split(',').map(c => c.trim()).filter(c => c);
                                                  if (cities.length > 0) {
                                                    locations_display.push(`${current_state}: ${cities.join(', ')}`);
                                                  } else {
                                                    locations_display.push(current_state);
                                                  }
                                                } else {
                                                  // Malformed bracket structure, just use the raw content
                                                  locations_display.push(`${current_state}: ${city_part_clean}`);
                                                }
                                              } else {
                                                locations_display.push(`${current_state}: ${city_part_clean}`);
                                              }
                                            } else if (current_state && part) {
                                              // This might be a continuation of the previous state's cities
                                              // Only add if it doesn't look like a new state name
                                              if (!part.match(/^[A-Z][a-z\s]+$/)) {
                                                locations_display.push(`${current_state}: ${part}`);
                                              } else {
                                                // This looks like a new state without a colon
                                                locations_display.push(part);
                                                current_state = null;
                                              }
                                            } else if (part) {
                                              // Just a location without state context
                                              locations_display.push(part);
                                            }
                                          }
                                        } else {
                                          // Not JSON-like, just use as-is
                                          locations_display.push(raw_location);
                                        }
                                      }
                                    } else {
                                      // Handle normal case where company_locations is properly formatted
                                      if (Array.isArray(company_locations)) {
                                        // Array of locations
                                        locations_display = company_locations;
                                      } else if (typeof company_locations === 'object') {
                                        // Object with state:cities mapping
                                        for (const [state, cities] of Object.entries(company_locations)) {
                                          if (Array.isArray(cities)) {
                                            const cities_str = cities.join(", ");
                                            locations_display.push(`${state}: ${cities_str}`);
                                          } else {
                                            locations_display.push(`${state}: ${cities}`);
                                          }
                                        }
                                      } else {
                                        locations_display.push(company_locations.toString());
                                      }
                                    }

                                    return locations_display.length > 0 ? locations_display.join("; ") : recruiter.company_locations;
                                  })()
                                }</p>
                                <p><strong>Minimum GPA:</strong> {recruiter.minimum_gpa}</p>

                                <div className="mt-3 space-y-2">
                                  <RequirementDropdown
                                    label="Eligible Majors"
                                    items={recruiter.eligible_majors ? recruiter.eligible_majors.split(',').map(item => item.trim()) : []}
                                    recruiterId={recruiter.id}
                                    fieldName="majors"
                                    expandedItems={expandedItems}
                                    setExpandedItems={setExpandedItems}
                                  />
                                  <RequirementDropdown
                                    label="Preferred Institutions"
                                    items={recruiter.preferred_institutions ? recruiter.preferred_institutions.split(',').map(item => item.trim()) : []}
                                    recruiterId={recruiter.id}
                                    fieldName="institutions"
                                    expandedItems={expandedItems}
                                    setExpandedItems={setExpandedItems}
                                  />
                                  <div>
                                    <p className="font-medium text-gray-700 dark:text-gray-300">Preferred Class Years:</p>
                                    <p className="text-gray-600 dark:text-gray-400">
                                      {recruiter.preferred_class_years ? recruiter.preferred_class_years.split(',').map(item => item.trim()).join(', ') : 'Not specified'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-row lg:flex-col items-start lg:items-end gap-2 w-full lg:w-auto">
                            {/* Action Buttons */}
                            <div className="flex flex-row sm:flex-col gap-2 flex-wrap sm:flex-nowrap">
                              {status === 'pending' && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusUpdate(recruiter.id, 'approved');
                                    }}
                                    className="px-3 py-2 bg-green-600 text-white text-xs sm:text-sm rounded hover:bg-green-700 transition-colors whitespace-nowrap"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusUpdate(recruiter.id, 'rejected');
                                    }}
                                    className="px-3 py-2 bg-red-600 text-white text-xs sm:text-sm rounded hover:bg-red-700 transition-colors whitespace-nowrap"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                              {status === 'approved' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusUpdate(recruiter.id, 'rejected');
                                  }}
                                  className="px-3 py-2 bg-red-600 text-white text-xs sm:text-sm rounded hover:bg-red-700 transition-colors whitespace-nowrap"
                                >
                                  Reject
                                </button>
                              )}
                              {status === 'rejected' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusUpdate(recruiter.id, 'approved');
                                  }}
                                  className="px-3 py-2 bg-green-600 text-white text-xs sm:text-sm rounded hover:bg-green-700 transition-colors whitespace-nowrap"
                                >
                                  Approve
                                </button>
                              )}
                              
                              {/* View Dashboard Button - Always available */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/recruiter/dashboard?adminView=true&recruiterId=${recruiter.id}`, '_blank');
                                }}
                                className="px-3 py-2 bg-blue-600 text-white text-xs sm:text-sm rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-1 whitespace-nowrap"
                                title={`View ${recruiter.name}'s dashboard`}
                              >
                                <span className="hidden sm:inline">👁️</span> View Dashboard
                              </button>

                              {/* Export Favorited Applicants Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExportCandidates(recruiter.id, recruiter.name || recruiter.organization, 'favorited');
                                }}
                                disabled={exportingCandidates.has(`${recruiter.id}-favorited`)}
                                className="px-3 py-2 bg-pink-600 text-white text-xs sm:text-sm rounded hover:bg-pink-700 transition-colors flex items-center justify-center gap-1 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                title={`Export applicants who favorited ${recruiter.organization}`}
                              >
                                {exportingCandidates.has(`${recruiter.id}-favorited`) ? (
                                  <>
                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span className="hidden sm:inline">Exporting...</span>
                                  </>
                                ) : (
                                  <>
                                    <span className="hidden sm:inline">💝</span> Favorited Export
                                  </>
                                )}
                              </button>

                              {/* Export Matching Applicants Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExportCandidates(recruiter.id, recruiter.name || recruiter.organization, 'matching');
                                }}
                                disabled={exportingCandidates.has(`${recruiter.id}-matching`)}
                                className="px-3 py-2 bg-purple-600 text-white text-xs sm:text-sm rounded hover:bg-purple-700 transition-colors flex items-center justify-center gap-1 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                title={`Export all matching applicants for ${recruiter.organization}`}
                              >
                                {exportingCandidates.has(`${recruiter.id}-matching`) ? (
                                  <>
                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span className="hidden sm:inline">Exporting...</span>
                                  </>
                                ) : (
                                  <>
                                    <span className="hidden sm:inline">📋</span> Matching Export
                                  </>
                                )}
                              </button>
                            </div>

                            {/* Expand/Collapse Arrow - Hidden on mobile, shown on larger screens */}
                            <div className="hidden lg:block">
                              <svg
                                className={`w-6 h-6 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        
                        {/* Mobile Expand Indicator */}
                        <div className="lg:hidden mt-4 flex justify-center">
                          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            {isExpanded ? 'Tap to collapse' : 'Tap to expand details'}
                            <svg
                              className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 dark:border-gray-700">
                          <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                              Company Details
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                              <div className="space-y-3">
                                <div>
                                  <p className="font-medium text-gray-700 dark:text-gray-300">Title:</p>
                                  <p className="text-gray-600 dark:text-gray-400">{recruiter.title || 'Not provided'}</p>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-700 dark:text-gray-300">Internship Type:</p>
                                  <p className="text-gray-600 dark:text-gray-400">{recruiter.internship_type}</p>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-700 dark:text-gray-300">Housing Provided:</p>
                                  <p className="text-gray-600 dark:text-gray-400">{recruiter.housing_provided}</p>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-700 dark:text-gray-300">Salary:</p>
                                  <p className="text-gray-600 dark:text-gray-400">{recruiter.internship_salary} ({recruiter.salary_type})</p>
                                </div>
                              </div>

                              <div className="space-y-3">
                                <div>
                                  <p className="font-medium text-gray-700 dark:text-gray-300">Eligible Majors:</p>
                                  <p className="text-gray-600 dark:text-gray-400">{recruiter.eligible_majors}</p>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-700 dark:text-gray-300">Internship Length:</p>
                                  <p className="text-gray-600 dark:text-gray-400">{recruiter.internship_length} weeks</p>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-700 dark:text-gray-300">Expected Start Date:</p>
                                  <p className="text-gray-600 dark:text-gray-400">
                                    {recruiter.expected_start_date ? new Date(recruiter.expected_start_date).toLocaleDateString() : 'Not specified'}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {recruiter.company_description && (
                              <div className="mt-6">
                                <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">Company Description:</p>
                                <div className="p-3 bg-gray-50 dark:bg-gray-600 rounded border text-gray-700 dark:text-gray-300">
                                  {recruiter.company_description.split('\n').filter(line => line.trim()).map((paragraph, index) => (
                                    <p key={index} className="mb-2 last:mb-0">{paragraph.trim()}</p>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Student Update Tokens Tab */}
        {activeTab === 'tokens' && canAccessSectionSync(ADMIN_SECTIONS.STUDENT_TOKENS, permissions) && (
          <>
            {/* Cycle Period Selector */}
            <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-shrink-0">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cycle Period:
                  </label>
                </div>
                <select
                  value={selectedCycle || ''}
                  onChange={(e) => setSelectedCycle(parseInt(e.target.value))}
                  disabled={cyclesLoading || availableCycles.length === 0}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cyclesLoading ? (
                    <option>Loading cycles...</option>
                  ) : availableCycles.length === 0 ? (
                    <option>No cycles configured</option>
                  ) : (
                    availableCycles.map(cycle => (
                      <option key={cycle.cycle_year} value={cycle.cycle_year}>
                        {cycle.cycle_year} {cycle.is_current ? '(Current)' : ''}
                        {cycle.notes ? ` - ${cycle.notes}` : ''}
                      </option>
                    ))
                  )}
                </select>
                {selectedCycle && selectedCycle !== currentCycleYear && (
                  <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium whitespace-nowrap">Viewing past cycle</span>
                  </div>
                )}
              </div>
            </div>

            {/* Token Filter */}
            <div className="mb-6 grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              {[
                { key: 'active', label: 'Active' },
                { key: 'all', label: 'All' },
                { key: 'expired', label: 'Expired' },
                { key: 'used', label: 'Used' },
                { key: 'no_token', label: 'No Token Issued' }
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setTokenFilter(filter.key)}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors text-center ${
                    tokenFilter === filter.key
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Application Status Filter - Show for tokens with IDs (not no_token filter) */}
            {tokenFilter !== 'no_token' && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                  Filter by Application Status
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setApplicationStatusFilter('all')}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors text-center ${
                      applicationStatusFilter === 'all'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    All Students
                    <div className="text-xs mt-1">({updateTokens.length})</div>
                  </button>
                  <button
                    onClick={() => setApplicationStatusFilter('not_applied')}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors text-center ${
                      applicationStatusFilter === 'not_applied'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    Not Applied
                    <div className="text-xs mt-1">({updateTokens.filter(t => !t.has_applied_this_cycle).length})</div>
                  </button>
                  <button
                    onClick={() => setApplicationStatusFilter('applied')}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors text-center ${
                      applicationStatusFilter === 'applied'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    Applied
                    <div className="text-xs mt-1">({updateTokens.filter(t => t.has_applied_this_cycle).length})</div>
                  </button>
                </div>
              </div>
            )}

            {/* Bulk Actions Bar - Only show for tokens with IDs (not no_token filter) */}
            {tokenFilter !== 'no_token' && updateTokens.some(t => t.id && !t.has_applied_this_cycle) && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-750 rounded-lg border-2 border-blue-200 dark:border-blue-900">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  {/* Select All Checkbox */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={
                        selectedTokens.size > 0 &&
                        selectedTokens.size === filteredUpdateTokens.filter(t => t.id && !t.has_applied_this_cycle).length
                      }
                      onChange={toggleSelectAllTokens}
                      className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    />
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Select All ({selectedTokens.size} selected)
                    </label>
                  </div>

                  {/* Bulk Action Buttons */}
                  {selectedTokens.size > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => bulkExtendTokens(30)}
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                      >
                        Extend 30 Days ({selectedTokens.size})
                      </button>
                      <button
                        onClick={() => bulkExtendTokens(60)}
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                      >
                        Extend 60 Days ({selectedTokens.size})
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Revoke ${selectedTokens.size} selected token(s)?`)) {
                            revokeTokens(Array.from(selectedTokens));
                          }
                        }}
                        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors shadow-sm"
                      >
                        Revoke ({selectedTokens.size})
                      </button>
                      <button
                        onClick={exportTokensToCSV}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                      >
                        Export CSV ({selectedTokens.size > 0 ? selectedTokens.size : filteredUpdateTokens.filter(t => t.id).length})
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Student Group Filter - Only show when "No Token Issued" is active */}
            {tokenFilter === 'no_token' && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                  Filter by Student Group
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  <button
                    onClick={() => setStudentGroupFilter('')}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors text-center ${
                      studentGroupFilter === ''
                        ? 'bg-purple-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    All Groups
                    {groupCounts && <div className="text-xs mt-1">({Object.values(groupCounts).reduce((a, b) => a + b, 0)})</div>}
                  </button>
                  <button
                    onClick={() => setStudentGroupFilter('self_registered')}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors text-center ${
                      studentGroupFilter === 'self_registered'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    Self-Registered
                    {groupCounts && <div className="text-xs mt-1">({groupCounts.self_registered || 0})</div>}
                  </button>
                  <button
                    onClick={() => setStudentGroupFilter('admin_upload')}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors text-center ${
                      studentGroupFilter === 'admin_upload'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    Admin Upload
                    {groupCounts && <div className="text-xs mt-1">({groupCounts.admin_upload || 0})</div>}
                  </button>
                  <button
                    onClick={() => setStudentGroupFilter('partner_upload_approved')}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors text-center ${
                      studentGroupFilter === 'partner_upload_approved'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    Partner Upload
                    {groupCounts && <div className="text-xs mt-1">({groupCounts.partner_upload_approved || 0})</div>}
                  </button>
                  <button
                    onClick={() => setStudentGroupFilter('partner_verified')}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors text-center ${
                      studentGroupFilter === 'partner_verified'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    Partner Verified
                    {groupCounts && <div className="text-xs mt-1">({groupCounts.partner_verified || 0})</div>}
                  </button>
                  <button
                    onClick={() => setStudentGroupFilter('unverified')}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors text-center ${
                      studentGroupFilter === 'unverified'
                        ? 'bg-purple-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    Unverified
                    {groupCounts && <div className="text-xs mt-1">({groupCounts.unverified || 0})</div>}
                  </button>
                </div>
              </div>
            )}

            {/* Data Controls Bar - Application Status + Sort Order */}
            {tokenFilter === 'no_token' && (
              <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">

                  {/* Application Status Filter */}
                  <div className="flex items-center gap-2 flex-1 flex-wrap">
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      Application:
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => setApplicationStatusFilter('all')}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          applicationStatusFilter === 'all'
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        All {appStatusCounts && `(${appStatusCounts.all || 0})`}
                      </button>
                      <button
                        onClick={() => setApplicationStatusFilter('not_applied')}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          applicationStatusFilter === 'not_applied'
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        Not Applied {appStatusCounts && `(${appStatusCounts.not_applied || 0})`}
                      </button>
                      <button
                        onClick={() => setApplicationStatusFilter('applied')}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          applicationStatusFilter === 'applied'
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        Applied {appStatusCounts && `(${appStatusCounts.applied || 0})`}
                      </button>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="hidden sm:block w-px h-8 bg-gray-300 dark:bg-gray-600"></div>

                  {/* Sort Order */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      Sort:
                    </label>
                    <button
                      onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      {sortOrder === 'desc' ? (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          Newest
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                          Oldest
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Bulk Token Generation for All Matching Students */}
            {tokenFilter === 'no_token' && (
              <div className="mb-6 p-4 bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-purple-900/20 dark:via-gray-800 dark:to-pink-900/20 border-l-4 border-purple-500 rounded-lg shadow-md">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-1">
                      Bulk Token Generation
                    </h3>
                    <p className="text-xs text-purple-800 dark:text-purple-200">
                      Generate tokens for all students matching current filters
                      {appStatusCounts && (
                        <span className="font-semibold ml-1">
                          ({getTotalMatchingCount()} students)
                        </span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowBulkTokenModal(true)}
                    disabled={getTotalMatchingCount() === 0}
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 via-purple-600 to-pink-600 hover:from-purple-600 hover:via-purple-700 hover:to-pink-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap"
                  >
                    Generate Tokens for All Matching
                  </button>
                </div>
              </div>
            )}

            {/* Search Students */}
            <div className="mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                  placeholder="Search by name, email, ID, or token..."
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {studentSearchTerm && (
                  <button
                    onClick={() => setStudentSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Clear search"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {studentSearchTerm && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Showing {filteredUpdateTokens.length} of {updateTokens.length} results
                </div>
              )}
            </div>

            {/* Generate Update Tokens */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-6">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Generate Update Tokens
                  </h2>
                  <button
                    onClick={() => setShowGenerateForm(!showGenerateForm)}
                    className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                  >
                    {showGenerateForm ? 'Hide Form' : 'Generate Tokens'}
                  </button>
                </div>

                {showGenerateForm && (
                  <div className="space-y-4">
                    {/* Search Type Selector */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Search Type
                      </label>
                      <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="general"
                            checked={searchType === 'general'}
                            onChange={(e) => {
                              setSearchType(e.target.value);
                              setSearchResults([]);
                              setSelectedApplicants(new Set());
                            }}
                            className="mr-2"
                          />
                          General Search
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="university"
                            checked={searchType === 'university'}
                            onChange={(e) => {
                              setSearchType(e.target.value);
                              setSearchResults([]);
                              setSelectedApplicants(new Set());
                            }}
                            className="mr-2"
                          />
                          Search by University
                        </label>
                      </div>
                    </div>

                    {/* Applicant Search */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {searchType === 'university' ? 'Search by University Name' : 'Search Applicants'}
                      </label>
                      <input
                        type="text"
                        value={searchType === 'university' ? universitySearch : applicantSearch}
                        onChange={(e) => {
                          if (searchType === 'university') {
                            setUniversitySearch(e.target.value);
                            searchApplicants(e.target.value, true);
                          } else {
                            setApplicantSearch(e.target.value);
                            searchApplicants(e.target.value, false);
                          }
                        }}
                        placeholder={searchType === 'university' ?
                          "Enter university name (e.g., MIT, Harvard, Stanford)..." :
                          "Search by name, email, or university..."}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                      {searchLoading && <p className="text-sm text-gray-500 mt-1">Searching...</p>}

                      {/* Bulk Selection Buttons */}
                      {searchResults.length > 0 && searchType === 'university' && (
                        <div className="mt-2 mb-2 flex flex-col sm:flex-row sm:items-center gap-2">
                          <div className="flex gap-2">
                            <button
                              onClick={handleSelectAll}
                              className="flex-1 sm:flex-none px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors whitespace-nowrap"
                            >
                              <span className="hidden sm:inline">Select All ({searchResults.length})</span>
                              <span className="sm:hidden">Select All</span>
                            </button>
                            <button
                              onClick={handleDeselectAll}
                              className="flex-1 sm:flex-none px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                            >
                              Deselect All
                            </button>
                          </div>
                          <span className="text-sm text-gray-600 dark:text-gray-300 py-1 text-center sm:text-left">
                            {selectedApplicants.size} selected
                          </span>
                        </div>
                      )}

                      {/* Search Results */}
                      {searchResults.length > 0 && (
                        <div className="mt-2 max-h-60 overflow-y-auto border border-gray-300 rounded-md">
                          {searchType === 'university' ? (
                            // University search with checkboxes
                            searchResults.map((applicant) => (
                              <div
                                key={applicant.id}
                                className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 border-b last:border-b-0 flex items-center"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedApplicants.has(applicant.id)}
                                  onChange={() => toggleApplicantSelection(applicant.id)}
                                  className="mr-3"
                                />
                                <div className="flex-1">
                                  <div className="text-sm">
                                    <strong>ID {applicant.id}:</strong> {applicant.name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {applicant.personal_email} • {applicant.major}
                                  </div>
                                  <div className="text-xs text-blue-600 font-medium">
                                    {applicant.current_university_verified || applicant.current_university}
                                    {applicant.current_university_verified && (
                                      <span className="text-green-600 ml-1">(verified)</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            // General search with click to add
                            searchResults.map((applicant) => (
                              <div
                                key={applicant.id}
                                onClick={() => {
                                  const currentIds = generateTokenForm.applicantIds.split(',').filter(id => id.trim());
                                  if (!currentIds.includes(applicant.id.toString())) {
                                    setGenerateTokenForm({
                                      ...generateTokenForm,
                                      applicantIds: currentIds.length > 0 ?
                                        `${generateTokenForm.applicantIds}, ${applicant.id}` :
                                        applicant.id.toString()
                                    });
                                  }
                                  setSearchResults([]);
                                  setApplicantSearch('');
                                }}
                                className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer border-b last:border-b-0"
                              >
                                <div className="text-sm">
                                  <strong>ID {applicant.id}:</strong> {applicant.name} ({applicant.personal_email})
                                </div>
                                <div className="text-xs text-gray-500">
                                  {applicant.current_university_verified || applicant.current_university}
                                  {applicant.current_university_verified && (
                                    <span className="text-green-600 ml-1">(verified)</span>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}

                      {searchResults.length === 0 && !searchLoading && (searchType === 'university' ? universitySearch : applicantSearch) && (
                        <p className="text-sm text-gray-500 mt-2">
                          No applicants found {searchType === 'university' ? 'from this university' : 'matching your search'}.
                        </p>
                      )}
                    </div>

                    {/* Manual ID Entry */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Applicant IDs (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={generateTokenForm.applicantIds}
                        onChange={(e) => setGenerateTokenForm({...generateTokenForm, applicantIds: e.target.value})}
                        placeholder="e.g., 123, 456, 789"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>

                    {/* Expiration Hours */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Expires in (hours)
                      </label>
                      <input
                        type="number"
                        value={generateTokenForm.expiresInHours}
                        onChange={(e) => setGenerateTokenForm({...generateTokenForm, expiresInHours: parseInt(e.target.value)})}
                        min="1"
                        max="8760"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                      <p className="text-xs text-gray-500 mt-1">Default: 720 hours (30 days)</p>
                    </div>

                    <button
                      onClick={generateUpdateTokens}
                      className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      Generate Tokens
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Tokens List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Update Tokens
                  </h2>
                  <span className="text-sm text-gray-600 dark:text-gray-400 font-medium bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full w-fit">
                    {studentSearchTerm ? `${filteredUpdateTokens.length} of ${updateTokens.length}` : `${updateTokens.length} total`}
                  </span>
                </div>

                {/* Bulk Actions for No Token Issued Filter */}
                {tokenFilter === 'no_token' && filteredUpdateTokens.length > 0 && (
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <button
                          onClick={() => {
                            if (selectedNoTokenStudents.size === filteredUpdateTokens.length) {
                              setSelectedNoTokenStudents(new Set());
                            } else {
                              const allIds = new Set(filteredUpdateTokens.map(t => t.applicant_id));
                              setSelectedNoTokenStudents(allIds);
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                        >
                          {selectedNoTokenStudents.size === filteredUpdateTokens.length ? 'Deselect All' : 'Select All'}
                        </button>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {selectedNoTokenStudents.size} of {filteredUpdateTokens.length} selected
                        </span>
                      </div>
                      {selectedNoTokenStudents.size > 0 && (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            onClick={async () => {
                              if (window.confirm(`Generate update tokens for ${selectedNoTokenStudents.size} selected student(s)?`)) {
                                try {
                                  const token = getValidToken();
                                  const applicantIds = Array.from(selectedNoTokenStudents);

                                  const requestBody = {
                                    applicant_ids: applicantIds,
                                    expires_in_hours: 720
                                  };

                                  const response = await axios.post(`${BACKEND_URL}/api/generate-update-tokens`, requestBody, {
                                    headers: {
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    }
                                  });

                                  const tokenCount = response.data.success;
                                  const tokenText = tokenCount === 1 ? 'token' : 'tokens';
                                  setError({ type: 'success', message: `Successfully generated ${tokenCount} update ${tokenText}. You can now export the CSV.` });
                                  // Don't clear selection so user can export CSV after generating tokens
                                  fetchUpdateTokens();

                                  setTimeout(() => setError(null), 5000);
                                } catch (err) {
                                  console.error('Error generating tokens:', err);
                                  setError({ type: 'error', message: err.response?.data?.detail || 'Failed to generate update tokens' });
                                }
                              }
                            }}
                            className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap font-medium"
                          >
                            Generate Tokens for Selected ({selectedNoTokenStudents.size})
                          </button>
                          <button
                            onClick={exportSelectedStudentsToCSV}
                            className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap font-medium flex items-center gap-2"
                            title="Export selected students to CSV with matching organizations"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="hidden sm:inline">Export CSV ({selectedNoTokenStudents.size})</span>
                            <span className="sm:hidden">Export</span>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedNoTokenStudents(new Set());
                              setError({ type: 'success', message: 'Selection cleared' });
                              setTimeout(() => setError(null), 2000);
                            }}
                            className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap font-medium"
                            title="Clear the current selection"
                          >
                            Clear Selection
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {tokensLoading ? (
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    ))}
                  </div>
                ) : updateTokens.length === 0 ? (
                  <p className="text-gray-600 dark:text-gray-300">
                    No tokens found for the selected filter.
                  </p>
                ) : filteredUpdateTokens.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="mt-4 text-gray-600 dark:text-gray-300">
                      No results found matching "<strong>{studentSearchTerm}</strong>"
                    </p>
                    <button
                      onClick={() => setStudentSearchTerm('')}
                      className="mt-3 text-purple-600 dark:text-purple-400 hover:underline"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredUpdateTokens.map((token) => (
                      <div
                        key={token.id || token.applicant_id}
                        className="border dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                          {/* Checkbox for No Token Issued filter */}
                          {tokenFilter === 'no_token' && (
                            <div className="flex items-start pt-1">
                              <input
                                type="checkbox"
                                checked={selectedNoTokenStudents.has(token.applicant_id)}
                                onChange={(e) => {
                                  const newSelected = new Set(selectedNoTokenStudents);
                                  if (e.target.checked) {
                                    newSelected.add(token.applicant_id);
                                  } else {
                                    newSelected.delete(token.applicant_id);
                                  }
                                  setSelectedNoTokenStudents(newSelected);
                                }}
                                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                              />
                            </div>
                          )}
                          {/* Checkbox for tokens with IDs (active, expired, used) - only show if not applied */}
                          {tokenFilter !== 'no_token' && token.id && !token.has_applied_this_cycle && (
                            <div className="flex items-start pt-1">
                              <input
                                type="checkbox"
                                checked={selectedTokens.has(token.id)}
                                onChange={() => toggleTokenSelection(token.id)}
                                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                              />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                              <h3 className="font-medium text-gray-900 dark:text-white break-words">
                                {token.applicant_name} (ID: {token.applicant_id})
                              </h3>
                              {token.token ? (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit ${
                                  token.used ? 'bg-gray-100 text-gray-800' :
                                  token.is_expired ? 'bg-red-100 text-red-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {token.used ? 'Used' : token.is_expired ? 'Expired' : 'Active'}
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded-full text-xs font-medium w-fit bg-orange-100 text-orange-800">
                                  No Token
                                </span>
                              )}
                              {/* Student Group Badge - only show for no-token students */}
                              {tokenFilter === 'no_token' && token.student_group_label && (
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium w-fit cursor-help ${
                                    token.student_group === 'self_registered' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200' :
                                    token.student_group === 'admin_upload' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200' :
                                    token.student_group === 'partner_upload_approved' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200' :
                                    token.student_group === 'partner_verified' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200' :
                                    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                                  }`}
                                  title={token.student_group === 'admin_upload' && token.verified_by && token.verified_at
                                    ? `Uploaded by: ${getAdminName(token.verified_by)}\nDate: ${new Date(token.verified_at).toLocaleDateString()}\n${token.admin_upload_notes ? `Batch: ${formatBatchInfo(token.admin_upload_notes)}` : ''}`
                                    : ''
                                  }
                                >
                                  {token.student_group_label}
                                </span>
                              )}
                              {/* Application Status Badge - only show for no-token students */}
                              {tokenFilter === 'no_token' && token.application_status_label && (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit ${
                                  token.application_status === 'applied'
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200'
                                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200'
                                }`}>
                                  {token.application_status_label}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
                              {tokenFilter === 'no_token' ? (
                                <>
                                  <p className="break-all">
                                    <strong>Personal Email:</strong> {token.personal_email || 'N/A'}
                                  </p>
                                  <p className="break-all">
                                    <strong>Verified University Email:</strong>{' '}
                                    <span className={token.university_email_verified && token.university_email_verified !== '0' ? '' : 'text-red-600 dark:text-red-400 italic'}>
                                      {token.university_email_verified && token.university_email_verified !== '0'
                                        ? token.university_email_verified
                                        : 'None (Outside Applicant)'}
                                    </span>
                                  </p>
                                  <p>
                                    <strong>GPA:</strong>{' '}
                                    {token.gpa_verified && token.gpa_verified !== '0'
                                      ? token.gpa_verified
                                      : token.current_gpa && token.current_gpa !== '0'
                                        ? token.current_gpa
                                        : 'N/A'}
                                  </p>
                                  <p>
                                    <strong>Major:</strong>{' '}
                                    {token.major_verified && token.major_verified !== '0'
                                      ? token.major_verified
                                      : token.major && token.major !== '0'
                                        ? token.major
                                        : 'N/A'}
                                  </p>
                                  <p>
                                    <strong>Class Year:</strong>{' '}
                                    {token.current_academic_class_verified && token.current_academic_class_verified !== '0'
                                      ? token.current_academic_class_verified
                                      : token.current_academic_class && token.current_academic_class !== '0'
                                        ? token.current_academic_class
                                        : 'N/A'}
                                  </p>
                                  <p>
                                    <strong>University:</strong>{' '}
                                    {token.university || 'N/A'}
                                  </p>
                                  {/* Admin Upload Details - only show for admin uploads */}
                                  {token.student_group === 'admin_upload' && (
                                    <>
                                      <p>
                                        <strong>Uploaded By:</strong> {getAdminName(token.verified_by)}
                                      </p>
                                      <p>
                                        <strong>Upload Date:</strong> {token.verified_at ? new Date(token.verified_at).toLocaleDateString() : 'N/A'}
                                      </p>
                                      {token.admin_upload_notes && (
                                        <p className="sm:col-span-2 text-xs text-gray-500 dark:text-gray-400">
                                          <strong>Batch:</strong> {formatBatchInfo(token.admin_upload_notes)}
                                        </p>
                                      )}
                                    </>
                                  )}

                                  {/* Matching Organizations Section */}
                                  {matchingOrganizations[token.applicant_id] && (
                                    <div className="sm:col-span-2 mt-2">
                                      <button
                                        onClick={() => {
                                          const newExpanded = new Set(expandedMatchingOrgs);
                                          if (newExpanded.has(token.applicant_id)) {
                                            newExpanded.delete(token.applicant_id);
                                          } else {
                                            newExpanded.add(token.applicant_id);
                                          }
                                          setExpandedMatchingOrgs(newExpanded);
                                        }}
                                        className="flex items-center gap-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                                      >
                                        <span>
                                          Matching Organizations ({matchingOrganizations[token.applicant_id].length})
                                        </span>
                                        <svg
                                          className={`w-4 h-4 transform transition-transform ${
                                            expandedMatchingOrgs.has(token.applicant_id) ? 'rotate-180' : ''
                                          }`}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </button>

                                      {expandedMatchingOrgs.has(token.applicant_id) && (
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                          {matchingOrganizations[token.applicant_id].length > 0 ? (
                                            matchingOrganizations[token.applicant_id].map((org, orgIndex) => (
                                              <div
                                                key={orgIndex}
                                                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600"
                                              >
                                                <div className="flex items-center gap-2 mb-2">
                                                  <h5 className="font-semibold text-gray-900 dark:text-white text-sm">
                                                    {org.organization}
                                                  </h5>
                                                  <span
                                                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                      org.approved
                                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                    }`}
                                                  >
                                                    {org.approved ? 'APPROVED' : 'PENDING'}
                                                  </span>
                                                </div>
                                                <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                                                  <p>
                                                    <strong>Recruiter:</strong> {org.recruiter_name}
                                                  </p>
                                                  <p>
                                                    <strong>Email:</strong> {org.recruiter_email}
                                                  </p>
                                                  <p>
                                                    <strong>Min GPA:</strong> {org.minimum_gpa}
                                                  </p>
                                                  <p>
                                                    <strong>Majors:</strong>{' '}
                                                    {org.eligible_majors && org.eligible_majors.length > 0
                                                      ? org.eligible_majors.join(', ')
                                                      : 'Unspecified'}
                                                  </p>
                                                  <p>
                                                    <strong>Institutions:</strong>{' '}
                                                    {org.preferred_institutions && org.preferred_institutions.length > 0
                                                      ? org.preferred_institutions.join(', ')
                                                      : 'Unspecified'}
                                                  </p>
                                                  <p>
                                                    <strong>Class Years:</strong>{' '}
                                                    {org.preferred_class_years && org.preferred_class_years.length > 0
                                                      ? org.preferred_class_years.join(', ')
                                                      : 'Unspecified'}
                                                  </p>
                                                </div>
                                              </div>
                                            ))
                                          ) : (
                                            <p className="text-gray-500 dark:text-gray-400 text-sm italic col-span-2">
                                              No matching organizations found
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {token.used_magic_link && token.application_status === 'applied' ? (
                                    <p className="sm:col-span-2 text-green-600 dark:text-green-400 font-medium">
                                      ✓ Applied via Magic Link
                                    </p>
                                  ) : (
                                    <div className="sm:col-span-2 space-y-2">
                                      <p className="text-red-600 dark:text-red-400 italic text-sm">
                                        This student has never been issued an update token.
                                      </p>
                                      {!token.has_applied_this_cycle && (
                                        <button
                                          onClick={() => generateSingleToken(token.applicant_id, token.applicant_name)}
                                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors font-medium"
                                        >
                                          Generate Token (30 Days)
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  <p className="break-all"><strong>Email:</strong> {token.university_email_verified || token.personal_email || 'N/A'}</p>
                                  <p><strong>Created:</strong> {formatDate(token.created_at)}</p>
                                  <p><strong>Expires:</strong> {formatDate(token.expires_at)}</p>
                                  {token.used_at && <p><strong>Used:</strong> {formatDate(token.used_at)}</p>}
                                  <div className="sm:col-span-2 space-y-2">
                                    <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs break-all">
                                      <strong>Token:</strong> <span className="font-mono">{token.token}</span>
                                    </div>
                                    <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs break-all">
                                      <strong>Update Link:</strong> <span className="font-mono">{token.update_link}</span>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          {token.token && (
                            <div className="flex flex-row sm:flex-col gap-2 sm:space-y-1 sm:ml-4 justify-center sm:justify-start">
                              <button
                                onClick={() => copyToClipboard(token.update_link)}
                                className="flex-1 sm:flex-none px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors whitespace-nowrap"
                              >
                                <span className="hidden sm:inline">Copy Link</span>
                                <span className="sm:hidden">Link</span>
                              </button>
                              <button
                                onClick={() => copyToClipboard(token.token)}
                                className="flex-1 sm:flex-none px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 transition-colors whitespace-nowrap"
                              >
                                <span className="hidden sm:inline">Copy Token</span>
                                <span className="sm:hidden">Token</span>
                              </button>
                              {!token.used && !token.is_expired && (
                                <button
                                  onClick={() => revokeTokens([token.id])}
                                  className="flex-1 sm:flex-none px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                                >
                                  Revoke
                                </button>
                              )}
                              {!token.has_applied_this_cycle && (
                                <>
                                  <button
                                    onClick={() => extendToken(token.id, 30)}
                                    className="flex-1 sm:flex-none px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors whitespace-nowrap"
                                  >
                                    <span className="hidden sm:inline">Extend 30 Days</span>
                                    <span className="sm:hidden">+30d</span>
                                  </button>
                                  <button
                                    onClick={() => extendToken(token.id, 60)}
                                    className="flex-1 sm:flex-none px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors whitespace-nowrap"
                                  >
                                    <span className="hidden sm:inline">Extend 60 Days</span>
                                    <span className="sm:hidden">+60d</span>
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Partner Verification Tokens Tab */}
        {activeTab === 'partner-tokens' && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  University Partner Verification Token Management
                </h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Generate verification tokens for university partners to manage their students
                </p>
              </div>

              {/* Token Generation Form */}
              <div className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Generate New Verification Token
                </h3>

                {/* Partner Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select University Partner
                  </label>
                  <select
                    value={selectedPartner || ''}
                    onChange={(e) => {
                      const partnerId = parseInt(e.target.value);
                      setSelectedPartner(partnerId);
                      if (partnerId) {
                        fetchEligibleStudents(partnerId);
                      } else {
                        setEligibleStudents([]);
                        setSelectedStudents(new Set());
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a partner...</option>
                    {universityPartners.map(partner => (
                      <option key={partner.id} value={partner.id}>
                        {partner.university} - {partner.full_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Student Selection */}
                {selectedPartner && (
                  <div className="mb-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Select Students ({selectedStudents.size} selected)
                      </label>
                      {eligibleStudents.length > 0 && !fetchingStudents && (
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedStudents.size === eligibleStudents.length) {
                              // Deselect all
                              setSelectedStudents(new Set());
                            } else {
                              // Select all
                              const allStudentIds = new Set(eligibleStudents.map(s => s.id));
                              setSelectedStudents(allStudentIds);
                            }
                          }}
                          className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors w-full sm:w-auto text-center whitespace-nowrap"
                        >
                          {selectedStudents.size === eligibleStudents.length ? 'Deselect All' : 'Select All'}
                        </button>
                      )}
                    </div>
                    <div className="max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800">
                      {fetchingStudents ? (
                        <div className="p-4 text-gray-500 dark:text-gray-400 text-center">
                          Loading eligible students...
                        </div>
                      ) : eligibleStudents.length === 0 ? (
                        <div className="p-4 text-gray-500 dark:text-gray-400 text-center">
                          No eligible students found for this partner
                        </div>
                      ) : (
                        eligibleStudents.map(student => (
                          <label key={student.id} className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedStudents.has(student.id)}
                              onChange={(e) => {
                                const newSelected = new Set(selectedStudents);
                                if (e.target.checked) {
                                  newSelected.add(student.id);
                                } else {
                                  newSelected.delete(student.id);
                                }
                                setSelectedStudents(newSelected);
                              }}
                              className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 dark:text-white break-words">
                                {student.name}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 break-words">
                                <div className="block sm:hidden space-y-1">
                                  <div>{student.email}</div>
                                  <div>{student.major}</div>
                                  <div>{student.current_academic_class}</div>
                                </div>
                                <div className="hidden sm:block">
                                  {student.email} • {student.major} • {student.current_academic_class}
                                </div>
                              </div>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Token Expiry */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Token Expiry (hours)
                  </label>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <input
                      type="number"
                      value={tokenExpiryHours}
                      onChange={(e) => setTokenExpiryHours(parseInt(e.target.value))}
                      min="1"
                      max="8760"
                      className="w-full sm:w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      (Default: 720 hours = 30 days)
                    </span>
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={generateVerificationToken}
                  disabled={!selectedPartner || selectedStudents.size === 0 || generatingToken}
                  className={`w-full sm:w-auto px-6 py-2 rounded-md font-medium transition-colors ${
                    !selectedPartner || selectedStudents.size === 0 || generatingToken
                      ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {generatingToken ? 'Generating...' : 'Generate Verification Token'}
                </button>
              </div>

              {/* Existing Verification Tokens */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                    Existing Verification Tokens
                  </h3>
                  <span className="text-sm text-gray-600 dark:text-gray-400 font-medium bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full w-fit">
                    {verificationTokens.length} total
                  </span>
                </div>

                {verificationTokens.length === 0 ? (
                  <p className="text-gray-600 dark:text-gray-300">
                    No verification tokens found.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {verificationTokens.map((tokenData) => (
                      <div
                        key={tokenData.id}
                        className="border dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                              <h3 className="font-medium text-gray-900 dark:text-white break-words">
                                {tokenData.university_partner.university} - {tokenData.university_partner.full_name}
                              </h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit ${
                                tokenData.used ? 'bg-gray-100 text-gray-800' :
                                new Date(tokenData.expires_at) < new Date() ? 'bg-red-100 text-red-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {tokenData.used ? 'Used' : new Date(tokenData.expires_at) < new Date() ? 'Expired' : 'Active'}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
                              <p className="break-all"><strong>Partner Email:</strong> {tokenData.university_partner.email}</p>
                              <p><strong>Students:</strong> {tokenData.student_count}</p>
                              <p><strong>Created:</strong> {new Date(tokenData.created_at).toLocaleDateString()}</p>
                              <p><strong>Expires:</strong> {new Date(tokenData.expires_at).toLocaleDateString()}</p>
                              <div className="sm:col-span-2 space-y-2">
                                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs break-all">
                                  <strong>Token:</strong> <span className="font-mono">{tokenData.token}</span>
                                </div>
                                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs break-all">
                                  <strong>Verification URL:</strong> <span className="font-mono">{tokenData.verification_url}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-row sm:flex-col gap-2 sm:space-y-1 sm:ml-4 justify-center sm:justify-start">
                            <button
                              onClick={() => navigator.clipboard.writeText(tokenData.verification_url)}
                              className="flex-1 sm:flex-none px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors whitespace-nowrap"
                            >
                              <span className="hidden sm:inline">Copy URL</span>
                              <span className="sm:hidden">URL</span>
                            </button>
                            <button
                              onClick={() => navigator.clipboard.writeText(tokenData.token)}
                              className="flex-1 sm:flex-none px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 transition-colors whitespace-nowrap"
                            >
                              <span className="hidden sm:inline">Copy Token</span>
                              <span className="sm:hidden">Token</span>
                            </button>
                            {!tokenData.used && new Date(tokenData.expires_at) > new Date() && (
                              <button
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to revoke this verification token? This action cannot be undone.')) {
                                    revokeVerificationToken(tokenData.id);
                                  }
                                }}
                                disabled={revokingTokens.has(tokenData.id)}
                                className={`flex-1 sm:flex-none px-3 py-1 text-xs rounded transition-colors ${
                                  revokingTokens.has(tokenData.id)
                                    ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                    : 'bg-red-600 text-white hover:bg-red-700'
                                }`}
                              >
                                {revokingTokens.has(tokenData.id) ? 'Revoking...' : 'Revoke'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Verification Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <>
            {dashboardLoading ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                  </div>
                </div>
              </div>
            ) : dashboardData ? (
              <div className="space-y-6">
                {/* Dashboard Header */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        University Partner Verification Dashboard
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Primary partners</span> are listed first and highlighted in blue
                      </p>
                    </div>
                    <button
                      onClick={exportAllUniversitiesCSV}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
                      title="Export all universities and students as CSV"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export All Universities
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                    <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {dashboardData.total_universities}
                      </div>
                      <div className="text-sm text-blue-600 dark:text-blue-400">Universities</div>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {dashboardData.total_students}
                      </div>
                      <div className="text-sm text-green-600 dark:text-green-400">Total Students</div>
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                        {dashboardData.total_verified}
                      </div>
                      <div className="text-sm text-yellow-600 dark:text-yellow-400">Verified</div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {dashboardData.total_unverified}
                      </div>
                      <div className="text-sm text-purple-600 dark:text-purple-400">Unverified</div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {dashboardData.total_students > 0
                          ? Math.round((dashboardData.total_verified / dashboardData.total_students) * 100)
                          : 0}%
                      </div>
                      <div className="text-sm text-purple-600 dark:text-purple-400">Verified</div>
                    </div>
                  </div>
                </div>

                {/* University Cards */}
                <div className="space-y-4">
                  {dashboardData.universities.map((university) => (
                    <div
                      key={university.university}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
                    >
                      {/* University Header */}
                      <div
                        className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => toggleUniversity(university.university)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                              {university.university}
                            </h3>

                            {/* Partners Section - Under University Name */}
                            <div className="mb-4">
                              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Partner{university.partners && university.partners.length > 1 ? 's' : ''}:
                              </div>
                              {university.partners && university.partners.length > 0 ? (
                                <div className="space-y-2">
                                  {university.partners.map((partner, idx) => (
                                    <div key={idx} className="mb-2">
                                      <div className="flex items-center gap-2">
                                        <span className={partner.is_primary ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}>
                                          {partner.name}
                                        </span>
                                        {partner.level && (
                                          <span className={`text-xs px-2 py-1 rounded-full ${
                                            partner.is_primary
                                              ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                          }`}>
                                            {partner.level}
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400 ml-0 mt-1">
                                        {partner.email}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-600 dark:text-gray-400">No partner assigned</span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-700 dark:text-gray-300">Total Students:</span>
                                <span className="ml-2 text-blue-600 dark:text-blue-400 font-semibold">
                                  {university.total_students}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700 dark:text-gray-300">Verified:</span>
                                <span className="ml-2 text-green-600 dark:text-green-400 font-semibold">
                                  {university.verified_students}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700 dark:text-gray-300">Unverified:</span>
                                <span className="ml-2 text-purple-600 dark:text-purple-400 font-semibold">
                                  {university.unverified_students}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-2">
                            {/* Export CSV Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                exportUniversityCSV(university);
                              }}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors flex items-center gap-1"
                              title="Export student data as CSV"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Export CSV
                            </button>

                            {/* Expand/Collapse Arrow */}
                            <svg
                              className={`w-6 h-6 text-gray-400 transform transition-transform ${
                                expandedUniversities.has(university.university) ? 'rotate-180' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Student Table */}
                      {expandedUniversities.has(university.university) && (
                        <div className="border-t border-gray-200 dark:border-gray-700">
                          <div className="p-6">
                            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                              Students from {university.university}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                              Sorted alphabetically: Priority students first, then verified students, then unverified students
                            </p>

                            {university.students.length === 0 ? (
                              <p className="text-gray-600 dark:text-gray-300">
                                No students found for this university.
                              </p>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                  <thead className="bg-gray-50 dark:bg-gray-800">
                                    <tr>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Full Name
                                      </th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Email
                                      </th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Student Applied
                                      </th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Application Date
                                      </th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Verification Status
                                      </th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Eligibility Status
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                    {university.students.map((student, index) => {
                                      const isPriority = student.is_priority;
                                      const isVerified = student.verification_status === 'verified';
                                      const nextStudent = university.students[index + 1];
                                      const prevStudent = university.students[index - 1];

                                      // Check if we need section headers
                                      const isFirstPriority = isPriority && (!prevStudent || !prevStudent.is_priority);
                                      const isFirstVerified = isVerified && !isPriority && (!prevStudent || (prevStudent.is_priority || !prevStudent.is_verified));
                                      const isFirstUnverified = !isPriority && !isVerified && (!prevStudent || prevStudent.is_verified || prevStudent.is_priority);

                                      return (
                                        <React.Fragment key={index}>
                                          {/* Priority Section Header */}
                                          {isFirstPriority && (
                                            <tr>
                                              <td colSpan="6" className="px-6 py-3 bg-purple-50 dark:bg-purple-900 border-l-4 border-purple-500">
                                                <div className="flex items-center gap-2">
                                                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                  </svg>
                                                  <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">
                                                    Priority - Partner Approval Needed
                                                  </span>
                                                </div>
                                              </td>
                                            </tr>
                                          )}

                                          {/* Verified Section Header */}
                                          {isFirstVerified && (
                                            <tr>
                                              <td colSpan="6" className="px-6 py-2 bg-green-50 dark:bg-green-900">
                                                <div className="text-xs text-green-700 dark:text-green-300 text-center font-medium">
                                                  — Verified Students —
                                                </div>
                                              </td>
                                            </tr>
                                          )}

                                          {/* Unverified Section Header */}
                                          {isFirstUnverified && (
                                            <tr>
                                              <td colSpan="6" className="px-6 py-2 bg-gray-50 dark:bg-gray-800">
                                                <div className="text-xs text-gray-500 dark:text-gray-400 text-center font-medium">
                                                  — Unverified Students —
                                                </div>
                                              </td>
                                            </tr>
                                          )}

                                          <tr className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                                            isPriority ? 'bg-purple-25 dark:bg-purple-950' :
                                            isVerified ? 'bg-green-25 dark:bg-green-950' : ''
                                          }`}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                          <div className="flex items-center gap-2">
                                            {isPriority && (
                                              <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                              </svg>
                                            )}
                                            {student.name}
                                          </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                          {student.email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 text-center">
                                          <span className={`font-semibold ${
                                            student.student_applied === 'Y'
                                              ? 'text-green-600 dark:text-green-400'
                                              : 'text-gray-500 dark:text-gray-400'
                                          }`}>
                                            {student.student_applied}
                                          </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                          {student.application_date || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            student.verification_status === 'verified'
                                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                              : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                          }`}>
                                            {student.verification_status || 'Unverified'}
                                          </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            student.eligibility_status === true || student.eligibility_status === 'true'
                                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                                          }`}>
                                            {student.eligibility_status === true || student.eligibility_status === 'true' ? 'Eligible' : 'Not Eligible'}
                                          </span>
                                        </td>
                                          </tr>
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <p className="text-gray-600 dark:text-gray-300">
                  Click "Verification Dashboard" to load university verification data.
                </p>
              </div>
            )}
          </>
        )}

        {/* Partner Account Management Tab */}
        {activeTab === 'partner-accounts' && (
          <>
            {/* Sticky Bulk Operations Toolbar */}
            {selectedPartners.size > 0 && (
              <div className="sticky top-0 z-40 bg-white dark:bg-gray-900 border-b-2 border-blue-500 shadow-lg mb-6 transition-all duration-300">
                <div className="px-4 py-3">
                  <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {selectedPartners.size} partner{selectedPartners.size !== 1 ? 's' : ''} selected
                        </span>
                      </div>
                      <button
                        onClick={deselectAllPartners}
                        disabled={bulkOperationInProgress}
                        className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
                      >
                        Clear selection
                      </button>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={bulkGenerateActivationTokens}
                        disabled={bulkOperationInProgress}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 disabled:bg-gray-300 transition-colors flex items-center gap-1 shadow-sm"
                      >
                        {bulkOperationInProgress ? 'Processing...' : 'Generate Tokens'}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1721 9z" />
                        </svg>
                      </button>
                      <button
                        onClick={bulkRevokeActivationTokens}
                        disabled={bulkOperationInProgress}
                        className="px-3 py-1.5 bg-orange-600 text-white text-sm font-medium rounded hover:bg-orange-700 disabled:bg-gray-300 transition-colors flex items-center gap-1 shadow-sm"
                      >
                        {bulkOperationInProgress ? 'Processing...' : 'Revoke Tokens'}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => bulkTogglePartnerStatus(e, true)}
                        disabled={bulkOperationInProgress}
                        className="px-3 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded hover:bg-emerald-700 disabled:bg-gray-300 transition-colors flex items-center gap-1 shadow-sm"
                      >
                        {bulkOperationInProgress ? 'Processing...' : 'Activate All'}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => bulkTogglePartnerStatus(e, false)}
                        disabled={bulkOperationInProgress}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:bg-gray-300 transition-colors flex items-center gap-1 shadow-sm"
                      >
                        {bulkOperationInProgress ? 'Processing...' : 'Deactivate All'}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  University Partner Account Management
                </h2>
                <button
                  onClick={() => navigate('/admin/activation-tokens')}
                  className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <span className="hidden sm:inline">View Activation Tokens</span>
                  <span className="sm:hidden">View Tokens</span>
                </button>
              </div>

              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Manage university partner accounts, generate activation tokens, and control account status.
              </p>


              {/* Bulk Actions */}
              {universityPartners.length > 0 && (
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={selectAllPartners}
                        disabled={bulkOperationInProgress}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
                      >
                        Select All ({universityPartners.length})
                      </button>
                      <button
                        onClick={deselectAllPartners}
                        disabled={bulkOperationInProgress}
                        className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 disabled:bg-gray-300 transition-colors"
                      >
                        Deselect All
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {selectedPartners.size} selected
                      </span>
                    </div>

                    {selectedPartners.size > 0 ? (
                      <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Bulk operations available at top of page</span>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={bulkGenerateActivationTokens}
                          disabled={bulkOperationInProgress}
                          className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:bg-gray-300 transition-colors flex items-center gap-1"
                        >
                          {bulkOperationInProgress ? 'Processing...' : 'Generate Tokens'}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                        </button>
                        <button
                          onClick={bulkRevokeActivationTokens}
                          disabled={bulkOperationInProgress}
                          className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:bg-gray-300 transition-colors flex items-center gap-1"
                        >
                          {bulkOperationInProgress ? 'Processing...' : 'Revoke Tokens'}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => bulkTogglePartnerStatus(e, true)}
                          disabled={bulkOperationInProgress}
                          className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:bg-gray-300 transition-colors flex items-center gap-1"
                        >
                          {bulkOperationInProgress ? 'Processing...' : 'Activate All'}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => bulkTogglePartnerStatus(e, false)}
                          disabled={bulkOperationInProgress}
                          className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:bg-gray-300 transition-colors flex items-center gap-1"
                        >
                          {bulkOperationInProgress ? 'Processing...' : 'Deactivate All'}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {partnersLoading ? (
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {universityPartners.map((partner) => (
                    <div
                      key={partner.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                        <div className="flex items-start gap-3 flex-1 w-full">
                          <input
                            type="checkbox"
                            checked={selectedPartners.has(partner.id)}
                            onChange={() => togglePartnerSelection(partner.id)}
                            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {partner.full_name}
                              </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit ${
                              partner.is_active && partner.is_approved
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : partner.is_active
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {partner.is_active && partner.is_approved
                                ? 'Active'
                                : partner.is_active
                                ? 'Pending Approval'
                                : 'Inactive'}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                            <div>
                              <span className="font-medium">University:</span> {partner.university}
                            </div>
                            <div>
                              <span className="font-medium">Email:</span> {partner.email}
                            </div>
                            <div>
                              <span className="font-medium">Username:</span> {partner.username}
                            </div>
                            <div>
                              <span className="font-medium">Title:</span> {partner.title || 'Not specified'}
                            </div>
                          </div>

                          {/* Show activation token data if generated */}
                          {activationTokens[partner.id] && (
                            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded">
                              <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                                ✅ Activation Token Generated
                              </p>
                              <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                                <p><strong>Activation URL:</strong></p>
                                <code className="block bg-blue-100 dark:bg-blue-800 p-2 rounded text-xs break-all">
                                  {activationTokens[partner.id].activation_url}
                                </code>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                  Expires: {new Date(activationTokens[partner.id].expires_at).toLocaleDateString()}
                                </p>
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                  Send this URL to the partner to activate their account.
                                </p>
                              </div>
                            </div>
                          )}
                          </div>
                        </div>

                        <div className="flex flex-row sm:flex-col gap-2 flex-wrap sm:flex-nowrap w-full lg:w-auto">
                          {/* Generate Activation Token Button */}
                          <button
                            onClick={() => generateActivationToken(partner.id)}
                            disabled={generatingActivation.has(partner.id)}
                            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                              generatingActivation.has(partner.id)
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
                            }`}
                          >
                            {generatingActivation.has(partner.id) ? (
                              'Generating...'
                            ) : (
                              <span>
                                <span className="hidden sm:inline">Generate Activation Token</span>
                                <span className="sm:hidden">Generate Token</span>
                              </span>
                            )}
                          </button>

                          {/* Toggle Status Button */}
                          <button
                            onClick={() => togglePartnerStatus(partner.id, partner.is_active)}
                            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                              partner.is_active
                                ? 'bg-red-600 text-white hover:bg-red-700'
                                : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                          >
                            {partner.is_active ? 'Deactivate' : 'Activate'}
                          </button>

                          {/* Approval Buttons - only show if partner is active */}
                          {partner.is_active && (
                            <>
                              {!partner.is_approved ? (
                                <button
                                  onClick={() => approvePartner(partner.id, true)}
                                  className="px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors bg-emerald-600 text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 whitespace-nowrap"
                                >
                                  <span className="hidden sm:inline">Approve Partner</span>
                                  <span className="sm:hidden">Approve</span>
                                </button>
                              ) : (
                                <button
                                  onClick={() => approvePartner(partner.id, false)}
                                  className="px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors bg-orange-600 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 whitespace-nowrap"
                                >
                                  <span className="hidden sm:inline">Reject Partner</span>
                                  <span className="sm:hidden">Reject</span>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {universityPartners.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500 dark:text-gray-400">No university partners found.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Partner Activity Tab */}
        {activeTab === 'partner-activity' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  University Partner Activity
                </h2>
                <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                  Track university partner login activity, including last login dates and total login counts.
                </p>
              </div>
              <button
                onClick={() => switchToTab('recruiters')}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Back to Dashboard
              </button>
            </div>

            {partnerActivityLoading ? (
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  ))}
                </div>
              </div>
            ) : partnerActivityData && partnerActivityData.length > 0 ? (
              <ResponsiveTable 
                data={partnerActivityData}
                renderMobileCard={(partner) => (
                  <MobilePartnerActivityCard
                    key={partner.id}
                    partner={partner}
                  />
                )}
              >
                <div className="max-h-[600px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          University
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Partner Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Last Login
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Total Logins
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {partnerActivityData.map((partner) => (
                        <tr key={partner.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {partner.university}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {partner.partner_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            {partner.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            {partner.last_login ? (
                              <div>
                                <div>{new Date(partner.last_login).toLocaleDateString()}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(partner.last_login).toLocaleTimeString()}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500 italic">Never</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {partner.total_logins || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              partner.is_active
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {partner.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ResponsiveTable>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 dark:text-gray-500 mb-2">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                  No university partner activity data available.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Student Activity Navigation Bar */}
        {activeTab === 'student-activity' && (
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 mb-6 rounded-t-lg shadow-lg">
            <div className="flex items-center justify-between p-2">
              <button
                onClick={() => setSectionNavCollapsed(!sectionNavCollapsed)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title={sectionNavCollapsed ? "Expand navigation" : "Collapse navigation"}
              >
                <svg
                  className={`w-5 h-5 text-gray-600 dark:text-gray-400 transform transition-transform ${
                    sectionNavCollapsed ? '' : 'rotate-90'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {sectionNavCollapsed && (
                <span className="text-sm text-gray-600 dark:text-gray-400">Section Navigation (Click to expand)</span>
              )}
            </div>
            {!sectionNavCollapsed && (
              <div className="flex flex-wrap gap-2 justify-center p-4 pt-0">
                {[
                  { id: 'student-application', name: 'Application Activity', icon: '📊' },
                  { id: 'university-missing', name: 'University Missing', icon: '🏫' },
                  { id: 'unassigned-students', name: 'Unassigned Students', icon: '👥' },
                  { id: 'internship-completion', name: 'CSIP Tracking', icon: '✅' },
                  { id: 'login-activity', name: 'Login Activity', icon: '🔐' }
                ].map(section => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-100 hover:bg-blue-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-blue-800 dark:text-blue-200 text-sm font-medium rounded-lg transition-colors"
                  >
                    <span>{section.icon}</span> {section.name}
                  </button>
                ))}

                {/* Duplicate Students Link */}
                {canAccessSectionSync(ADMIN_SECTIONS.STUDENT_RECORDS, permissions) && (
                  <button
                    onClick={() => navigate('/admin/duplicate-students')}
                    className="flex items-center gap-2 px-3 py-2 bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 text-sm font-medium rounded-lg transition-colors border border-yellow-300 dark:border-yellow-700"
                  >
                    <span>🔀</span> Merge Duplicates
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Student Activity Tab */}
        {activeTab === 'student-activity' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6" data-card="student-application">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setStudentActivityCollapsed(!studentActivityCollapsed)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  >
                    <svg
                      className={`w-5 h-5 text-gray-600 dark:text-gray-400 transform transition-transform ${
                        studentActivityCollapsed ? '' : 'rotate-90'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Student Application Activity
                    </h2>
                    {studentActivityCollapsed && studentActivityData && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {studentActivityData.length} students tracked
                      </p>
                    )}
                  </div>
                </div>
                {!studentActivityCollapsed && (
                  <p className="text-gray-600 dark:text-gray-300 mt-2 ml-8">
                    Track student application submissions, including application dates and submission counts.
                  </p>
                )}
              </div>
              {!studentActivityCollapsed && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <button
                    onClick={() => switchToTab('recruiters')}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Back to Dashboard
                  </button>
                  <button
                    onClick={() => {
                      // Scroll to internship completion card
                      const internshipCard = document.querySelector('[data-card="internship-completion"]');
                      if (internshipCard) {
                        internshipCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Update Internship Completion Status
                  </button>
                  <button
                    onClick={exportStudentActivityToCSV}
                    disabled={!studentActivityData || studentActivityData.length === 0}
                    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      studentActivityData && studentActivityData.length > 0
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export CSV
                  </button>
                </div>
              )}
            </div>

            {!studentActivityCollapsed && (
              <>
                {/* Search Bar */}
                <div className="mb-6">
                  <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={studentSearchQuery}
                  onChange={(e) => setStudentSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search by student name or ID..."
                />
              </div>
            </div>

            {studentActivityLoading ? (
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  ))}
                </div>
              </div>
            ) : studentActivityData && studentActivityData.length > 0 ? (
              <div className="overflow-x-auto">
                {(() => {
                  // Filter students based on search query
                  const filteredStudents = studentActivityData.filter(student => {
                    if (!studentSearchQuery) return true;
                    const searchLower = studentSearchQuery.toLowerCase();
                    return (
                      student.student_name.toLowerCase().includes(searchLower) ||
                      (student.applicant_id && student.applicant_id.toString().includes(searchLower))
                    );
                  });

                  return filteredStudents.length > 0 ? (
                    <>
                      <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                        Showing {filteredStudents.length} of {studentActivityData.length} students
                      </div>
                      {console.log('Student Activity rendering ResponsiveTable with', filteredStudents?.length, 'filtered students')}
                      <ResponsiveTable
                        data={filteredStudents}
                        keyPrefix="student-activity"
                        renderMobileCard={(student) => (
                          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                                  {student.student_name}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  ID: {student.applicant_id || '-'}
                                </p>
                              </div>
                              <div className="flex flex-col gap-1">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  student.verification_status === 'verified'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                }`}>
                                  {student.verification_status === 'verified' ? 'Verified' : 'Pending'}
                                </span>
                                {student.eligibility !== null && (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    student.eligibility === true
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}>
                                    {student.eligibility === true ? 'Eligible' : 'Not Eligible'}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="space-y-2 text-sm mb-4">
                              {student.university_email && (
                                <div>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span>
                                  <p className="text-gray-600 dark:text-gray-400 break-all">{student.university_email}</p>
                                </div>
                              )}
                              {student.last_application_formatted && (
                                <div>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Last Application:</span>
                                  <p className="text-gray-600 dark:text-gray-400">
                                    {student.last_application_formatted}
                                    {student.days_since_application !== null && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                        ({student.days_since_application} days ago)
                                      </span>
                                    )}
                                  </p>
                                </div>
                              )}
                              {student.verified_at && (
                                <div>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Date Verified:</span>
                                  <p className="text-gray-600 dark:text-gray-400">
                                    {new Date(student.verified_at).toLocaleDateString()}
                                  </p>
                                </div>
                              )}
                              {student.preferred_companies && (
                                <div>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Preferred Companies:</span>
                                  <p className="text-gray-600 dark:text-gray-400">{student.preferred_companies}</p>
                                </div>
                              )}
                              {student.hired_by_companies && (
                                <div>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Hired By:</span>
                                  <p className="text-gray-600 dark:text-gray-400 font-medium text-green-600 dark:text-green-400">
                                    {student.hired_by_companies}
                                  </p>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex gap-2 flex-wrap">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                Offers: {student.total_offers || 0}
                              </span>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                Accepted: {student.offers_accepted || 0}
                              </span>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                Total Apps: {student.total_applications || 0}
                              </span>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                Cycle: {student.current_cycle_year}
                              </span>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                This Cycle: {student.applications_this_cycle || 0}
                              </span>
                            </div>
                          </div>
                        )}
                      >
                      <div className="max-h-[600px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Student ID
                            </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Student Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Last Application
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Date Verified
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Eligibility
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Preferred Companies
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Offers
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Offers Accepted
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Hired By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Total Applications
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Current Cycle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        This Cycle Apps
                      </th>
                    </tr>
                  </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {filteredStudents.map((student) => (
                            <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {student.applicant_id || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {student.student_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            student.verification_status === 'verified'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          }`}>
                            {student.verification_status === 'verified' ? 'Verified' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {student.last_application_formatted && student.last_application_formatted !== 'No applications' ? (
                            <div>
                              <div>{student.last_application_formatted}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {student.days_since_application !== null ?
                                  `${student.days_since_application} days ago` :
                                  'Recent'
                                }
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 italic">No applications</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {student.verified_at ? (
                            <div>
                              <div>{new Date(student.verified_at).toLocaleDateString()}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(student.verified_at).toLocaleTimeString()}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {student.eligibility !== null ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              student.eligibility === true
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {student.eligibility === true ? 'Eligible' : 'Not Eligible'}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {student.university_email}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {student.preferred_companies ? (
                            <div className="max-w-xs">
                              <span className="block truncate" title={student.preferred_companies}>
                                {student.preferred_companies}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                            {student.total_offers || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            {student.offers_accepted || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {student.hired_by_companies ? (
                            <div className="max-w-xs">
                              <span className="block truncate" title={student.hired_by_companies}>
                                {student.hired_by_companies}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {student.total_applications || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            {student.current_cycle_year}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            {student.applications_this_cycle || 0}
                          </span>
                        </td>
                      </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                      </ResponsiveTable>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-gray-400 dark:text-gray-500 mb-2">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300">
                        No students found matching "{studentSearchQuery}"
                      </p>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 dark:text-gray-500 mb-2">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-600 dark:text-gray-300">
                  No student application data available.
                </p>
              </div>
            )}
              </>
            )}

          </div>
        )}

        {/* Students - University Status Missing Card */}
        {activeTab === 'student-activity' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mt-6" data-card="university-missing">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setStudentsUniversityMissingCollapsed(!studentsUniversityMissingCollapsed)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  >
                    <svg
                      className={`w-5 h-5 text-gray-600 dark:text-gray-400 transform transition-transform ${
                        studentsUniversityMissingCollapsed ? '' : 'rotate-90'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Students - University Status Missing
                    </h2>
                    {studentsUniversityMissingCollapsed && studentsUniversityMissingData && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {studentsUniversityMissingData.length} students with missing university status
                      </p>
                    )}
                  </div>
                </div>
                {!studentsUniversityMissingCollapsed && (
                  <p className="text-gray-600 dark:text-gray-300 mt-2 ml-8">
                    Students with current_university_verified set to '0' indicating missing verified university information.
                  </p>
                )}
              </div>
              {!studentsUniversityMissingCollapsed && (
                <button
                  onClick={exportStudentsUniversityMissingToCSV}
                  disabled={!studentsUniversityMissingData || studentsUniversityMissingData.length === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    studentsUniversityMissingData && studentsUniversityMissingData.length > 0
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </button>
              )}
            </div>

            {!studentsUniversityMissingCollapsed && (
              <>
                {/* Search Bar */}
                <div className="mb-6">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={universityMissingSearchQuery}
                      onChange={(e) => setUniversityMissingSearchQuery(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Search by student name or ID..."
                    />
                  </div>
                </div>

                {studentsUniversityMissingLoading ? (
                  <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                    <div className="space-y-4">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      ))}
                    </div>
                  </div>
                ) : studentsUniversityMissingData && studentsUniversityMissingData.length > 0 ? (
                  <div className="overflow-x-auto">
                    {(() => {
                      // Filter students based on search query
                      const filteredStudents = studentsUniversityMissingData.filter(student => {
                        if (!universityMissingSearchQuery) return true;
                        const searchLower = universityMissingSearchQuery.toLowerCase();
                        return (
                          student.student_name.toLowerCase().includes(searchLower) ||
                          (student.applicant_id && student.applicant_id.toString().includes(searchLower))
                        );
                      });

                      return filteredStudents.length > 0 ? (
                        <ResponsiveTable
                          data={filteredStudents}
                          keyPrefix="university-missing"
                          renderMobileCard={(student) => (
                            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex-1">
                                  <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                                    {student.student_name}
                                  </h3>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    ID: {student.applicant_id}
                                  </p>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    student.verification_status === 'verified'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                  }`}>
                                    {student.verification_status}
                                  </span>
                                  {student.eligibility !== null && (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      student.eligibility === true
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    }`}>
                                      {student.eligibility === true ? 'Eligible' : 'Not Eligible'}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              <div className="space-y-2 text-sm mb-4">
                                {student.university_email && (
                                  <div>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span>
                                    <p className="text-gray-600 dark:text-gray-400 break-all">{student.university_email}</p>
                                  </div>
                                )}
                                {student.scholarship_verified && (
                                  <div>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Scholarship:</span>
                                    <p className="text-gray-600 dark:text-gray-400">{student.scholarship_verified}</p>
                                  </div>
                                )}
                                {student.last_application_formatted && (
                                  <div>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Last Application:</span>
                                    <p className="text-gray-600 dark:text-gray-400">{student.last_application_formatted}</p>
                                  </div>
                                )}
                                {student.verified_at_formatted && (
                                  <div>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Date Verified:</span>
                                    <p className="text-gray-600 dark:text-gray-400">
                                      {student.verified_at_formatted}
                                      {student.days_since_verification !== undefined && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                          ({student.days_since_verification} days ago)
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                )}
                                {student.preferred_companies && (
                                  <div>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Preferred Companies:</span>
                                    <p className="text-gray-600 dark:text-gray-400">{student.preferred_companies}</p>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex gap-2 flex-wrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  Total Apps: {student.total_applications || 0}
                                </span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  Cycle: {student.current_cycle_year}
                                </span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                  This Cycle: {student.applications_this_cycle || 0}
                                </span>
                              </div>
                            </div>
                          )}
                        >
                          <div className="max-h-[600px] overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Student ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Student Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Scholarship
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Last Application
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Date Verified
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Eligibility
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Preferred Companies
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Total Applications
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Current Cycle
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  This Cycle Apps
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {filteredStudents.map((student) => (
                                <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                    {student.applicant_id}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                    {student.student_name}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    {student.scholarship_verified || '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      student.verification_status === 'verified'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    }`}>
                                      {student.verification_status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    {student.last_application_formatted}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    {student.verified_at_formatted ? (
                                      <div>
                                        <div>{student.verified_at_formatted}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                          {student.days_since_verification !== undefined &&
                                            `${student.days_since_verification} days ago`}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {student.eligibility !== null ? (
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        student.eligibility === true
                                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                      }`}>
                                        {student.eligibility === true ? 'Eligible' : 'Not Eligible'}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    {student.university_email}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                    {student.preferred_companies ? (
                                      <div className="max-w-xs">
                                        <span className="block truncate" title={student.preferred_companies}>
                                          {student.preferred_companies}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                      {student.total_applications || 0}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                      {student.current_cycle_year}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                      {student.applications_this_cycle || 0}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </ResponsiveTable>
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-400 dark:text-gray-500 mb-2">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          <p className="text-gray-600 dark:text-gray-300">
                            No students found matching "{universityMissingSearchQuery}"
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 dark:text-gray-500 mb-2">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      No students with missing university status found.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Unassigned Students Card */}
        {activeTab === 'student-activity' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mt-6" data-card="unassigned-students">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setUnassignedStudentsCollapsed(!unassignedStudentsCollapsed)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  >
                    <svg
                      className={`w-5 h-5 text-gray-600 dark:text-gray-400 transform transition-transform ${
                        unassignedStudentsCollapsed ? '' : 'rotate-90'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Unassigned Students
                    </h2>
                    {unassignedStudentsCollapsed && unassignedStudentsData && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {unassignedStudentsData.length} unassigned students
                      </p>
                    )}
                  </div>
                </div>
                {!unassignedStudentsCollapsed && (
                  <div className="mt-2 ml-8 flex items-center gap-2">
                    <p className="text-gray-600 dark:text-gray-300 md:inline hidden">
                      Students with no block phase verification (block_phase_verified = null). Green flag = new students added by university partner(s) and approved by admin.
                    </p>
                    <div className="md:hidden flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <span className="text-sm">Students without block phase verification</span>
                      <div className="relative group">
                        <button 
                          type="button"
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 cursor-help"
                          title="Table explanation"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 w-64 shadow-lg">
                          <div className="space-y-1">
                            <p><strong>What this shows:</strong> Students with no block phase verification (block_phase_verified = null)</p>
                            <p><strong className="text-green-400 dark:text-green-600">🟢 Green flag:</strong> New students added by university partners and approved by admin</p>
                          </div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {!unassignedStudentsCollapsed && (
                <button
                  onClick={exportUnassignedStudentsToCSV}
                  disabled={!unassignedStudentsData || unassignedStudentsData.length === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    unassignedStudentsData && unassignedStudentsData.length > 0
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </button>
              )}
            </div>

            {!unassignedStudentsCollapsed && (
              <>
                {/* Search Bar */}
                <div className="mb-6">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={unassignedSearchQuery}
                      onChange={(e) => setUnassignedSearchQuery(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Search by student name or ID..."
                    />
                  </div>
                </div>

                {unassignedStudentsLoading ? (
                  <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                    <div className="space-y-4">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      ))}
                    </div>
                  </div>
                ) : unassignedStudentsData && unassignedStudentsData.length > 0 ? (
                  <div className="overflow-x-auto">
                    {(() => {
                      // Filter students based on search query
                      const filteredStudents = unassignedStudentsData.filter(student => {
                        if (!unassignedSearchQuery) return true;
                        const searchLower = unassignedSearchQuery.toLowerCase();
                        return (
                          student.student_name.toLowerCase().includes(searchLower) ||
                          (student.applicant_id && student.applicant_id.toString().includes(searchLower))
                        );
                      });

                      return filteredStudents.length > 0 ? (
                        <ResponsiveTable
                          data={filteredStudents}
                          keyPrefix="unassigned"
                          renderMobileCard={(student) => (
                            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                                      {student.student_name}
                                    </h3>
                                    {/* Flag indicators */}
                                    {student.pending_addition_status === 'pending' && (
                                      <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20" title="Pending approval">
                                        <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                    {student.pending_addition_status === 'approved' && (
                                      <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20" title="Approved">
                                        <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    ID: {student.applicant_id}
                                  </p>
                                  {student.pending_addition_status && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      {student.pending_addition_status === 'pending' 
                                        ? '⚠️ Pending approval in student additions'
                                        : '✅ Approved in student additions'}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    student.verification_status === 'verified'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                  }`}>
                                    {student.verification_status}
                                  </span>
                                  {student.eligibility !== null && (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      student.eligibility === true
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    }`}>
                                      {student.eligibility === true ? 'Eligible' : 'Not Eligible'}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              <div className="space-y-2 text-sm mb-4">
                                {student.university_email && (
                                  <div>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span>
                                    <p className="text-gray-600 dark:text-gray-400 break-all">{student.university_email}</p>
                                  </div>
                                )}
                                {student.last_application_formatted && (
                                  <div>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Last Application:</span>
                                    <p className="text-gray-600 dark:text-gray-400">{student.last_application_formatted}</p>
                                  </div>
                                )}
                                {student.verified_at_formatted && (
                                  <div>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Date Verified:</span>
                                    <p className="text-gray-600 dark:text-gray-400">
                                      {student.verified_at_formatted}
                                      {student.days_since_verification !== undefined && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                          ({student.days_since_verification} days ago)
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                )}
                                {student.preferred_companies && (
                                  <div>
                                    <span className="font-medium text-gray-700 dark:text-gray-300">Preferred Companies:</span>
                                    <p className="text-gray-600 dark:text-gray-400">{student.preferred_companies}</p>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex gap-2 flex-wrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  Total Apps: {student.total_applications || 0}
                                </span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  Cycle: {student.current_cycle_year}
                                </span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                  This Cycle: {student.applications_this_cycle || 0}
                                </span>
                              </div>
                            </div>
                          )}
                        >
                          <div className="max-h-[600px] overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Student ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Student Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Last Application
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Date Verified
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Eligibility
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Preferred Companies
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Total Applications
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Current Cycle
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  This Cycle Apps
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {filteredStudents.map((student) => (
                                <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                    {student.applicant_id}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                    <div className="flex items-center gap-2">
                                      {student.student_name}
                                      {/* Flag indicator based on pending_student_additions status */}
                                      {student.pending_addition_status === 'pending' && (
                                        <div className="relative group">
                                          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                          </svg>
                                          <div className="absolute hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded whitespace-nowrap z-10">
                                            Pending approval in pending_student_additions
                                          </div>
                                        </div>
                                      )}
                                      {student.pending_addition_status === 'approved' && (
                                        <div className="relative group">
                                          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                                          </svg>
                                          <div className="absolute hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded whitespace-nowrap z-10">
                                            Approved in pending_student_additions
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      student.verification_status === 'verified'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    }`}>
                                      {student.verification_status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    {student.last_application_formatted}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    {student.verified_at_formatted ? (
                                      <div>
                                        <div>{student.verified_at_formatted}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                          {student.days_since_verification !== undefined &&
                                            `${student.days_since_verification} days ago`}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {student.eligibility !== null ? (
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        student.eligibility === true
                                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                      }`}>
                                        {student.eligibility === true ? 'Eligible' : 'Not Eligible'}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    {student.university_email}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                    {student.preferred_companies ? (
                                      <div className="max-w-xs">
                                        <span className="block truncate" title={student.preferred_companies}>
                                          {student.preferred_companies}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                      {student.total_applications || 0}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                      {student.current_cycle_year}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                      {student.applications_this_cycle || 0}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </ResponsiveTable>
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-400 dark:text-gray-500 mb-2">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          <p className="text-gray-600 dark:text-gray-300">
                            No students found matching "{unassignedSearchQuery}"
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 dark:text-gray-500 mb-2">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      No unassigned students found.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Student Login Activity Card - Separate from Student Activity Tab */}
        {activeTab === 'student-activity' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mt-6" data-card="login-activity">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setStudentLoginActivityCollapsed(!studentLoginActivityCollapsed)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  >
                    <svg
                      className={`w-5 h-5 text-gray-400 transform transition-transform ${
                        studentLoginActivityCollapsed ? '-rotate-90' : 'rotate-0'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Student Login Activity
                  </h3>
                  {studentLoginActivityData && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {studentLoginActivityData.length} students with logins
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Track student login activity and engagement
                </p>
              </div>
            </div>

            {!studentLoginActivityCollapsed && (
              <>
                {studentLoginActivityLoading ? (
                  <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                    <div className="space-y-4">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      ))}
                    </div>
                  </div>
                ) : studentLoginActivityData && studentLoginActivityData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Student ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Last Login
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Total Logins
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {studentLoginActivityData.map((student) => (
                          <tr key={student.student_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {student.student_id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {student.student_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                              {student.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                              <div>
                                <div>{new Date(student.last_login).toLocaleDateString()}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(student.last_login).toLocaleTimeString()}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                {student.total_logins || 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 dark:text-gray-500 mb-2">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      No students have logged in yet.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Internship Completion Tracking Card */}
        {activeTab === 'student-activity' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mt-6" data-card="internship-completion">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setInternshipCompletionCollapsed(!internshipCompletionCollapsed)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  >
                    <svg
                      className={`w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform ${
                        internshipCompletionCollapsed ? '' : 'transform rotate-90'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      CSIP Completion Tracking
                    </h2>
                    {internshipCompletionCollapsed && internshipCompletionData && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {internshipCompletionData.length} internship(s) being tracked
                      </p>
                    )}
                  </div>
                </div>
                {!internshipCompletionCollapsed && (
                  <p className="text-gray-600 dark:text-gray-300 mt-2 ml-8">
                    Track completion status for students who have accepted internship offers.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={exportInternshipCompletionsToCSV}
                  disabled={!internshipCompletionData || internshipCompletionData.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </button>
              </div>
            </div>

            {!internshipCompletionCollapsed && (
              <>
                {/* Search Bar */}
                <div className="mb-6">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search by student name, applicant ID, or company..."
                      value={internshipSearchQuery}
                      onChange={(e) => setInternshipSearchQuery(e.target.value)}
                      className="block w-full pl-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {internshipCompletionLoading ? (
                  <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                    <div className="space-y-4">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      ))}
                    </div>
                  </div>
                ) : internshipCompletionData && internshipCompletionData.length > 0 ? (
                  <div className="overflow-x-auto">
                    {(() => {
                      // Filter completions based on search query
                      const filteredCompletions = internshipCompletionData.filter(completion => {
                        if (!internshipSearchQuery) return true;
                        const searchLower = internshipSearchQuery.toLowerCase();
                        return (
                          completion.student_name.toLowerCase().includes(searchLower) ||
                          completion.company.toLowerCase().includes(searchLower) ||
                          (completion.applicant_id && completion.applicant_id.toString().includes(searchLower))
                        );
                      });

                      return filteredCompletions.length > 0 ? (
                        <>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Showing {filteredCompletions.length} of {internshipCompletionData.length} internships
                          </div>
                          <ResponsiveTable
                            data={filteredCompletions}
                            getItemKey={(completion) => `internship-${completion.applicant_id}-${completion.interaction_id}`}
                            renderMobileCard={(completion) => {
                              const updateKey = `${completion.applicant_id}-${completion.interaction_id}`;
                              const isUpdating = updatingCompletion.has(updateKey);
                              
                              return (
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
                                  <div className="flex justify-between items-start mb-3">
                                    <div className="flex-1">
                                      <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                                        {completion.student_name}
                                      </h3>
                                      <p className="text-sm text-gray-600 dark:text-gray-400">
                                        ID: {completion.applicant_id || '-'}
                                      </p>
                                      <p className="text-sm font-medium text-blue-600 dark:text-blue-400 mt-1">
                                        {completion.company}
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        completion.verification_status === 'verified'
                                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                      }`}>
                                        {completion.verification_status === 'verified' ? 'Verified' : 'Pending'}
                                      </span>
                                      {completion.eligibility !== null && (
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                          completion.eligibility === true
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                        }`}>
                                          {completion.eligibility === true ? 'Eligible' : 'Not Eligible'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* CSIP Completion Status Dropdown */}
                                  <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      CSIP Completion Status:
                                    </label>
                                    <select
                                      value={completion.completion_status}
                                      onChange={(e) => {
                                        const newStatus = e.target.value;
                                        if (newStatus === 'completed') {
                                          const completionDate = prompt('Enter completion date (YYYY-MM-DD):');
                                          if (completionDate) {
                                            updateCompletionStatus(
                                              completion.applicant_id,
                                              completion.interaction_id,
                                              newStatus,
                                              completionDate,
                                              completion.notes
                                            );
                                          }
                                        } else {
                                          updateCompletionStatus(
                                            completion.applicant_id,
                                            completion.interaction_id,
                                            newStatus,
                                            null,
                                            completion.notes
                                          );
                                        }
                                      }}
                                      disabled={isUpdating}
                                      className={`w-full text-sm px-3 py-2 rounded-lg border-0 focus:ring-2 focus:ring-blue-500 ${
                                        completion.completion_status === 'completed'
                                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                          : completion.completion_status === 'in_progress'
                                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                          : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300'
                                      }`}
                                    >
                                      <option value="not_started">Not Started</option>
                                      <option value="in_progress">In Progress</option>
                                      <option value="completed">Completed</option>
                                    </select>
                                    {isUpdating && (
                                      <div className="mt-2 flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                        <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">Updating...</span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="space-y-2 text-sm mb-4">
                                    {completion.completion_date_formatted && (
                                      <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Completion Date:</span>
                                        <p className="text-gray-600 dark:text-gray-400">{completion.completion_date_formatted}</p>
                                      </div>
                                    )}
                                    {completion.university_email && (
                                      <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span>
                                        <p className="text-gray-600 dark:text-gray-400 break-all">{completion.university_email}</p>
                                      </div>
                                    )}
                                    {completion.last_application_formatted && (
                                      <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Last Application:</span>
                                        <p className="text-gray-600 dark:text-gray-400">
                                          {completion.last_application_formatted}
                                          {completion.days_since_application !== null && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                              ({completion.days_since_application} days ago)
                                            </span>
                                          )}
                                        </p>
                                      </div>
                                    )}
                                    {completion.verified_at && (
                                      <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Date Verified:</span>
                                        <p className="text-gray-600 dark:text-gray-400">
                                          {new Date(completion.verified_at).toLocaleDateString()}
                                        </p>
                                      </div>
                                    )}
                                    {completion.notes && (
                                      <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">Notes:</span>
                                        <p className="text-gray-600 dark:text-gray-400 italic">{completion.notes}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }}
                          >
                          <div className="max-h-[600px] overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Student ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Student Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Company
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  CSIP Completion Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Completion Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Last Application
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Date Verified
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Eligibility
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Notes
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {filteredCompletions.map((completion) => {
                                const updateKey = `${completion.applicant_id}-${completion.interaction_id}`;
                                const isUpdating = updatingCompletion.has(updateKey);
                                
                                return (
                                  <tr key={`${completion.applicant_id}-${completion.interaction_id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                      {completion.applicant_id || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                      {completion.student_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                      {completion.company}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <select
                                        value={completion.completion_status}
                                        onChange={(e) => {
                                          const newStatus = e.target.value;
                                          if (newStatus === 'completed') {
                                            const completionDate = prompt('Enter completion date (YYYY-MM-DD):');
                                            if (completionDate) {
                                              updateCompletionStatus(
                                                completion.applicant_id,
                                                completion.interaction_id,
                                                newStatus,
                                                completionDate,
                                                completion.notes
                                              );
                                            }
                                          } else {
                                            updateCompletionStatus(
                                              completion.applicant_id,
                                              completion.interaction_id,
                                              newStatus,
                                              null,
                                              completion.notes
                                            );
                                          }
                                        }}
                                        disabled={isUpdating}
                                        className={`text-xs px-3 py-1 rounded-full border-0 focus:ring-2 focus:ring-blue-500 ${
                                          completion.completion_status === 'completed'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : completion.completion_status === 'in_progress'
                                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                        }`}
                                      >
                                        <option value="not_started">Not Started</option>
                                        <option value="in_progress">In Progress</option>
                                        <option value="completed">Completed</option>
                                      </select>
                                      {isUpdating && (
                                        <div className="mt-1">
                                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                      {completion.completion_date_formatted || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        completion.verification_status === 'verified'
                                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                      }`}>
                                        {completion.verification_status === 'verified' ? 'Verified' : 'Pending'}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                      {completion.last_application_formatted && completion.last_application_formatted !== 'Unknown' ? (
                                        <div>
                                          <div>{completion.last_application_formatted}</div>
                                          <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {completion.days_since_application !== null ?
                                              `${completion.days_since_application} days ago` :
                                              'Recent'
                                            }
                                          </div>
                                        </div>
                                      ) : (
                                        <span className="text-gray-400 dark:text-gray-500 italic">No applications</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                      {completion.verified_at ? (
                                        <div>
                                          <div>{new Date(completion.verified_at).toLocaleDateString()}</div>
                                          <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {new Date(completion.verified_at).toLocaleTimeString()}
                                          </div>
                                        </div>
                                      ) : (
                                        <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {completion.eligibility !== null ? (
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                          completion.eligibility === true
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                        }`}>
                                          {completion.eligibility === true ? 'Eligible' : 'Not Eligible'}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                      {completion.university_email}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-xs">
                                      {completion.notes ? (
                                        <div className="truncate" title={completion.notes}>
                                          {completion.notes}
                                        </div>
                                      ) : (
                                        <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          </div>
                          </ResponsiveTable>
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-400 dark:text-gray-500 mb-2">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          <p className="text-gray-600 dark:text-gray-300">
                            No internships found matching "{internshipSearchQuery}"
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 dark:text-gray-500 mb-2">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      No internship completion data available.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Student Geocoding Card */}
        {activeTab === 'student-activity' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mt-6" data-card="geocoding">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setGeocodingCollapsed(!geocodingCollapsed)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  >
                    <svg
                      className={`w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform ${
                        geocodingCollapsed ? '' : 'transform rotate-90'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Student Address Geocoding
                    </h2>
                    {geocodingCollapsed && geocodingStatus && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {geocodingStatus.geocoded_count} geocoded, {geocodingStatus.pending_with_valid_address} pending
                      </p>
                    )}
                  </div>
                </div>
                {!geocodingCollapsed && (
                  <p className="text-gray-600 dark:text-gray-300 mt-2 ml-8">
                    Geocode student addresses to enable map visualization on the Recruiter Dashboard.
                  </p>
                )}
              </div>
            </div>

            {!geocodingCollapsed && (
              <div className="space-y-6">
                {/* Status Overview */}
                {geocodingStatus && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {geocodingStatus.geocoded_count || 0}
                      </div>
                      <div className="text-sm text-green-700 dark:text-green-300">Geocoded</div>
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                        {geocodingStatus.pending_with_valid_address || 0}
                      </div>
                      <div className="text-sm text-yellow-700 dark:text-yellow-300">Pending (Valid Address)</div>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                        {geocodingStatus.status_counts?.failed || 0}
                      </div>
                      <div className="text-sm text-red-700 dark:text-red-300">Failed</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                        {geocodingStatus.bad_address_count || 0}
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">Bad/Missing Address</div>
                    </div>
                  </div>
                )}

                {/* Progress Bar (shown during geocoding) */}
                {geocodingInProgress && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                        Geocoding in progress...
                      </span>
                      <span className="text-sm text-blue-600 dark:text-blue-400">
                        {geocodingProgress.processed} / {geocodingProgress.total}
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-3">
                      <div
                        className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                        style={{
                          width: `${geocodingProgress.total > 0 ? (geocodingProgress.processed / geocodingProgress.total) * 100 : 0}%`
                        }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-blue-600 dark:text-blue-400 mt-1">
                      <span>✓ {geocodingProgress.successful} successful</span>
                      <span>✗ {geocodingProgress.failed} failed</span>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={runGeocodingBatch}
                    disabled={geocodingInProgress || (geocodingStatus?.pending_with_valid_address === 0)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      geocodingInProgress || (geocodingStatus?.pending_with_valid_address === 0)
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {geocodingInProgress ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Geocode All Pending
                      </>
                    )}
                  </button>
                  <button
                    onClick={fetchGeocodingStatus}
                    disabled={geocodingInProgress}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh Status
                  </button>
                  {/* Export Failed Addresses Button - only show if there are failed geocoding attempts */}
                  {geocodingStatus && geocodingStatus.status_counts?.failed > 0 && (
                    <button
                      onClick={exportFailedGeocodingToCSV}
                      disabled={geocodingExportLoading || geocodingInProgress}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        geocodingExportLoading
                          ? 'bg-orange-400 cursor-not-allowed text-white'
                          : 'bg-orange-500 hover:bg-orange-600 text-white'
                      }`}
                    >
                      {geocodingExportLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Exporting...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Export Failed Addresses
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Help Text */}
                <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <p className="font-medium mb-2">About Geocoding:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Geocoding converts student addresses to map coordinates using the US Census API</li>
                    <li>Students with "Bad/Missing Address" need to update their profile with a valid address</li>
                    <li>"Failed" addresses may be new streets not yet in Census data or invalid addresses</li>
                    <li>This process runs in batches to avoid overwhelming the API</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recruiter Activity Tab */}
        {activeTab === 'recruiter-activity' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
            {/* Print-specific styles for recruiter activity */}
            <style>{`
              @media print {
                /* Hide non-printable elements */
                .print-hide {
                  display: none !important;
                }

                /* Show only selected recruiter or all if none selected */
                .recruiter-card {
                  display: none !important;
                }
                .recruiter-card.print-visible {
                  display: block !important;
                }

                /* Page setup */
                @page {
                  size: portrait;
                  margin: 0.5in;
                }

                /* Reset backgrounds */
                body, .bg-white, .bg-gray-50, .dark\\:bg-gray-800, .dark\\:bg-gray-700 {
                  background: white !important;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }

                /* Ensure colored backgrounds print */
                .bg-gray-50, .bg-blue-50 {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }

                /* Make text dark for printing */
                .text-gray-900, .dark\\:text-white {
                  color: #111827 !important;
                }

                /* Remove shadows */
                .shadow, .shadow-lg {
                  box-shadow: none !important;
                }
              }
            `}</style>

            <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  Recruiter Activity Dashboard
                </h2>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                  Track interaction history and engagement metrics for all recruiters
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 print-hide">
                <button
                  onClick={handleExportRecruiterActivityCSV}
                  disabled={recruiterActivityExporting || recruiterActivityLoading}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {recruiterActivityExporting ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export CSV
                    </>
                  )}
                </button>
                <button
                  onClick={handlePrintRecruiterActivity}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print / PDF
                </button>
                <button
                  onClick={() => switchToTab('recruiters')}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Back to Main Dashboard
                </button>
              </div>
            </div>

            {/* Print Selection Controls - Collapsible */}
            {recruiterActivityData && recruiterActivityData.length > 0 && (
              <div className="mb-4 bg-gray-50 dark:bg-gray-700 rounded-lg print-hide">
                {/* Toggle Header */}
                <button
                  onClick={() => setShowPrintOptions(!showPrintOptions)}
                  className="w-full p-3 flex items-center justify-between text-left hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <svg
                      className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${showPrintOptions ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Print Options
                    </span>
                    {selectedRecruitersForPrint.size > 0 ? (
                      <span className="text-xs text-blue-600 dark:text-blue-400">
                        ({selectedRecruitersForPrint.size} of {recruiterActivityData.length} selected)
                      </span>
                    ) : (
                      <span className="text-xs text-green-600 dark:text-green-400">
                        (All {recruiterActivityData.length} will be printed)
                      </span>
                    )}
                  </div>
                </button>

                {/* Collapsible Content */}
                {showPrintOptions && (
                  <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-600">
                    <div className="pt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Select recruiters to include in print:
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const allIds = new Set(recruiterActivityData.map(r => r.recruiter_id));
                            setSelectedRecruitersForPrint(allIds);
                          }}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                        >
                          Check All
                        </button>
                        <button
                          onClick={() => setSelectedRecruitersForPrint(new Set())}
                          className="text-xs px-2 py-1 bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                        >
                          Uncheck All
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {recruiterActivityData.map((recruiter) => (
                        <label key={recruiter.recruiter_id} className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedRecruitersForPrint.has(recruiter.recruiter_id)}
                            onChange={() => {
                              const newSelected = new Set(selectedRecruitersForPrint);
                              if (newSelected.has(recruiter.recruiter_id)) {
                                newSelected.delete(recruiter.recruiter_id);
                              } else {
                                newSelected.add(recruiter.recruiter_id);
                              }
                              setSelectedRecruitersForPrint(newSelected);
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{recruiter.recruiter_name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Leave all unchecked to print everyone, or check specific recruiters to print only those.
                    </p>
                  </div>
                )}
              </div>
            )}

            {recruiterActivityLoading ? (
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  ))}
                </div>
              </div>
            ) : recruiterActivityData && recruiterActivityData.length > 0 ? (
              <div className="space-y-4">
                {recruiterActivityData.map((recruiter) => (
                  <div
                    key={recruiter.recruiter_id}
                    className={`recruiter-card border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 ${
                      selectedRecruitersForPrint.size === 0 || selectedRecruitersForPrint.has(recruiter.recruiter_id)
                        ? 'print-visible'
                        : ''
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white break-words">
                          {recruiter.recruiter_name}
                        </h3>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <div className="block sm:hidden space-y-1">
                            <div className="break-words">{recruiter.organization}</div>
                            <div className="break-all">{recruiter.email}</div>
                          </div>
                          <div className="hidden sm:block break-words">
                            {recruiter.organization} • {recruiter.email}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/recruiter/interactions/${recruiter.recruiter_id}`)}
                        className="w-full sm:w-auto px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors whitespace-nowrap text-center print-hide"
                      >
                        <span className="hidden sm:inline">View Full History</span>
                        <span className="sm:hidden">View History</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 text-sm">
                      <div className="bg-gray-50 dark:bg-gray-700 p-2 sm:p-3 rounded">
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Shortlisted</p>
                        <p className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                          {recruiter.shortlisted_count}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-2 sm:p-3 rounded">
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Profile Requests</p>
                        <p className="text-lg sm:text-xl font-semibold text-blue-600 dark:text-blue-400">
                          {recruiter.apply_invites_sent}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-2 sm:p-3 rounded">
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">External Links</p>
                        <p className="text-lg sm:text-xl font-semibold text-green-600 dark:text-green-400">
                          {recruiter.external_links_sent}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-2 sm:p-3 rounded">
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Interview Queue</p>
                        <p className="text-lg sm:text-xl font-semibold text-purple-600 dark:text-purple-400">
                          {recruiter.interview_invites_sent}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-2 sm:p-3 rounded sm:col-span-3 lg:col-span-1">
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Last Activity</p>
                        <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                          {recruiter.last_activity ? new Date(recruiter.last_activity).toLocaleDateString() : 'No activity'}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        <div className="block sm:hidden space-y-1">
                          <div>Current Cycle: {recruiter.current_cycle_year}</div>
                          <div>Total Interactions: {recruiter.total_interactions}</div>
                        </div>
                        <div className="hidden sm:block">
                          Current Cycle: {recruiter.current_cycle_year} • Total Interactions: {recruiter.total_interactions}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const newExpanded = new Set(expandedRecruitersActivity);
                          if (newExpanded.has(recruiter.recruiter_id)) {
                            newExpanded.delete(recruiter.recruiter_id);
                          } else {
                            newExpanded.add(recruiter.recruiter_id);
                          }
                          setExpandedRecruitersActivity(newExpanded);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 self-start sm:self-auto print-hide"
                      >
                        {expandedRecruitersActivity.has(recruiter.recruiter_id) ? '▼ Hide Details' : '▶ Show Details'}
                      </button>
                    </div>

                    {/* Expanded details section */}
                    {expandedRecruitersActivity.has(recruiter.recruiter_id) && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 print-hide">
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
                          For detailed interaction history including student names, actions taken, and timeline, click "View Full History" above.
                        </p>
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
                          <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-200">
                            💡 Tip: The full history page shows all cycles, individual student interactions, notes, and export options.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 dark:text-gray-500 mb-2">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                  No recruiter activity data available.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Approve New Students Tab */}
        {activeTab === 'approve-students' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                Approve New Students
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Review and approve new students submitted by university partners.
              </p>
            </div>
            
            {/* Controls */}
            <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={fetchPendingStudents}
                  disabled={pendingStudentsLoading}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                    pendingStudentsLoading
                      ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {pendingStudentsLoading ? 'Loading...' : 'Refresh'}
                </button>
                
                {selectedPendingStudents.size > 0 && (
                  <>
                    <button
                      onClick={approveSelectedStudents}
                      disabled={processingStudents}
                      className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                        processingStudents
                          ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {processingStudents 
                        ? 'Processing...' 
                        : `Approve (${selectedPendingStudents.size})`
                      }
                    </button>
                    
                    <button
                      onClick={() => setShowRejectModal(true)}
                      disabled={processingStudents}
                      className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                        processingStudents
                          ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      {processingStudents 
                        ? 'Processing...' 
                        : `Reject (${selectedPendingStudents.size})`
                      }
                    </button>
                  </>
                )}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {pendingStudents.length} pending student{pendingStudents.length !== 1 ? 's' : ''} 
                {selectedPendingStudents.size > 0 && ` • ${selectedPendingStudents.size} selected`}
              </div>
            </div>

            {/* Loading State */}
            {pendingStudentsLoading && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">Loading pending students...</p>
              </div>
            )}

            {/* Mobile Select All - Only visible on mobile */}
            {!pendingStudentsLoading && pendingStudents.length > 0 && (
              <div className="md:hidden mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={pendingStudents.length > 0 && selectedPendingStudents.size === pendingStudents.length}
                    onChange={toggleSelectAllStudents}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  Select All Students
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedPendingStudents.size} of {pendingStudents.length} selected
                </span>
              </div>
            )}

            {/* Student Table */}
            {!pendingStudentsLoading && pendingStudents.length > 0 && (
              <ResponsiveTable 
                data={pendingStudents} 
                renderMobileCard={(student) => (
                  <MobilePendingStudentCard
                    student={student}
                    isSelected={selectedPendingStudents.has(student.id)}
                    onToggleSelect={() => toggleStudentSelection(student.id)}
                  />
                )}
              >
                <div className="max-h-[600px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={pendingStudents.length > 0 && selectedPendingStudents.size === pendingStudents.length}
                          onChange={toggleSelectAllStudents}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Student Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        University
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Academic Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Upload Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {pendingStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedPendingStudents.has(student.id)}
                            onChange={() => toggleStudentSelection(student.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {student.first_name} {student.last_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {student.university_email}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            ID: {student.id}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {student.university}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Batch: {student.batch_id}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {student.major || 'Not specified'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Class: {student.current_academic_class || 'Not specified'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            GPA: {student.gpa || 'Not specified'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {student.uploaded_by}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {student.uploaded_at ? new Date(student.uploaded_at).toLocaleDateString() : 'Unknown date'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            student.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                              : student.status === 'approved' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}>
                            {student.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </ResponsiveTable>
            )}

            {/* Empty State */}
            {!pendingStudentsLoading && pendingStudents.length === 0 && (
              <div className="text-center py-8">
                <div className="text-gray-400 dark:text-gray-500 mb-2">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-lg">
                  No pending students found
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  New students uploaded by university partners will appear here for review.
                </p>
              </div>
            )}

            {/* Debug Info */}
            <div className="mt-6 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Debug: {pendingStudents.length} students loaded, {selectedPendingStudents.size} selected
                {selectedPendingStudents.size > 0 && ` (IDs: ${Array.from(selectedPendingStudents).join(', ')})`}
              </p>
            </div>

            {/* Reject Students Modal */}
            {showRejectModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Reject Students
                  </h3>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    You are about to reject {selectedPendingStudents.size} student{selectedPendingStudents.size !== 1 ? 's' : ''}. 
                    Please provide a reason for rejection:
                  </p>
                  
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Enter rejection reason (required)..."
                    className="w-full h-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    disabled={processingStudents}
                  />
                  
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => {
                        setShowRejectModal(false);
                        setRejectReason('');
                      }}
                      disabled={processingStudents}
                      className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white 
                               border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700
                               disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Cancel
                    </button>
                    
                    <button
                      onClick={rejectSelectedStudents}
                      disabled={processingStudents || !rejectReason.trim()}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        processingStudents || !rejectReason.trim()
                          ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      {processingStudents 
                        ? 'Rejecting...' 
                        : `Reject ${selectedPendingStudents.size} Student${selectedPendingStudents.size !== 1 ? 's' : ''}`
                      }
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Student Approval/Rejection Response Modal */}
            {showStudentResponseModal && studentResponseData && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {studentResponseData.title}
                    </h3>
                    <button
                      onClick={() => {
                        setShowStudentResponseModal(false);
                        setStudentResponseData(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex items-start gap-3">
                      <svg 
                        className={`w-6 h-6 mt-0.5 ${
                          studentResponseData.type === 'success' ? 'text-green-500' : 'text-red-500'
                        }`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        {studentResponseData.type === 'success' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        )}
                      </svg>
                      <div className="flex-1">
                        <p className="text-gray-700 dark:text-gray-300">
                          {studentResponseData.message}
                        </p>
                        
                        {studentResponseData.details && studentResponseData.details.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                              Details:
                            </p>
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 max-h-32 overflow-y-auto">
                              {studentResponseData.details.map((detail, index) => (
                                <p key={index} className="text-xs text-gray-600 dark:text-gray-400 mb-1 last:mb-0">
                                  • {detail}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setShowStudentResponseModal(false);
                        setStudentResponseData(null);
                      }}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        studentResponseData.type === 'success'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Bulk Token Generation Confirmation Modal */}
            {showBulkTokenModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-lg w-full p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 p-2 rounded-lg">
                      <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        Confirm Bulk Token Generation
                      </h2>
                      <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                        <p>You are about to generate update tokens for:</p>
                        <ul className="list-disc list-inside space-y-1 bg-gradient-to-br from-purple-50 to-pink-50 dark:bg-gray-900 p-3 rounded">
                          <li><strong>{getTotalMatchingCount()}</strong> students</li>
                          <li>Student Group: <strong>{getGroupFilterLabel()}</strong></li>
                          <li>Application Status: <strong>{getAppStatusLabel()}</strong></li>
                          <li>Token Expiration: <strong>{bulkTokenExpiry} hours</strong></li>
                        </ul>
                        <p className="text-purple-700 dark:text-purple-300 font-medium mt-3">
                          ⚠️ This action will generate tokens for ALL matching students, even those not visible on this page.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Expiry Hours Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Token Expiration (hours)
                    </label>
                    <input
                      type="number"
                      value={bulkTokenExpiry}
                      onChange={(e) => setBulkTokenExpiry(parseInt(e.target.value) || 720)}
                      className="w-full px-3 py-2 border border-purple-300 dark:border-purple-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      min="1"
                      max="8760"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Default: 720 hours (30 days) • Max: 8760 hours (1 year)
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowBulkTokenModal(false)}
                      disabled={bulkTokenGenerating}
                      className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium disabled:opacity-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkTokenGeneration}
                      disabled={bulkTokenGenerating}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 via-purple-600 to-pink-600 hover:from-purple-600 hover:via-purple-700 hover:to-pink-700 disabled:opacity-50 text-white rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      {bulkTokenGenerating ? 'Generating...' : `Generate ${getTotalMatchingCount()} Tokens`}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Activation Token Modal */}
            {showActivationModal && activationTokenData && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Activation Token Generated Successfully
                    </h3>
                    <button
                      onClick={() => {
                        setShowActivationModal(false);
                        setActivationTokenData(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      Partner ID: <span className="font-medium">{activationTokenData.partnerId}</span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      Share this activation link with the university partner:
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between gap-4">
                      <code className="text-sm text-blue-600 dark:text-blue-400 break-all">
                        {activationTokenData.url}
                      </code>
                      <button
                        onClick={(e) => {
                          copyToClipboard(activationTokenData.url, false);
                          // Show temporary success message in the modal
                          const btn = e.currentTarget;
                          const originalText = btn.innerText;
                          btn.innerText = 'Copied!';
                          btn.classList.add('bg-green-600', 'text-white');
                          setTimeout(() => {
                            btn.innerText = originalText;
                            btn.classList.remove('bg-green-600', 'text-white');
                          }, 2000);
                        }}
                        className="flex-shrink-0 px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 
                                 text-blue-700 dark:text-blue-200 rounded transition-colors"
                      >
                        Copy Link
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>Important:</strong> This link will expire in 24 hours. The partner must use this link to activate their account.
                    </p>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setShowActivationModal(false);
                        setActivationTokenData(null);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Settings Toggle Modal (Verification, Race, Ethnicity) */}
            {showSettingsModal && settingsModalData && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {settingsModalData.success ? 'Setting Updated' : 'Update Failed'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowSettingsModal(false);
                        setSettingsModalData(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="mb-6">
                    {settingsModalData.success ? (
                      <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-gray-700 dark:text-gray-300">
                            {settingsModalData.message}
                          </p>
                          {settingsModalData.enabled !== undefined && (
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {settingsModalData.setting === 'verification' ? (
                                  settingsModalData.enabled ? (
                                    <>
                                      <strong>Public access enabled:</strong> Verification forms can now be accessed directly via their unique links without requiring partner dashboard login.
                                    </>
                                  ) : (
                                    <>
                                      <strong>Public access disabled:</strong> Verification forms are now only accessible through the partner dashboard after login.
                                    </>
                                  )
                                ) : settingsModalData.setting === 'race' ? (
                                  settingsModalData.enabled ? (
                                    <>
                                      <strong>Required field:</strong> Students must select their race when completing the application form. This helps with diversity tracking and reporting.
                                    </>
                                  ) : (
                                    <>
                                      <strong>Optional field:</strong> Students can choose whether to provide their race information. The field will still appear but won't be mandatory.
                                    </>
                                  )
                                ) : settingsModalData.setting === 'ethnicity' ? (
                                  settingsModalData.enabled ? (
                                    <>
                                      <strong>Required field:</strong> Students must select their ethnicity when completing the application form. This helps with diversity tracking and reporting.
                                    </>
                                  ) : (
                                    <>
                                      <strong>Optional field:</strong> Students can choose whether to provide their ethnicity information. The field will still appear but won't be mandatory.
                                    </>
                                  )
                                ) : null}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-gray-700 dark:text-gray-300">
                          {settingsModalData.message}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setShowSettingsModal(false);
                        setSettingsModalData(null);
                      }}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        settingsModalData.success
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Partner Activation Modal */}
            {showPartnerActivationModal && partnerActivationData && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {partnerActivationData.success ? 'Partner Status Updated' : 'Update Failed'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowPartnerActivationModal(false);
                        setPartnerActivationData(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="mb-6">
                    {partnerActivationData.success ? (
                      <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-gray-700 dark:text-gray-300">
                            {partnerActivationData.message}
                          </p>
                          {partnerActivationData.activated !== undefined && (
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {partnerActivationData.activated ? (
                                  <>
                                    <strong>Partner activated:</strong> This partner can now log in to their dashboard and access all features including student verification forms and CSV uploads.
                                  </>
                                ) : (
                                  <>
                                    <strong>Partner deactivated:</strong> This partner's access has been temporarily suspended. They will not be able to log in until reactivated.
                                  </>
                                )}
                              </p>
                            </div>
                          )}
                          {partnerActivationData.partnerId && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                              Partner ID: {partnerActivationData.partnerId}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-gray-700 dark:text-gray-300">
                          {partnerActivationData.message}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setShowPartnerActivationModal(false);
                        setPartnerActivationData(null);
                      }}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        partnerActivationData.success
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Partner Approval Modal */}
            {showPartnerApprovalModal && partnerApprovalData && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {partnerApprovalData.success ? 'Partner Approval Updated' : 'Update Failed'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowPartnerApprovalModal(false);
                        setPartnerApprovalData(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="mb-6">
                    {partnerApprovalData.success ? (
                      <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-gray-700 dark:text-gray-300">
                            {partnerApprovalData.message}
                          </p>
                          {partnerApprovalData.approved !== undefined && (
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {partnerApprovalData.approved ? (
                                  <>
                                    <strong>Partner approved:</strong> This partner has been granted full access to the platform. They can now:
                                    <ul className="list-disc list-inside mt-1 ml-2">
                                      <li>Access their dashboard</li>
                                      <li>Upload student data via CSV</li>
                                      <li>Manage verification forms</li>
                                      <li>View student analytics</li>
                                    </ul>
                                  </>
                                ) : (
                                  <>
                                    <strong>Partner rejected:</strong> This partner's application has been declined. They will:
                                    <ul className="list-disc list-inside mt-1 ml-2">
                                      <li>Remain in pending status</li>
                                      <li>Not have access to partner features</li>
                                      <li>Need manual review for future approval</li>
                                    </ul>
                                  </>
                                )}
                              </p>
                            </div>
                          )}
                          {partnerApprovalData.partnerId && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                              Partner ID: {partnerApprovalData.partnerId}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-gray-700 dark:text-gray-300">
                          {partnerApprovalData.message}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setShowPartnerApprovalModal(false);
                        setPartnerApprovalData(null);
                      }}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        partnerApprovalData.success
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Bulk Operation Modal */}
            {showBulkModal && bulkModalData && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {bulkModalData.title}
                    </h3>
                    <button
                      onClick={() => {
                        setShowBulkModal(false);
                        setBulkModalData(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex items-start gap-3">
                      <svg 
                        className={`w-6 h-6 mt-0.5 ${
                          bulkModalData.type === 'success' ? 'text-green-500' : 'text-red-500'
                        }`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        {bulkModalData.type === 'success' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        )}
                      </svg>
                      <div className="flex-1">
                        <p className="text-gray-700 dark:text-gray-300">
                          {bulkModalData.message}
                        </p>
                        
                        {bulkModalData.details && bulkModalData.details.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                              Details:
                            </p>
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 max-h-32 overflow-y-auto">
                              {bulkModalData.details.map((detail, index) => (
                                <p key={index} className="text-xs text-gray-600 dark:text-gray-400 mb-1 last:mb-0">
                                  • {detail}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setShowBulkModal(false);
                        setBulkModalData(null);
                      }}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        bulkModalData.type === 'success'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Board Liaisons Tab */}
        {activeTab === 'board-liaisons' && (
          <AdminBoardLiaisonManagement />
        )}

      </div>
      
      {/* Mobile Debugger - only shows in development */}
      <MobileDebugger />
      
      {/* Alumni Dash Admin Modal */}
      {showAlumniAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Alumni Dashboard Administration
              </h3>
              <button
                onClick={() => setShowAlumniAdminModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Apify Token Configuration */}
              <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                  LinkedIn Import Configuration
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Configure the Apify API token for LinkedIn profile imports. This token is required for alumni to import their LinkedIn profiles.
                </p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Apify API Token
                    </label>
                    <input
                      type="password"
                      value={apifyToken}
                      onChange={(e) => setApifyToken(e.target.value)}
                      placeholder="Enter your Apify API token"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                      disabled={isLoadingApifyToken}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Get your token from <a href="https://console.apify.com/settings/integrations" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600">Apify Console → Settings → Integrations</a>
                    </p>
                  </div>

                  {apifyTokenMessage && (
                    <div className={`text-sm p-2 rounded ${
                      apifyTokenMessage.includes('successfully') 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                        : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                    }`}>
                      {apifyTokenMessage}
                    </div>
                  )}

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowAlumniAdminModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveApifyToken}
                      disabled={isSavingApifyToken || isLoadingApifyToken}
                      className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                        isSavingApifyToken || isLoadingApifyToken
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {isSavingApifyToken ? 'Saving...' : 'Save Token'}
                    </button>
                  </div>
                </div>
              </div>

              {/* LinkedIn Import Status */}
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-2">
                  LinkedIn Import Status
                </h4>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`w-2 h-2 rounded-full ${isApifyTokenConfigured ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    <span className="text-gray-700 dark:text-gray-300">
                      LinkedIn Import: {isApifyTokenConfigured ? 'Configured' : 'Not Configured'}
                    </span>
                  </div>
                  {isApifyTokenConfigured && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Alumni can now import their LinkedIn profiles from their dashboard.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Tasks Modal (Research, Outreach, Brokerage) */}
      {showPendingTasksModal && pendingTasksSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-green-50 dark:bg-green-900/30 border-b border-green-200 dark:border-green-800 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-900 dark:text-green-200">
                    You Have Pending Tasks
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-400">
                    {[
                      pendingTasksSummary.research?.total > 0 && 'Research',
                      pendingTasksSummary.outreach?.total > 0 && 'Outreach',
                      pendingTasksSummary.brokerage?.total > 0 && 'Brokerage',
                      pendingTasksSummary.content_moderation?.total > 0 && 'Content Moderation',
                      pendingTasksSummary.leadership_review_queue?.total > 0 && 'Leadership Review',
                      pendingTasksSummary.grant_reviews?.total > 0 && 'Grant Reviews',
                    ].filter(Boolean).join(', ')} tasks need your attention
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Research section */}
              {pendingTasksSummary.research?.total > 0 && (
                <div className="p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-semibold text-teal-800 dark:text-teal-300">Research Tasks</h4>
                    <span className="text-lg font-bold text-teal-700 dark:text-teal-400">{pendingTasksSummary.research.total}</span>
                  </div>
                  <p className="text-xs text-teal-600 dark:text-teal-400">
                    {pendingTasksSummary.research.total} pending LinkedIn research task{pendingTasksSummary.research.total !== 1 ? 's' : ''}
                    {pendingTasksSummary.research.overdue > 0 && (
                      <span className="ml-1 text-red-600 dark:text-red-400 font-medium">({pendingTasksSummary.research.overdue} overdue)</span>
                    )}
                  </p>
                  <button
                    onClick={() => { setShowPendingTasksModal(false); navigate('/admin/alumni-research-assignments'); }}
                    className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"
                  >
                    Go to Research
                  </button>
                </div>
              )}

              {/* Outreach section */}
              {pendingTasksSummary.outreach?.total > 0 && (
                <div className="p-3 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-semibold text-violet-800 dark:text-violet-300">Outreach Tasks</h4>
                    <span className="text-lg font-bold text-violet-700 dark:text-violet-400">{pendingTasksSummary.outreach.total}</span>
                  </div>
                  <p className="text-xs text-violet-600 dark:text-violet-400">
                    {pendingTasksSummary.outreach.total} active outreach assignment{pendingTasksSummary.outreach.total !== 1 ? 's' : ''}
                    {pendingTasksSummary.outreach.overdue > 0 && (
                      <span className="ml-1 text-red-600 dark:text-red-400 font-medium">({pendingTasksSummary.outreach.overdue} overdue)</span>
                    )}
                  </p>
                  {pendingTasksSummary.outreach.stages && Object.keys(pendingTasksSummary.outreach.stages).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {Object.entries(pendingTasksSummary.outreach.stages).map(([stage, count]) => (
                        <span key={stage} className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300">
                          {stage} <span className="opacity-70">{count}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => { setShowPendingTasksModal(false); navigate('/admin/alumni-research-assignments?tab=outreach'); }}
                    className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
                  >
                    Go to Outreach
                  </button>
                </div>
              )}

              {/* Brokerage section */}
              {pendingTasksSummary.brokerage?.total > 0 && (
                <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-semibold text-indigo-800 dark:text-indigo-300">Mentorship &amp; Networking Requests</h4>
                    <span className="text-lg font-bold text-indigo-700 dark:text-indigo-400">{pendingTasksSummary.brokerage.total}</span>
                  </div>
                  <p className="text-xs text-indigo-600 dark:text-indigo-400">
                    {pendingTasksSummary.brokerage.total} active request{pendingTasksSummary.brokerage.total !== 1 ? 's' : ''} awaiting action
                  </p>
                  <button
                    onClick={() => { setShowPendingTasksModal(false); navigate('/admin/executive-intelligence?tab=brokerage'); }}
                    className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                  >
                    Go to Brokerage
                  </button>
                </div>
              )}

              {/* Content Moderation section */}
              {pendingTasksSummary.content_moderation?.total > 0 && (
                <div className="p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-semibold text-rose-800 dark:text-rose-300">Content Moderation</h4>
                    <span className="text-lg font-bold text-rose-700 dark:text-rose-400">{pendingTasksSummary.content_moderation.total}</span>
                  </div>
                  <p className="text-xs text-rose-600 dark:text-rose-400">
                    {pendingTasksSummary.content_moderation.total} unresolved report{pendingTasksSummary.content_moderation.total !== 1 ? 's' : ''} pending review
                  </p>
                  <button
                    onClick={() => { setShowPendingTasksModal(false); navigate('/admin/content-moderation'); }}
                    className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors"
                  >
                    Go to Content Moderation
                  </button>
                </div>
              )}

              {/* Leadership Review Queue section (superadmin only) */}
              {pendingTasksSummary.leadership_review_queue?.total > 0 && (
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-300">Leadership Review Queue</h4>
                    <span className="text-lg font-bold text-purple-700 dark:text-purple-400">{pendingTasksSummary.leadership_review_queue.total}</span>
                  </div>
                  <p className="text-xs text-purple-600 dark:text-purple-400">
                    {pendingTasksSummary.leadership_review_queue.total} high-scoring contact{pendingTasksSummary.leadership_review_queue.total !== 1 ? 's' : ''} pending review
                  </p>
                  <button
                    onClick={() => { setShowPendingTasksModal(false); navigate('/admin/alumni-research-assignments?tab=review-queue'); }}
                    className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                  >
                    Go to Review Queue
                  </button>
                </div>
              )}

              {/* Grant Reviews section (superadmin only) */}
              {pendingTasksSummary.grant_reviews?.total > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Grant Proposal Reviews</h4>
                    <span className="text-lg font-bold text-amber-700 dark:text-amber-400">{pendingTasksSummary.grant_reviews.total}</span>
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    {pendingTasksSummary.grant_reviews.total} proposal{pendingTasksSummary.grant_reviews.total !== 1 ? 's' : ''} submitted for review
                  </p>
                  <button
                    onClick={() => { setShowPendingTasksModal(false); navigate('/admin/grants'); }}
                    className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
                  >
                    Go to Grants
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex justify-end">
              <button
                onClick={() => setShowPendingTasksModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminApproveRecruiters;
